// SPDX-FileCopyrightText: 2026 mmayhew
// SPDX-License-Identifier: AGPL-3.0-only

// ── Shell chrome controller ──
// Owns the desktop rail (Recent, Workflows, nav) and the bottom HUD.
// Loaded after the shell core, the active-project HUD helpers, and controller.js.
// The heavier Projects workspace controllers are loaded on first workspace open.

import {
  openOptions as importedOpenOptions,
  openThemeSelector as importedOpenThemeSelector,
} from './app.js';
import { closeMajorOverlays as importedCloseMajorOverlays } from './ui/overlay_actions_bridge.js';
import { getAppConfig as importedGetAppConfig } from './core/config.js';
import { setControllerActionHandlers as importedSetControllerActionHandlers } from './controller_action_bridge.js';
import {
  openFaq as importedOpenFaq,
  openWorkflows as importedOpenWorkflows,
  toggleHistoryPanelSurface as importedToggleHistoryPanelSurface,
} from './controller.js';
import { openWorkspace as importedOpenWorkspace } from './workspace_bridge.js';
import {
  loadCommandRegistry as importedLoadCommandRegistry,
  loadFindingsBoard as importedLoadFindingsBoard,
  loadSchedulesModal as importedLoadSchedulesModal,
  loadWatchersModal as importedLoadWatchersModal,
} from './core/lazy_assets.js';
import {
  openAtlas as importedOpenAtlas,
} from './features/atlas/atlas_bridge.js';
import { openAtlasQuickLookupFromSurface as importedOpenAtlasQuickLookupFromSurface } from './features/atlas/atlas_quick_lookup_launch.js';
import { openCommandRegistry as importedOpenCommandRegistry } from './features/command-registry/command_registry_bridge.js';
import { resetCmdHistoryNav as importedResetCmdHistoryNav } from './features/history/history_recall.js';
import { openHistoryRunDetails as importedOpenHistoryRunDetails } from './features/history/history_run_modal_state_bridge.js';
import { restoreHistoryRunIntoTab as importedRestoreHistoryRunIntoTab } from './features/history/history_restore_bridge.js';
import { openHistoryCompareLauncher as importedOpenHistoryCompareLauncher } from './features/run-comparison/history_compare_bridge.js';
import { setProjectContextHandlers as importedSetProjectContextHandlers } from './features/projects/project_context_bridge.js';
import { setProjectHudHandlers as importedSetProjectHudHandlers } from './features/projects/project_hud_bridge.js';
import { DarklabProjectActiveContext as importedProjectActiveContext } from './features/projects/project_active_context.js';
import { DarklabProjectSharedUi as importedProjectSharedUi } from './features/projects/project_shared_ui.js';
import { DarklabProjectWorkspaceState as importedProjectWorkspaceState } from './features/projects/project_workspace_state.js';
import {
  copyTab as importedCopyTab,
  exportTabHtml as importedExportTabHtml,
  exportTabPdf as importedExportTabPdf,
  permalinkTab as importedPermalinkTab,
  saveTab as importedSaveTab,
} from './features/tabs/tab_exports.js';
import {
  activeTeamScopeCan as importedActiveTeamScopeCan,
  teamScopeDeniedMessage as importedTeamScopeDeniedMessage,
} from './features/team_scope.js';
import {
  ensureWorkflowCatalogLoaded as importedEnsureWorkflowCatalogLoaded,
  hasWorkflowHandler as importedHasWorkflowHandler,
  renderWorkflowItems as importedRenderWorkflowItems,
} from './features/workflows/workflows_bridge.js';
import {
  getActiveTabId as importedGetActiveTabId,
  getAppState as importedGetAppState,
  getTab as importedGetTab,
  getTabs as importedGetTabs,
  emitUiEvent as importedEmitUiEvent,
  onUiEvent as importedOnUiEvent,
} from './core/state.js';
import {
  downloadBlobAsAttachment as importedDownloadBlobAsAttachment,
  downloadUrlAsAttachment as importedDownloadUrlAsAttachment,
  showToast as importedShowToast,
} from './core/utils.js';
import {
  apiFetch as importedApiFetch,
  getBrowserIdentitySnapshot as importedGetBrowserIdentitySnapshot,
  logClientError as importedLogClientError,
} from './session.js';
import { openStatusMonitor as importedRuntimeOpenStatusMonitor } from './runtime_bridge.js';
import { confirmKill as importedConfirmKill } from './runner_bridge.js';
import { clearTab as importedClearTab } from './tabs_bridge.js';
import { cancelWelcome as importedCancelWelcome } from './welcome_bridge.js';
import { loadProjectAutocompleteTargets as importedLoadProjectAutocompleteTargets } from './features/autocomplete/suggestions.js';
import {
  activateOptionsTab as importedActivateOptionsTab,
  getHudClockPreference as importedGetHudClockPreference,
  getPreference as importedGetPreference,
  setPreferenceCookie as importedSetPreferenceCookie,
} from './features/preferences/preferences.js';
import { _getStarred as importedGetStarred } from './features/history/history_actions.js';
import { DarklabEntityMetadata as importedEntityMetadata } from './ui/ui_entity_metadata.js';
import { ProjectTargetValidation as importedProjectTargetValidation } from './features/projects/project_target_validation.js';
import { DarklabProjectWorkspaceConstants as importedProjectWorkspaceConstants } from './features/projects/project_workspace_constants.js';
import { bindDisclosure as importedBindDisclosure } from './ui/ui_disclosure.js';
import { bindDismissible as importedBindDismissible } from './ui/ui_dismissible.js';
import { bindFocusTrap as importedBindFocusTrap } from './ui/ui_focus_trap.js';
import { bindMobileSheet as importedBindMobileSheet } from './ui/mobile_sheet.js';
import { bindOutsideClickClose as importedBindOutsideClickClose } from './ui/ui_outside_click.js';
import { bindPressable as importedBindPressable } from './ui/ui_pressable.js';
import { showConfirm as importedShowConfirm } from './ui/ui_confirm.js';
import {
  blurVisibleComposerInputIfMobile as importedBlurVisibleComposerInputIfMobile,
  enhanceAppSelects as importedEnhanceAppSelects,
  focusElement as importedFocusElement,
  hideModalOverlay as importedHideModalOverlay,
  markInteractionSurfaceReady as importedMarkInteractionSurfaceReady,
  refocusComposerAfterAction as importedRefocusComposerAfterAction,
  setComposerValue as importedSetComposerValue,
  showModalOverlay as importedShowModalOverlay,
  showWorkflowsOverlay as importedShowWorkflowsOverlay,
} from './ui/ui_helpers.js';
import { useMobileTerminalViewportMode as importedUseMobileTerminalViewportMode } from './features/mobile/mobile_shell_layout.js';
import { confirmBrowserLogOut } from './features/preferences/browser_logout.js';

let importedOpenStatusMonitor = importedRuntimeOpenStatusMonitor;
let importedProjectActivity;
let importedProjectAssessment;
let importedProjectArtifacts;
let importedProjectWebSurface;
let importedProjectDetails;
let importedProjectEntities;
let importedProjectEntityEditor;
let importedProjectFilters;
let importedProjectFindings;
let importedProjectFindingsBoard;
let importedProjectFindingsData;
let importedProjectList;
let importedProjectMobileCompare;
let importedProjectMobileDetail;
let importedProjectMobileShell;
let importedProjectMonitoring;
let importedProjectNavigation;
let importedProjectNestedSheets;
let importedProjectOverview;
let importedProjectPackages;
let importedProjectReport;
let importedProjectRuns;
let importedProjectTargets;
let importedProjectWorkspaceActions;
let importedProjectWorkspaceBootstrap;
let importedProjectWorkspaceEvents;
let importedProjectWorkspaceLifecycle;
let importedProjectWorkspaceRenderer;
let importedProjectWorkspaceShell;

(function initShellChrome(global) {
  if (typeof document === 'undefined') return;

  function _shellFn(name, imported = null) {
    if (typeof imported === 'function') return imported;
    const fn = global && global[name];
    return typeof fn === 'function' ? fn : null;
  }

  function _shellValue(name, imported = undefined) {
    return imported !== undefined ? imported : (global ? global[name] : undefined);
  }

  function _projectModule(name, imported = undefined) {
    return _shellValue(name, imported) || null;
  }

  const _shellApiFetch = (...args) => _shellFn('apiFetch', importedApiFetch)?.(...args);
  const _shellLogClientError = (...args) => _shellFn('logClientError', importedLogClientError)?.(...args);
  const _shellShowToast = (...args) => _shellFn('showToast', importedShowToast)?.(...args);
  const _shellBindPressable = (...args) => _shellFn('bindPressable', importedBindPressable)?.(...args);
  const _shellBindOutsideClickClose = (...args) => _shellFn('bindOutsideClickClose', importedBindOutsideClickClose)?.(...args);
  const _shellBindDisclosure = (...args) => _shellFn('bindDisclosure', importedBindDisclosure)?.(...args);
  const _shellGetPreference = (name) => _shellFn('getPreference', importedGetPreference)?.(name) || '';
  const _shellSetPreferenceCookie = (name, value) => _shellFn('setPreferenceCookie', importedSetPreferenceCookie)?.(name, value);
  const _shellRefocusComposer = (...args) => _shellFn('refocusComposerAfterAction', importedRefocusComposerAfterAction)?.(...args);
  const _shellSetComposerValue = (...args) => _shellFn('setComposerValue', importedSetComposerValue)?.(...args);
  const _shellDownloadBlobAsAttachment = (...args) => _shellFn('downloadBlobAsAttachment', importedDownloadBlobAsAttachment)?.(...args);
  const _shellDownloadUrlAsAttachment = (...args) => _shellFn('downloadUrlAsAttachment', importedDownloadUrlAsAttachment)?.(...args);
  const _shellShowConfirm = (...args) => _shellFn('showConfirm', importedShowConfirm)?.(...args);
  const _shellUseMobileTerminalViewportMode = () => !!_shellFn('useMobileTerminalViewportMode', importedUseMobileTerminalViewportMode)?.();
  const _shellResetCmdHistoryNav = (...args) => _shellFn('resetCmdHistoryNav', importedResetCmdHistoryNav)?.(...args);
  const _shellGetActiveTabId = () => _shellFn('getActiveTabId', importedGetActiveTabId)?.() || null;
  const _shellGetAppState = () => _shellFn('getAppState', importedGetAppState)?.() || {};
  const _shellGetTab = (id) => _shellFn('getTab', importedGetTab)?.(id) || null;
  const _shellTabs = () => {
    const list = _shellFn('getTabs', importedGetTabs)?.();
    return Array.isArray(list) ? list : [];
  };
  const _shellEmitUiEvent = (...args) => _shellFn('emitUiEvent', importedEmitUiEvent)?.(...args);
  const _shellOnUiEvent = (...args) => _shellFn('onUiEvent', importedOnUiEvent)?.(...args);

  async function _shellLoadLazyModal(importedLoader, globalLoaderName) {
    const loader = _shellFn(globalLoaderName, importedLoader);
    return typeof loader === 'function' ? loader() : null;
  }

  async function _shellOpenAtlas(...args) {
    const open = _shellFn('openAtlas', importedOpenAtlas);
    return typeof open === 'function' ? open(...args) : undefined;
  }

  function _reportSurfaceOpenFailure(surface, err, { source = '' } = {}) {
    const normalized = String(surface || '').toLowerCase();
    const isAtlasSurface = normalized === 'atlas';
    const label = isAtlasSurface ? 'Atlas' : 'Projects';
    const event = isAtlasSurface ? 'ATLAS_OPEN_FAILED' : 'PROJECT_WORKSPACE_OPEN_FAILED';
    _shellLogClientError(`failed to open ${label}`, err, {
      event,
      level: 'error',
      source,
    });
    _shellShowToast(`Failed to open ${label}. Please try again.`, 'error');
  }

  function _openAtlasFromSurface(source = '') {
    void _shellOpenAtlas({ source })
      .catch((err) => _reportSurfaceOpenFailure('atlas', err, { source }));
  }

  function _openAtlasQuickLookupFromSurface(source = '') {
    const open = _shellFn('openAtlasQuickLookupFromSurface', importedOpenAtlasQuickLookupFromSurface);
    if (typeof open !== 'function') return;
    void open(source, {
      onFailure: () => _shellShowToast('Failed to open Quick Lookup. Please try again.', 'error'),
    });
  }

  function _openProjectWorkspaceFromSurface(source = '') {
    void openProjectWorkspace()
      .catch((err) => _reportSurfaceOpenFailure('projects', err, { source }));
  }

  async function _shellOpenCommandRegistry(...args) {
    const registry = await _shellLoadLazyModal(importedLoadCommandRegistry, 'loadCommandRegistry');
    const open = registry?.openCommandRegistry || _shellFn('openCommandRegistry', importedOpenCommandRegistry);
    return typeof open === 'function' ? open(...args) : undefined;
  }

  async function _shellOpenFindingsBoard(...args) {
    const board = await _shellLoadLazyModal(importedLoadFindingsBoard, 'loadFindingsBoard');
    const open = board?.openFindingsBoard || _shellFn('openFindingsBoard');
    return typeof open === 'function' ? open(...args) : undefined;
  }

  async function _shellOpenSchedulesModal(...args) {
    const schedules = await _shellLoadLazyModal(importedLoadSchedulesModal, 'loadSchedulesModal');
    const open = schedules?.openSchedulesModal || _shellFn('openSchedulesModal');
    return typeof open === 'function' ? open(...args) : undefined;
  }

  async function _shellOpenWatchersModal(...args) {
    const watchers = await _shellLoadLazyModal(importedLoadWatchersModal, 'loadWatchersModal');
    const open = watchers?.openWatchersModal || _shellFn('openWatchersModal');
    return typeof open === 'function' ? open(...args) : undefined;
  }

  function _shellActiveTeamScopeCan(capability) {
    const can = (typeof importedActiveTeamScopeCan !== 'undefined' && importedActiveTeamScopeCan) || null;
    return typeof can === 'function' ? can(capability) : true;
  }

  function _shellTeamScopeDeniedMessage(action) {
    const denied = (typeof importedTeamScopeDeniedMessage !== 'undefined' && importedTeamScopeDeniedMessage) || null;
    return typeof denied === 'function'
      ? denied(action)
      : `View-only team members can't ${action}. Switch to Personal or ask for operator access.`;
  }

  function _shellEnhanceAppSelects() {
    return (typeof importedEnhanceAppSelects !== 'undefined' && importedEnhanceAppSelects) || null;
  }

  // ── Elements ────────────────────────────────────────────────────
  const rail              = document.getElementById('rail');
  if (!rail) return; // mobile-only DOM build; nothing to do

  const railCollapseBtn   = document.getElementById('rail-collapse-btn');
  const railResizeHandle  = document.getElementById('rail-resize-handle');
  const railSplitArea     = document.getElementById('rail-split-area');
  const railSplitter      = document.getElementById('rail-splitter');
  const railSectionRecent = document.getElementById('rail-section-recent');
  const railRecentBody    = document.getElementById('rail-recent-list');
  const railRecentCount   = document.getElementById('rail-recent-count');
  const railRecentHeader  = document.getElementById('rail-recent-header');
  const railSectionWorkflows = document.getElementById('rail-section-workflows');
  const railWorkflowsBody = document.getElementById('rail-workflows-list');
  const railWorkflowsHeader = document.getElementById('rail-workflows-header');
  const railWorkflowsCount = document.getElementById('rail-workflows-count');
  const railNav           = document.getElementById('rail-nav');
  const railMoreBtn       = document.getElementById('rail-more-btn');
  const railMoreMenu      = document.getElementById('rail-more-menu');

  const hud               = document.getElementById('hud');
  const hudLastExitEl     = document.getElementById('hud-last-exit');
  const hudTabsEl         = document.getElementById('hud-tabs');
  const hudLatencyEl      = document.getElementById('hud-latency');
  const hudSessionEl      = document.getElementById('hud-session');
  const mobileAccessStateEl = document.getElementById('mobile-menu-access-state');
  const hudProjectCell    = document.getElementById('hud-project-cell');
  const hudProjectEl      = document.getElementById('hud-project');
  const hudUptimeEl       = document.getElementById('hud-uptime');
  const hudClockEl        = document.getElementById('hud-clock');
  const hudDbEl           = document.getElementById('hud-db');
  const hudRedisEl        = document.getElementById('hud-redis');
  let projectWorkspaceOverlay = null;
  let projectWorkspaceModal = null;
  let projectWorkspaceBody = null;
  let projectWorkspacePagination = null;
  let projectExplorerBody = null;
  let projectWorkspaceSubtitle = null;
  let projectWorkspaceCreateForm = null;
  let projectWorkspaceNameInput = null;
  let projectMobileRoot = null;
  let projectMobileListView = null;
  let projectMobileBody = null;
  let projectMobilePagination = null;
  let projectMobileSummary = null;
  let projectMobileCreateForm = null;
  let projectMobileNameInput = null;
  let projectMobileDetailView = null;
  let projectMobileDetailTopbar = null;
  let projectMobileTabs = null;
  let projectMobileDetailBody = null;
  let projectWorkspaceOpenToken = 0;
  let projectWorkspaceDomPromise = null;
  let projectWorkspaceReturnToAtlas = null;
  let projectTargetEditorOverlay = null;
  let projectTargetEditorTitle = null;
  let projectTargetCreateForm = null;
  let projectTargetTypeSelect = null;
  let projectTargetValueInput = null;
  let projectTargetValueHelp = null;
  let projectTargetValueError = null;
  let projectTargetLabelInput = null;
  let projectTargetNotesInput = null;
  let projectTargetSubmitButton = null;
  let projectPackageManifestOverlay = null;
  let projectPackageManifestTitle = null;
  let projectPackageManifestSummary = null;
  let projectPackageManifestJson = null;
  let projectPackageWizardOverlay = null;
  let projectPackageWizardBody = null;
  let projectEntityEditorOverlay = null;
  let projectEntityEditorTitle = null;
  let projectEntityEditorSubtitle = null;
  let projectEntityEditorForm = null;
  let projectEntityLabelsInput = null;
  let projectEntityNoteInput = null;
  let projectEntityActivityRoot = null;
  let projectEntitySubmitButton = null;
  let projectNotesForm = null;
  let projectNotesInput = null;
  let projectLabelsForm = null;
  let projectLabelsInput = null;
  let projectLabelsSaveButton = null;
  let projectWorkspaceMessage = null;
  const EntityMetadataClient = (typeof importedEntityMetadata !== 'undefined' && importedEntityMetadata) || {};
  const PROJECT_WORKSPACE_FRAGMENT_URL = '/static/fragments/project_workspace.html';

  function _refreshProjectWorkspaceElements() {
    projectWorkspaceOverlay = document.getElementById('project-workspace-overlay');
    projectWorkspaceModal = document.getElementById('project-workspace-modal');
    projectWorkspaceBody = document.getElementById('project-workspace-body');
    projectWorkspacePagination = document.getElementById('project-workspace-pagination');
    projectExplorerBody = document.getElementById('project-explorer-body');
    projectWorkspaceSubtitle = document.getElementById('project-workspace-subtitle');
    projectWorkspaceCreateForm = document.getElementById('project-workspace-create-form');
    projectWorkspaceNameInput = document.getElementById('project-workspace-name');
    projectMobileRoot = document.getElementById('project-mobile-root');
    projectMobileListView = document.getElementById('project-mobile-list-view');
    projectMobileBody = document.getElementById('project-mobile-body');
    projectMobilePagination = document.getElementById('project-mobile-pagination');
    projectMobileSummary = document.getElementById('project-mobile-summary');
    projectMobileCreateForm = document.getElementById('project-mobile-create-form');
    projectMobileNameInput = document.getElementById('project-mobile-name');
    projectMobileDetailView = document.getElementById('project-mobile-detail-view');
    projectMobileDetailTopbar = document.getElementById('project-mobile-detail-topbar');
    projectMobileTabs = document.getElementById('project-mobile-tabs');
    projectMobileDetailBody = document.getElementById('project-mobile-detail-body');
    projectTargetEditorOverlay = document.getElementById('project-target-editor-overlay');
    projectTargetEditorTitle = document.getElementById('project-target-editor-title');
    projectTargetCreateForm = document.getElementById('project-target-create-form');
    projectTargetTypeSelect = document.getElementById('project-target-type');
    projectTargetValueInput = document.getElementById('project-target-value');
    projectTargetValueHelp = document.getElementById('project-target-value-help');
    projectTargetValueError = document.getElementById('project-target-value-error');
    projectTargetLabelInput = document.getElementById('project-target-label');
    projectTargetNotesInput = document.getElementById('project-target-notes');
    projectTargetSubmitButton = document.getElementById('project-target-submit');
    projectPackageManifestOverlay = document.getElementById('project-package-manifest-overlay');
    projectPackageManifestTitle = document.getElementById('project-package-manifest-title');
    projectPackageManifestSummary = document.getElementById('project-package-manifest-summary');
    projectPackageManifestJson = document.getElementById('project-package-manifest-json');
    projectPackageWizardOverlay = document.getElementById('project-package-wizard-overlay');
    projectPackageWizardBody = document.getElementById('project-package-wizard-body');
    projectEntityEditorOverlay = document.getElementById('project-entity-editor-overlay');
    projectEntityEditorTitle = document.getElementById('project-entity-editor-title');
    projectEntityEditorSubtitle = document.getElementById('project-entity-editor-subtitle');
    projectEntityEditorForm = document.getElementById('project-entity-editor-form');
    projectEntityLabelsInput = document.getElementById('project-entity-labels');
    projectEntityNoteInput = document.getElementById('project-entity-note');
    projectEntityActivityRoot = document.getElementById('project-entity-activity');
    projectEntitySubmitButton = document.getElementById('project-entity-submit');
    projectNotesForm = document.getElementById('project-notes-form');
    projectNotesInput = document.getElementById('project-notes-input');
    projectLabelsForm = document.getElementById('project-labels-form');
    projectLabelsInput = document.getElementById('project-labels-input');
    projectLabelsSaveButton = document.getElementById('project-labels-save-btn');
    projectWorkspaceMessage = document.getElementById('project-workspace-message');
  }

  function _mountProjectWorkspaceFragment(html) {
    const template = document.createElement('template');
    template.innerHTML = String(html || '').trim();
    if (!template.content || !template.content.childNodes.length) {
      throw new Error('Project workspace fragment is empty');
    }
    const anchor = document.getElementById('finding-triage-overlay') || document.getElementById('history-panel');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(template.content, anchor);
    } else {
      document.body.appendChild(template.content);
    }
  }

  async function _ensureProjectWorkspaceDom() {
    _refreshProjectWorkspaceElements();
    if (projectWorkspaceOverlay && projectWorkspaceModal && projectWorkspaceBody && projectExplorerBody) {
      return projectWorkspaceOverlay;
    }
    if (typeof fetch !== 'function' || !document.body) return projectWorkspaceOverlay;
    if (!projectWorkspaceDomPromise) {
      projectWorkspaceDomPromise = fetch(PROJECT_WORKSPACE_FRAGMENT_URL, {
        credentials: 'same-origin',
        cache: 'no-cache',
      })
        .then((resp) => {
          if (!resp || !resp.ok) {
            const status = resp && typeof resp.status !== 'undefined' ? resp.status : 'unknown';
            throw new Error(`Failed to load project workspace fragment: ${status}`);
          }
          return resp.text();
        })
        .then((html) => {
          if (!document.getElementById('project-workspace-overlay')) {
            _mountProjectWorkspaceFragment(html);
          }
          _refreshProjectWorkspaceElements();
          if (!projectWorkspaceOverlay || !projectWorkspaceModal || !projectWorkspaceBody || !projectExplorerBody) {
            throw new Error('Project workspace fragment missing required shell');
          }
          return projectWorkspaceOverlay;
        })
        .catch((err) => {
          projectWorkspaceDomPromise = null;
          throw err;
        });
    }
    return projectWorkspaceDomPromise;
  }

  _refreshProjectWorkspaceElements();

  // ── Prefs (cookie-backed) ───────────────────────────────────────
  const PREF_COLLAPSED = 'pref_rail_collapsed';
  const PREF_WIDTH     = 'pref_rail_width';
  const PREF_RECENT    = 'pref_rail_recent_open';
  const PREF_WORKFLOWS = 'pref_rail_workflows_open';

  const MIN_W = 180, MAX_W = 360, DEFAULT_W = 214;
  const NARROW_BRAND_W = 200;
  const MIN_SECTION_H = 80;
  const PROJECT_TARGET_HELPERS = (typeof importedProjectTargetValidation !== 'undefined' && importedProjectTargetValidation) || null;
  if (!PROJECT_TARGET_HELPERS) throw new Error('ProjectTargetValidation is unavailable');
  const PROJECT_TARGET_TYPES = PROJECT_TARGET_HELPERS.TARGET_TYPES;
  const PROJECT_WORKSPACE_CONSTANTS = (
    typeof importedProjectWorkspaceConstants !== 'undefined'
    && importedProjectWorkspaceConstants
  ) || null;
  if (!PROJECT_WORKSPACE_CONSTANTS) throw new Error('DarklabProjectWorkspaceConstants is unavailable');

  const readBool = (name, dflt) => {
    const v = _shellGetPreference(name);
    if (v === '1' || v === 'true') return true;
    if (v === '0' || v === 'false') return false;
    return dflt;
  };
  const writePref = (name, value) => {
    _shellSetPreferenceCookie(name, String(value));
  };

  // ── State ────────────────────────────────────────────────────────
  const ui = {
    collapsed: readBool(PREF_COLLAPSED, false),
    railW: (() => {
      const raw = parseInt(_shellGetPreference(PREF_WIDTH), 10);
      return Number.isFinite(raw) ? Math.max(MIN_W, Math.min(MAX_W, raw)) : DEFAULT_W;
    })(),
    recentOpen: readBool(PREF_RECENT, true),
    workflowsOpen: readBool(PREF_WORKFLOWS, true),
    recentHeight: null, // null → auto-size next time Workflows opens
  };

  let allWorkflows = [];
  const projectWorkspaceStateFactory = importedProjectWorkspaceState
    && importedProjectWorkspaceState.createProjectWorkspaceState;
  if (typeof projectWorkspaceStateFactory !== 'function') throw new Error('DarklabProjectWorkspaceState is unavailable');
  const projectWorkspaceState = projectWorkspaceStateFactory();
  const PROJECT_WORKSPACE_CORE_GLOBALS = [
    ['DarklabProjectDetails', 'createProjectDetailsController'],
    ['DarklabProjectList', 'createProjectListController'],
    ['DarklabProjectNavigation', 'createProjectNavigationController'],
    ['DarklabProjectWorkspaceShell', 'createProjectWorkspaceShellController'],
    ['DarklabProjectWorkspaceLifecycle', 'createProjectWorkspaceLifecycleController'],
    ['DarklabProjectWorkspaceRenderer', 'createProjectWorkspaceRendererController'],
    ['DarklabProjectWorkspaceBootstrap', 'createProjectWorkspaceBootstrapController'],
    ['DarklabProjectWorkspaceEvents', 'createProjectWorkspaceEventsController'],
    ['DarklabProjectFilters', 'createProjectFiltersController'],
    ['DarklabProjectTargets', 'createProjectTargetsController'],
  ];
  let projectWorkspaceModulesPromise = null;
  const projectWorkspaceDeferredModulePromises = new Map();
  let projectWorkspaceBootstrapped = false;

  function _projectWorkspaceModuleReady(name, factory) {
    return !!(global[name] && typeof global[name][factory] === 'function');
  }

  function _projectFactoryReady(name, imported, factory) {
    const namespace = _projectModule(name, imported);
    return !!(namespace && typeof namespace[factory] === 'function');
  }

  function _projectWorkspaceModulesReady() {
    return PROJECT_WORKSPACE_CORE_GLOBALS.every(([name, factory]) => _projectWorkspaceModuleReady(name, factory));
  }

  function _projectWorkspaceOverlayOpenFallback() {
    _refreshProjectWorkspaceElements();
    return !!(projectWorkspaceOverlay && projectWorkspaceOverlay.classList.contains('open'));
  }

  function _bindProjectWorkspaceIfNeeded() {
    if (projectWorkspaceBootstrapped) return;
    _projectWorkspaceBootstrapController().bindAll();
    projectWorkspaceBootstrapped = true;
  }

  async function _loadProjectWorkspaceModules(moduleNames) {
    const loader = global.loadProjectWorkspace;
    if (typeof loader !== 'function') throw new Error('Project workspace loader is unavailable');
    return loader({ modules: moduleNames });
  }

  async function _loadProjectWorkspaceDeferredModules(moduleNames) {
    const names = Array.from(new Set((Array.isArray(moduleNames) ? moduleNames : [])
      .map(name => String(name || '').trim())
      .filter(Boolean)));
    if (!names.length) return {};
    const key = names.slice().sort().join('|');
    if (!projectWorkspaceDeferredModulePromises.has(key)) {
      projectWorkspaceDeferredModulePromises.set(key, _loadProjectWorkspaceModules(names).finally(() => {
        projectWorkspaceDeferredModulePromises.delete(key);
      }));
    }
    return projectWorkspaceDeferredModulePromises.get(key);
  }

  async function _ensureProjectWorkspaceModules() {
    await _ensureProjectWorkspaceDom();
    if (_projectWorkspaceModulesReady()) {
      _bindProjectWorkspaceIfNeeded();
      return;
    }
    if (!projectWorkspaceModulesPromise) {
      projectWorkspaceModulesPromise = _loadProjectWorkspaceModules()
        .then(() => {
          if (!_projectWorkspaceModulesReady()) throw new Error('Project workspace modules did not finish loading');
          _bindProjectWorkspaceIfNeeded();
        })
        .finally(() => {
          projectWorkspaceModulesPromise = null;
        });
    }
    await projectWorkspaceModulesPromise;
  }
  // ── Layout application ──────────────────────────────────────────
  function applyCollapsed() {
    rail.classList.toggle('rail-collapsed', ui.collapsed);
    rail.classList.toggle('rail-narrow-brand', !ui.collapsed && ui.railW <= NARROW_BRAND_W);
    rail.style.setProperty('--rail-w', ui.collapsed ? '44px' : `${ui.railW}px`);
    if (railCollapseBtn) {
      railCollapseBtn.textContent = ui.collapsed ? '»' : '«';
      const label = ui.collapsed ? 'Expand sidebar (Alt+\\)' : 'Collapse sidebar (Alt+\\)';
      railCollapseBtn.title = label;
      railCollapseBtn.setAttribute('aria-label', label);
    }
  }

  function applyWidth() {
    rail.classList.toggle('rail-narrow-brand', !ui.collapsed && ui.railW <= NARROW_BRAND_W);
    if (!ui.collapsed) rail.style.setProperty('--rail-w', `${ui.railW}px`);
  }

  function applySectionsState() {
    if (!railSplitArea) return;
    railSectionRecent?.classList.toggle('closed', !ui.recentOpen);
    railSectionWorkflows?.classList.toggle('closed', !ui.workflowsOpen);

    const bothOpen = ui.recentOpen && ui.workflowsOpen;
    railSplitArea.classList.toggle('both-open', bothOpen);
    railSplitArea.classList.toggle('workflows-closed', !ui.workflowsOpen);
    railSplitArea.classList.toggle('recent-fixed', bothOpen && ui.recentHeight != null);

    if (railSplitter) railSplitter.hidden = !bothOpen;

    if (bothOpen && ui.recentHeight != null) {
      railSplitArea.style.setProperty('--recent-h', `${ui.recentHeight}px`);
    } else {
      railSplitArea.style.removeProperty('--recent-h');
    }
  }

  // ── Collapse ─────────────────────────────────────────────────────
  function setCollapsed(next) {
    ui.collapsed = !!next;
    applyCollapsed();
    writePref(PREF_COLLAPSED, ui.collapsed ? '1' : '0');
  }
  function toggleRailCollapsed() {
    setCollapsed(!ui.collapsed);
  }
  railCollapseBtn?.addEventListener('click', () => setCollapsed(!ui.collapsed));

  // ── Horizontal drag ──────────────────────────────────────────────
  let railDrag = null;
  function beginRailDrag(clientX) {
    railDrag = { startX: clientX, startW: ui.railW };
    rail.classList.add('rail-dragging');
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }
  railResizeHandle?.addEventListener('mousedown', e => {
    if (ui.collapsed) return;
    e.preventDefault();
    beginRailDrag(e.clientX);
  });

  // ── Splitter drag ────────────────────────────────────────────────
  let splitterDrag = null;
  function beginSplitterDrag(clientY) {
    if (!railSplitArea) return;
    splitterDrag = { rect: railSplitArea.getBoundingClientRect() };
    rail.classList.add('rail-dragging');
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }
  railSplitter?.addEventListener('mousedown', e => {
    e.preventDefault();
    beginSplitterDrag(e.clientY);
  });

  function clampRecentHeight(pixels) {
    if (!railSplitArea) return pixels;
    const areaH = railSplitArea.getBoundingClientRect().height;
    return Math.max(MIN_SECTION_H, Math.min(areaH - MIN_SECTION_H - 6, pixels));
  }

  window.addEventListener('mousemove', e => {
    if (railDrag) {
      const next = Math.max(MIN_W, Math.min(MAX_W, railDrag.startW + (e.clientX - railDrag.startX)));
      ui.railW = next;
      applyWidth();
    } else if (splitterDrag) {
      const offsetY = e.clientY - splitterDrag.rect.top;
      ui.recentHeight = clampRecentHeight(offsetY);
      applySectionsState();
    }
  });

  window.addEventListener('mouseup', () => {
    if (railDrag) {
      railDrag = null;
      writePref(PREF_WIDTH, ui.railW);
    }
    if (splitterDrag) splitterDrag = null;
    rail.classList.remove('rail-dragging');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  });

  // ── Section toggles ──────────────────────────────────────────────
  // Rail section headers own their open/closed state via bindDisclosure.
  // `panel: null` + `openClass: null` lets applySectionsState stay the sole
  // writer of the `.closed` class on the section element (it has to
  // coordinate both sections plus the splitter and sizing vars, so letting
  // the helper also toggle classes would produce double-writes). The helper
  // still owns aria-expanded on the header and the post-activation focus
  // contract.
  function onRecentToggle(open) {
    ui.recentOpen = open;
    writePref(PREF_RECENT, open ? '1' : '0');
    applySectionsState();
  }
  function onWorkflowsToggle(open) {
    ui.workflowsOpen = open;
    writePref(PREF_WORKFLOWS, open ? '1' : '0');
    applySectionsState();
  }

  if (railRecentHeader) {
    _shellBindDisclosure(railRecentHeader, {
      panel: null,
      openClass: null,
      initialOpen: ui.recentOpen,
      onToggle: onRecentToggle,
    });
  }
  if (railWorkflowsHeader) {
    _shellBindDisclosure(railWorkflowsHeader, {
      panel: null,
      openClass: null,
      initialOpen: ui.workflowsOpen,
      onToggle: onWorkflowsToggle,
    });
  }

  // ── Recent list rendering ───────────────────────────────────────
  function renderRailRecent() {
    if (!railRecentBody) return;
    const recentPreviewHistory = _shellGetAppState().recentPreviewHistory;
    const items = Array.isArray(recentPreviewHistory) ? recentPreviewHistory : [];
    railRecentBody.replaceChildren();
    if (railRecentCount) railRecentCount.textContent = String(items.length);

    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'rail-section-empty';
      empty.textContent = 'no commands yet';
      railRecentBody.appendChild(empty);
      return;
    }
    // Partition starred-first while preserving original recency order within
    // each group. The star toggle lives in the history drawer / mobile sheet
    // (one source of truth); the rail only reflects the state via ordering
    // and an amber left-edge stripe.
    const starred = typeof importedGetStarred === 'function' ? importedGetStarred() : new Set();
    const ordered = [
      ...items.filter(cmd => starred.has(cmd)),
      ...items.filter(cmd => !starred.has(cmd)),
    ];
    ordered.forEach(cmd => {
      const isStarred = starred.has(cmd);
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'rail-item' + (isStarred ? ' starred' : '');
      row.title = cmd;
      const text = document.createElement('span');
      text.className = 'rail-item-text';
      text.textContent = cmd;
      row.appendChild(text);
      row.addEventListener('click', () => {
        _shellSetComposerValue(cmd, cmd.length, cmd.length);
        _shellRefocusComposer({ preventScroll: true });
        _shellResetCmdHistoryNav();
      });
      railRecentBody.appendChild(row);
    });
  }

  // ── Workflows list rendering ────────────────────────────────────
  function renderRailWorkflows(items) {
    allWorkflows = Array.isArray(items) ? items.slice() : [];
    if (railWorkflowsCount) railWorkflowsCount.textContent = String(allWorkflows.length);
    if (!railWorkflowsBody) return;
    railWorkflowsBody.replaceChildren();
    const browseRow = document.createElement('button');
    browseRow.type = 'button';
    browseRow.className = 'rail-item rail-workflows-browse-all';
    browseRow.title = 'Browse all workflows';
    const browseGlyph = document.createElement('span');
    browseGlyph.className = 'drill-chev';
    browseGlyph.setAttribute('aria-hidden', 'true');
    browseGlyph.textContent = '›';
    const browseText = document.createElement('span');
    browseText.className = 'rail-item-text';
    browseText.textContent = 'Browse all workflows';
    browseRow.append(browseGlyph, browseText);
    browseRow.addEventListener('click', () => openWorkflowWorkspace());
    railWorkflowsBody.appendChild(browseRow);
    if (!allWorkflows.length) {
      const empty = document.createElement('div');
      empty.className = 'rail-section-empty';
      empty.textContent = 'no workflows';
      railWorkflowsBody.appendChild(empty);
      return;
    }
    allWorkflows.forEach((wf, idx) => {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'rail-item';
      const label = wf.title || wf.name || `workflow ${idx + 1}`;
      row.title = [label, wf.description].filter(Boolean).join('\n');
      const glyph = document.createElement('span');
      glyph.className = 'drill-chev';
      glyph.setAttribute('aria-hidden', 'true');
      glyph.textContent = '›';
      const text = document.createElement('span');
      text.className = 'rail-item-text';
      text.textContent = label;
      row.appendChild(glyph);
      row.appendChild(text);
      row.addEventListener('click', () => openWorkflowWorkspace(wf.id));
      railWorkflowsBody.appendChild(row);
    });
  }

  async function openWorkflowWorkspace(selectedWorkflowId = '') {
    const loadWorkflowsFn = _shellFn('loadWorkflows');
    if (loadWorkflowsFn) {
      try { await loadWorkflowsFn(); } catch (_) { /* non-critical */ }
    }
    const openWorkflowsFn = _shellFn('openWorkflows', importedOpenWorkflows);
    if (openWorkflowsFn) {
      openWorkflowsFn({ selectedWorkflowId, view: 'workflows' });
    } else {
      _shellFn('showWorkflowsOverlay', importedShowWorkflowsOverlay)?.();
    }
  }

  // ── Nav menu ─────────────────────────────────────────────────────
  // The visible rail is the desktop source of truth. Route clicks directly
  // into the shared action layer.
  function positionRailMoreMenu() {
    if (!railMoreBtn || !railMoreMenu || railMoreMenu.classList.contains('u-hidden')) return;
    if (typeof railMoreBtn.getBoundingClientRect !== 'function') return;
    const triggerRect = railMoreBtn.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1024;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 768;
    const gutter = 8;
    const menuWidth = Math.max(railMoreMenu.offsetWidth || 220, 180);
    const menuHeight = Math.max(railMoreMenu.offsetHeight || railMoreMenu.getBoundingClientRect?.().height || 1, 1);
    const maxMenuHeight = Math.max(120, viewportHeight - (gutter * 2));
    const effectiveMenuHeight = Math.min(menuHeight, maxMenuHeight);
    const desiredArrowFromBottom = 32;
    const triggerCenterY = triggerRect.top + (triggerRect.height / 2);
    const preferredTop = triggerCenterY - Math.max(28, effectiveMenuHeight - desiredArrowFromBottom);
    const top = Math.min(
      Math.max(gutter, preferredTop),
      Math.max(gutter, viewportHeight - effectiveMenuHeight - gutter),
    );
    const left = Math.min(
      Math.max(gutter, triggerRect.right + 8),
      Math.max(gutter, viewportWidth - menuWidth - gutter),
    );
    const arrowLimit = Math.max(18, effectiveMenuHeight - 18);
    const arrowY = Math.min(arrowLimit, Math.max(18, triggerCenterY - top - 4));
    railMoreMenu.style.position = 'fixed';
    railMoreMenu.style.left = `${left}px`;
    railMoreMenu.style.top = `${top}px`;
    railMoreMenu.style.right = 'auto';
    railMoreMenu.style.bottom = 'auto';
    railMoreMenu.style.maxHeight = `${maxMenuHeight}px`;
    railMoreMenu.style.overflowY = menuHeight > maxMenuHeight ? 'auto' : '';
    railMoreMenu.style.setProperty('--rail-more-arrow-y', `${arrowY}px`);
  }

  function closeRailMoreMenu() {
    if (!railMoreBtn || !railMoreMenu) return;
    railMoreBtn.setAttribute('aria-expanded', 'false');
    railMoreMenu.classList.add('u-hidden');
    railMoreMenu.style.position = '';
    railMoreMenu.style.left = '';
    railMoreMenu.style.top = '';
    railMoreMenu.style.right = '';
    railMoreMenu.style.bottom = '';
    railMoreMenu.style.maxHeight = '';
    railMoreMenu.style.overflowY = '';
    railMoreMenu.style.removeProperty('--rail-more-arrow-y');
  }

  function openRailMoreMenu() {
    if (!railMoreBtn || !railMoreMenu) return;
    railMoreBtn.setAttribute('aria-expanded', 'true');
    railMoreMenu.classList.remove('u-hidden');
    positionRailMoreMenu();
    railMoreMenu.querySelector('[data-action]:not(.u-hidden)')?.focus?.();
  }

  function toggleRailMoreMenu() {
    if (railMoreBtn?.getAttribute('aria-expanded') === 'true') {
      closeRailMoreMenu();
    } else {
      openRailMoreMenu();
    }
  }

  function openStatusMonitorFromHud(source) {
    const openStatusMonitor = _shellFn('openStatusMonitor', importedOpenStatusMonitor);
    if (typeof openStatusMonitor !== 'function') return;
    void openStatusMonitor({ source });
  }

  function makeHudCellOpenStatusMonitor(cell, source, label) {
    if (!cell || cell.dataset.statusMonitorTrigger === '1') return;
    cell.dataset.statusMonitorTrigger = '1';
    cell.classList.add('hud-cell-clickable', 'hud-action-cell');
    cell.setAttribute('role', 'button');
    cell.setAttribute('tabindex', '0');
    cell.setAttribute('aria-haspopup', 'dialog');
    cell.setAttribute('aria-label', label);
    cell.addEventListener('click', () => openStatusMonitorFromHud(source));
    cell.addEventListener('keydown', event => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openStatusMonitorFromHud(source);
    });
  }

  function bindHudStatusMonitorTriggers() {
    makeHudCellOpenStatusMonitor(
      document.getElementById('hud-status-cell'),
      'status',
      'Open status monitor from status',
    );
    makeHudCellOpenStatusMonitor(
      document.getElementById('hud-last-exit-cell') || document.getElementById('hud-last-exit')?.closest('.hud-cell'),
      'last-exit',
      'Open status monitor from last exit',
    );
    makeHudCellOpenStatusMonitor(
      document.getElementById('hud-tabs-cell') || document.getElementById('hud-tabs')?.closest('.hud-cell'),
      'tabs',
      'Open status monitor from tabs',
    );
  }

  railNav?.addEventListener('click', e => {
    const item = e.target.closest?.('[data-action]');
    if (!item) return;
    const action = item.dataset.action;
    if (action === 'diag' || action === 'admin' || action === 'audit') {
      closeRailMoreMenu();
      return; // native <a> navigation
    }
    e.preventDefault();
    if (action === 'rail-more') {
      toggleRailMoreMenu();
      return;
    }
    closeRailMoreMenu();
    if (action === 'logout') {
      void confirmBrowserLogOut(railMoreBtn);
      return;
    }
    if (action === 'history' && typeof importedToggleHistoryPanelSurface === 'function') {
      importedToggleHistoryPanelSurface();
      return;
    }
    const openStatusMonitor = _shellFn('openStatusMonitor', importedOpenStatusMonitor);
    if (action === 'atlas') {
      _openAtlasFromSurface('rail');
      return;
    }
    if (action === 'quick-lookup') {
      _openAtlasQuickLookupFromSurface('rail');
      return;
    }
    if (action === 'findings-board') {
      void _shellOpenFindingsBoard({ source: 'rail' });
      return;
    }
    if (action === 'status-monitor' && typeof openStatusMonitor === 'function') {
      void openStatusMonitor({ source: 'rail' });
      return;
    }
    if (action === 'command-registry') {
      void _shellOpenCommandRegistry();
      return;
    }
    if (action === 'schedules') {
      void _shellOpenSchedulesModal();
      return;
    }
    if (action === 'watchers') {
      void _shellOpenWatchersModal();
      return;
    }
    if (action === 'projects') {
      _openProjectWorkspaceFromSurface('rail');
      return;
    }
    if (action === 'options' && typeof importedOpenOptions === 'function') {
      importedOpenOptions();
      return;
    }
    if (action === 'theme' && typeof importedOpenThemeSelector === 'function') {
      importedOpenThemeSelector();
      return;
    }
    if (action === 'workspace' && typeof importedOpenWorkspace === 'function') {
      void importedOpenWorkspace();
      return;
    }
    if (action === 'faq' && typeof importedOpenFaq === 'function') {
      importedOpenFaq();
    }
  });

  document.addEventListener('click', event => {
    if (!railMoreMenu || railMoreMenu.classList.contains('u-hidden')) return;
    const target = event.target;
    if (target instanceof Node && railNav?.contains(target)) return;
    closeRailMoreMenu();
  });

  railNav?.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      closeRailMoreMenu();
      railMoreBtn?.focus?.();
    }
  });

  window.addEventListener('resize', positionRailMoreMenu);

  let hudProjectMenu = null;
  let hudProjectMenuSearchInput = null;
  let hudProjectMenuProjects = null;
  let hudProjectMenuNote = null;
  let hudProjectMenuSearchTimer = null;
  let hudProjectMenuRequestId = 0;

  function _isHudProjectMenuOpen() {
    return !!(hudProjectMenu && !hudProjectMenu.classList.contains('u-hidden'));
  }

  function _openProjectsFromHudMenu(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    closeHudProjectMenu();
    _openProjectWorkspaceFromSurface('hud_menu');
  }

  function _canCreateProjectFromHud() {
    return _shellActiveTeamScopeCan('mutate_projects');
  }

  function _projectCreateDeniedTitle() {
    return _shellTeamScopeDeniedMessage('create team projects');
  }

  function _showHudProjectToast(message, tone = 'info') {
      _shellShowToast(message, tone);
  }

  async function _hudProjectResponseMessage(resp, fallback) {
    try {
      const data = await resp.json();
      return data?.error || data?.message || fallback;
    } catch (_) {
      return fallback;
    }
  }

  function _createHudProjectMenuButton({ label, action, title = '', disabled = false, selected = false, onActivate }) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dropdown-item dropdown-item-compact';
    btn.dataset.action = action || '';
    btn.setAttribute('role', selected ? 'menuitemradio' : 'menuitem');
    if (selected) btn.setAttribute('aria-checked', 'true');
    btn.textContent = label;
    if (title) btn.title = title;
    if (disabled) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    }
    const pressable = _shellFn('bindPressable', importedBindPressable);
    if (pressable) {
      pressable(btn, {
        refocusComposer: false,
        onActivate,
      });
    } else if (typeof onActivate === 'function') {
      btn.addEventListener('click', onActivate);
    }
    return btn;
  }

  function _positionHudProjectMenu() {
    if (!hudProjectMenu || !hudProjectCell || !_isHudProjectMenuOpen()) return;
    const rect = hudProjectCell.getBoundingClientRect();
    const menuWidth = hudProjectMenu.offsetWidth || 260;
    const viewportWidth = global.innerWidth || document.documentElement.clientWidth || 0;
    const left = Math.max(8, Math.min(rect.left, Math.max(8, viewportWidth - menuWidth - 8)));
    hudProjectMenu.style.left = `${left}px`;
    hudProjectMenu.style.bottom = `${Math.max(8, (global.innerHeight || 0) - rect.top - 1)}px`;
  }

  function closeHudProjectMenu({ restoreFocus = false } = {}) {
    if (!hudProjectMenu) return;
    if (hudProjectMenuSearchTimer) {
      global.clearTimeout?.(hudProjectMenuSearchTimer);
      hudProjectMenuSearchTimer = null;
    }
    hudProjectMenuRequestId += 1;
    hudProjectMenu.classList.add('u-hidden');
    hudProjectCell?.classList.remove('open');
    hudProjectCell?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && hudProjectCell && typeof hudProjectCell.focus === 'function') {
      hudProjectCell.focus({ preventScroll: true });
    }
  }

  function _focusHudProjectMenuItem(delta) {
    if (!hudProjectMenu) return;
    const items = Array.from(hudProjectMenu.querySelectorAll('.dropdown-item:not([disabled])'));
    if (!items.length) return;
    const currentIdx = items.indexOf(document.activeElement);
    const fallbackIdx = delta > 0 ? -1 : 0;
    const nextIdx = (currentIdx >= 0 ? currentIdx : fallbackIdx) + delta;
    items[(nextIdx + items.length) % items.length]?.focus({ preventScroll: true });
  }

  function _setHudProjectMenuNote(text) {
    if (!hudProjectMenuNote) return;
    hudProjectMenuNote.textContent = text || '';
    hudProjectMenuNote.classList.toggle('u-hidden', !text);
  }

  function _scheduleHudProjectMenuLoad(query) {
    if (hudProjectMenuSearchTimer) {
      global.clearTimeout?.(hudProjectMenuSearchTimer);
      hudProjectMenuSearchTimer = null;
    }
    hudProjectMenuSearchTimer = global.setTimeout?.(() => {
      hudProjectMenuSearchTimer = null;
      void _loadHudProjectMenu(query);
    }, 120) || null;
  }

  async function _selectHudProject(project, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!project || !project.id) return;
    try {
      const resp = await _shellApiFetch('/projects/active', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: project.id }),
      });
      if (!resp.ok) throw new Error(await _hudProjectResponseMessage(resp, 'Unable to set active project.'));
      const data = await resp.json();
      _setActiveProject(data?.project || project);
      closeHudProjectMenu();
      _showHudProjectToast('Active project updated.');
    } catch (err) {
      const message = err?.message || 'Unable to set active project.';
      _setHudProjectMenuNote(message);
      _shellLogClientError('failed to set active project from HUD switcher', err);
      _showHudProjectToast(message, 'error');
    }
  }

  async function _clearHudProject(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    try {
      const resp = await _shellApiFetch('/projects/active', { method: 'DELETE' });
      if (!resp.ok) throw new Error(await _hudProjectResponseMessage(resp, 'Unable to clear active project.'));
      _setActiveProject(null);
      closeHudProjectMenu();
      _showHudProjectToast('Active project cleared.');
    } catch (err) {
      const message = err?.message || 'Unable to clear active project.';
      _setHudProjectMenuNote(message);
      _shellLogClientError('failed to clear active project from HUD switcher', err);
      _showHudProjectToast(message, 'error');
    }
  }

  function _openCreateProjectFromHudMenu(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!_canCreateProjectFromHud()) {
      const message = _projectCreateDeniedTitle();
      _setHudProjectMenuNote(message);
      _showHudProjectToast(message, 'error');
      return;
    }
    closeHudProjectMenu();
    _openProjectWorkspaceFromSurface('hud_create');
  }

  function _renderHudProjectMenuProjects(projects, query = '') {
    if (!hudProjectMenuProjects) return;
    hudProjectMenuProjects.textContent = '';
    const activeProject = _activeProject();
    const activeProjectId = activeProject?.id ? String(activeProject.id) : '';
    const rows = Array.isArray(projects) ? projects : [];

    if (activeProjectId) {
      hudProjectMenuProjects.appendChild(_createHudProjectMenuButton({
        label: 'No project',
        action: 'clear-active-project',
        title: 'Clear active project',
        onActivate: _clearHudProject,
      }));
    }

    rows.forEach((project) => {
      if (!project || !project.id) return;
      const name = _projectDisplayName(project) || String(project.id);
      const selected = String(project.id) === activeProjectId;
      hudProjectMenuProjects.appendChild(_createHudProjectMenuButton({
        label: selected ? `${name} (active)` : name,
        action: 'select-project',
        title: selected ? `Active project: ${name}` : `Set active project: ${name}`,
        selected,
        onActivate: event => _selectHudProject(project, event),
      }));
    });

    if (!hudProjectMenuProjects.children.length) {
      _setHudProjectMenuNote(query ? 'No matching projects.' : 'No projects yet.');
    } else {
      _setHudProjectMenuNote('');
    }
    _positionHudProjectMenu();
  }

  async function _loadHudProjectMenu(query = '') {
    if (!hudProjectMenuProjects) return;
    const requestId = ++hudProjectMenuRequestId;
    const trimmedQuery = String(query || '').trim();
    const params = new URLSearchParams({ mode: 'switcher', limit: '8' });
    if (trimmedQuery) params.set('q', trimmedQuery);
    if (!hudProjectMenuProjects.children.length) {
      _setHudProjectMenuNote('Loading projects...');
    }
    try {
      const resp = await _shellApiFetch(`/projects?${params.toString()}`, { cache: 'no-store' });
      if (requestId !== hudProjectMenuRequestId) return;
      if (!resp.ok) throw new Error(await _hudProjectResponseMessage(resp, 'Unable to load projects.'));
      const data = await resp.json();
      if (requestId !== hudProjectMenuRequestId) return;
      _renderHudProjectMenuProjects(data?.projects || [], trimmedQuery);
    } catch (err) {
      if (requestId !== hudProjectMenuRequestId) return;
      const message = err?.message || 'Unable to load projects.';
      hudProjectMenuProjects.textContent = '';
      _setHudProjectMenuNote(message);
      _shellLogClientError('failed to load HUD project switcher', err);
    }
  }

  function _refreshHudProjectCreateAction() {
    const createBtn = hudProjectMenu?.querySelector('[data-action="create-project"]');
    if (!createBtn) return;
    const allowed = _canCreateProjectFromHud();
    createBtn.disabled = !allowed;
    createBtn.setAttribute('aria-disabled', allowed ? 'false' : 'true');
    createBtn.title = allowed ? 'Open Projects to create a project' : _projectCreateDeniedTitle();
  }

  function _ensureHudProjectMenu() {
    if (hudProjectMenu) return hudProjectMenu;
    const menu = document.createElement('div');
    menu.id = 'hud-project-menu';
    menu.className = 'hud-project-menu dropdown-surface dropdown-up u-hidden';
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', 'Active project switcher');

    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'hud-project-search';
    search.placeholder = 'search projects';
    search.setAttribute('aria-label', 'Search projects');
    search.autocomplete = 'off';
    search.spellcheck = false;
    search.addEventListener('click', event => event.stopPropagation());
    search.addEventListener('input', event => {
      event.stopPropagation();
      _scheduleHudProjectMenuLoad(search.value);
    });
    search.addEventListener('keydown', event => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        closeHudProjectMenu({ restoreFocus: true });
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        _focusHudProjectMenuItem(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        _focusHudProjectMenuItem(-1);
      } else if (event.key === 'Tab') {
        closeHudProjectMenu();
      }
    });
    menu.appendChild(search);

    const projectsSection = document.createElement('div');
    projectsSection.className = 'hud-project-menu-section';
    menu.appendChild(projectsSection);

    const note = document.createElement('div');
    note.className = 'hud-project-menu-note u-hidden';
    menu.appendChild(note);

    const divider = document.createElement('div');
    divider.className = 'hud-project-menu-divider';
    menu.appendChild(divider);

    const createProject = _createHudProjectMenuButton({
      label: 'Create project',
      action: 'create-project',
      title: 'Open Projects to create a project',
      disabled: !_canCreateProjectFromHud(),
      onActivate: _openCreateProjectFromHudMenu,
    });
    menu.appendChild(createProject);

    const openProjects = _createHudProjectMenuButton({
      label: 'Open Projects',
      action: 'open-projects',
      onActivate: _openProjectsFromHudMenu,
    });
    menu.appendChild(openProjects);

    menu.addEventListener('click', event => event.stopPropagation());
    menu.addEventListener('keydown', event => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        closeHudProjectMenu({ restoreFocus: true });
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        _focusHudProjectMenuItem(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        _focusHudProjectMenuItem(-1);
      } else if (event.key === 'Tab') {
        closeHudProjectMenu();
      }
    });

    document.body.appendChild(menu);
    hudProjectMenu = menu;
    hudProjectMenuSearchInput = search;
    hudProjectMenuProjects = projectsSection;
    hudProjectMenuNote = note;

    const bindOutsideClickClose = _shellFn('bindOutsideClickClose', importedBindOutsideClickClose);
    if (bindOutsideClickClose) {
      bindOutsideClickClose(menu, {
        capture: true,
        triggers: hudProjectCell,
        isOpen: _isHudProjectMenuOpen,
        onClose: () => closeHudProjectMenu(),
      });
    }
    return hudProjectMenu;
  }

  function toggleHudProjectMenu(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    _closeHudSaveMenu();
    _ensureHudProjectMenu();
    if (_isHudProjectMenuOpen()) {
      closeHudProjectMenu({ restoreFocus: true });
      return;
    }
    hudProjectMenu.classList.remove('u-hidden');
    hudProjectCell?.classList.add('open');
    hudProjectCell?.setAttribute('aria-expanded', 'true');
    _refreshHudProjectCreateAction();
    if (hudProjectMenuSearchInput) hudProjectMenuSearchInput.value = '';
    _renderHudProjectMenuProjects([], '');
    void _loadHudProjectMenu('');
    _positionHudProjectMenu();
    requestAnimationFrame(_positionHudProjectMenu);
    hudProjectMenuSearchInput?.focus({ preventScroll: true });
  }

  if (hudProjectCell && _shellFn('bindPressable', importedBindPressable)) {
    _shellBindPressable(hudProjectCell, {
      refocusComposer: false,
      onActivate: toggleHudProjectMenu,
    });
  } else {
    hudProjectCell?.addEventListener('click', toggleHudProjectMenu);
    hudProjectCell?.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') toggleHudProjectMenu(event);
    });
  }
  global.addEventListener?.('resize', _positionHudProjectMenu);
  global.addEventListener?.('scroll', _positionHudProjectMenu, true);
  document.addEventListener?.('app:active-project-changed', () => {
    if (!_isHudProjectMenuOpen()) return;
    _refreshHudProjectCreateAction();
    void _loadHudProjectMenu(hudProjectMenuSearchInput?.value || '');
  });
  document.addEventListener?.('app:scope-changed', () => {
    closeHudProjectMenu();
    loadActiveProjectContext().catch(() => {});
  });
  document.addEventListener?.('app:scope-capabilities-changed', () => {
    _refreshHudProjectCreateAction();
  });

  // ── HUD action buttons ──────────────────────────────────────────
  // Desktop-only mirror of the per-tab `.terminal-actions` footer. Each
  // button resolves the active tab at click time so no per-tab wiring is
  // needed; the per-tab footer still exists in the DOM for mobile.
  const hudActions = document.getElementById('hud-actions');
  let hudKillBtn = null;
  let hudShareSnapshotBtn = null;
  let hudCompareBtn = null;

  function _currentTabId() {
    return _shellGetActiveTabId();
  }

  function _closeHudSaveMenu() {
    document.querySelectorAll('.hud-save-wrap.open').forEach(w => w.classList.remove('open'));
  }

  function _makeHudBtn(label, action, onClick, cls = 'btn btn-secondary btn-compact', title = '') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = cls;
    btn.textContent = label;
    if (action) btn.dataset.action = action;
    if (title) btn.title = title;
    // save-menu is a disclosure trigger: suppress auto-refocus so the dropdown
    // retains user attention. Every other HUD button returns focus to the
    // composer after activation.
    const isDisclosure = action === 'save-menu';
    _shellBindPressable(btn, {
      refocusComposer: !isDisclosure,
      onActivate: e => {
        e.preventDefault();
        onClick(e, btn);
      },
    });
    return btn;
  }

  function _bindProjectRuntimePressable(el, options = {}) {
    if (el && _shellFn('bindPressable', importedBindPressable)) {
      _shellBindPressable(el, { onActivate: () => {}, refocusComposer: false, ...options });
    }
    return el;
  }

  function _canCreateHudShareSnapshot() {
    const config = typeof importedGetAppConfig === 'function' ? importedGetAppConfig() : {};
    if (config?.public_shares_enabled === false) return false;
    return _shellActiveTeamScopeCan('manage_history');
  }

  function _hudShareSnapshotDeniedTitle() {
    const config = typeof importedGetAppConfig === 'function' ? importedGetAppConfig() : {};
    if (config?.public_shares_enabled === false) return 'Public share links are disabled for this deployment.';
    return _shellTeamScopeDeniedMessage('create team history snapshots');
  }

  function _refreshHudShareSnapshotState() {
    if (!hudShareSnapshotBtn) return;
    const allowed = _canCreateHudShareSnapshot();
    hudShareSnapshotBtn.disabled = !allowed;
    hudShareSnapshotBtn.title = allowed
      ? 'Share tab as permalink (Option+Shift+P / Alt+Shift+P)'
      : _hudShareSnapshotDeniedTitle();
  }

  function _comparisonRunForTab(tab) {
    const runId = String(tab?.historyRunId || '').trim();
    const runKind = String(tab?.historyRunKind || '').trim();
    if (!runId || tab?.st === 'running' || runKind !== 'external') return null;
    return { id: runId };
  }

  function buildHudActions() {
    if (!hudActions) return;
    hudActions.replaceChildren();
    const copyCurrentTab = typeof importedCopyTab === 'function'
      ? importedCopyTab
      : _shellFn('copyTab');
    const permalinkCurrentTab = typeof importedPermalinkTab === 'function'
      ? importedPermalinkTab
      : _shellFn('permalinkTab');
    const saveCurrentTab = typeof importedSaveTab === 'function'
      ? importedSaveTab
      : _shellFn('saveTab');
    const exportCurrentTabHtml = typeof importedExportTabHtml === 'function'
      ? importedExportTabHtml
      : _shellFn('exportTabHtml');
    const exportCurrentTabPdf = typeof importedExportTabPdf === 'function'
      ? importedExportTabPdf
      : _shellFn('exportTabPdf');

    hudKillBtn = _makeHudBtn('\u25A0 Kill', 'kill', () => {
      const id = _currentTabId();
      const confirmKill = (typeof importedConfirmKill === 'function' && importedConfirmKill)
        || _shellFn('confirmKill');
      if (id) confirmKill?.(id);
    }, 'btn btn-destructive btn-compact u-hidden', 'Kill current run');
    hudActions.appendChild(hudKillBtn);

    hudCompareBtn = _makeHudBtn('compare', 'compare', () => {
      const run = _comparisonRunForTab(_shellGetTab(_currentTabId()));
      if (run) importedOpenHistoryCompareLauncher?.(run, { returnFocus: hudCompareBtn });
    }, 'btn btn-secondary btn-compact u-hidden', 'Compare active run with a previous run');
    hudActions.appendChild(hudCompareBtn);

    hudShareSnapshotBtn = _makeHudBtn('share snapshot', 'permalink', () => {
      const id = _currentTabId();
      if (id && permalinkCurrentTab) permalinkCurrentTab(id);
    }, 'btn btn-secondary btn-compact', 'Share tab as permalink (Option+Shift+P / Alt+Shift+P)');
    hudActions.appendChild(hudShareSnapshotBtn);
    _refreshHudShareSnapshotState();

    hudActions.appendChild(_makeHudBtn('copy', 'copy', () => {
      const id = _currentTabId();
      if (id && copyCurrentTab) copyCurrentTab(id);
    }, 'btn btn-secondary btn-compact', 'Copy tab output (Option+Shift+C)'));

    // Save menu — shares .save-menu markup so existing CSS applies.
    const saveWrap = document.createElement('div');
    saveWrap.className = 'hud-save-wrap';
    const saveBtn = _makeHudBtn('save', 'save-menu', () => {
      closeHudProjectMenu();
      saveWrap.classList.toggle('open');
    }, 'btn btn-secondary btn-compact', 'Save tab output (txt / html / pdf)');
    const saveMenu = document.createElement('div');
    saveMenu.className = 'save-menu dropdown-surface dropdown-up';
    [
      ['Plain text (.txt)',   'save-txt',  () => { const id = _currentTabId(); if (id && saveCurrentTab) saveCurrentTab(id); }],
      ['Styled HTML (.html)', 'save-html', () => { const id = _currentTabId(); if (id && exportCurrentTabHtml) exportCurrentTabHtml(id); }],
      ['PDF document (.pdf)', 'save-pdf',  () => { const id = _currentTabId(); if (id && exportCurrentTabPdf) exportCurrentTabPdf(id); }],
    ].forEach(([label, action, fn]) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'dropdown-item dropdown-item-compact';
      item.textContent = label;
      item.dataset.action = action;
      _shellBindPressable(item, {
        onActivate: e => {
          e.preventDefault();
          e.stopPropagation();
          saveWrap.classList.remove('open');
          fn();
        },
      });
      saveMenu.appendChild(item);
    });
    saveWrap.appendChild(saveBtn);
    saveWrap.appendChild(saveMenu);
    hudActions.appendChild(saveWrap);

    hudActions.appendChild(_makeHudBtn('clear', 'clear', () => {
      const id = _currentTabId();
      if (!id) return;
      const cancelWelcomeFn = _shellFn('cancelWelcome', importedCancelWelcome);
      if (typeof cancelWelcomeFn === 'function') cancelWelcomeFn(id);
      _shellFn('clearTab', importedClearTab)?.(id, { preserveRunState: true });
    }, 'btn btn-secondary btn-compact', 'Clear active tab (Ctrl+L)'));

    _shellBindOutsideClickClose(saveWrap, {
      triggers: saveBtn,
      isOpen: () => saveWrap.classList.contains('open'),
      onClose: () => _closeHudSaveMenu(),
    });
  }

  function _setHudKillVisible(show) {
    if (!hudKillBtn) return;
    hudKillBtn.classList.toggle('u-hidden', !show);
  }

  function _setHudCompareVisible(show) {
    if (!hudCompareBtn) return;
    hudCompareBtn.classList.toggle('u-hidden', !show);
  }

  function refreshHudActions(tabId) {
    const id = tabId || _currentTabId();
    const tab = _shellGetTab(id);
    _setHudKillVisible(!!(tab && tab.st === 'running'));
    _setHudCompareVisible(!!_comparisonRunForTab(tab));
    _refreshHudShareSnapshotState();
  }

  buildHudActions();
  document.addEventListener('app:scope-changed', () => {
    _refreshHudShareSnapshotState();
  });
  document.addEventListener('app:scope-capabilities-changed', () => {
    _refreshHudShareSnapshotState();
  });

  // ── HUD metrics ─────────────────────────────────────────────────
  // Live-updating pills on the left side of the HUD. State is owned here;
  // setters are exposed on `global` so runner.js and session.js can push in.
  const STATUS_POLL_VISIBLE_MS = 3000;
  const STATUS_POLL_HIDDEN_MS  = 15000;
  const CLOCK_TICK_MS          = 1000;
  const LAT_WARN_MS            = 250;
  const LAT_BAD_MS             = 500;

  const hudState = {
    lastExit: null,     // number | 'killed' | null
    latencyMs: null,    // number | null
    serverUptime: null, // seconds as reported by /status
    serverUptimeAt: 0,  // performance.now() when serverUptime was recorded
    db: null,           // 'ok' | 'down' | null
    redis: null,        // 'ok' | 'down' | 'none' | null
  };
  let hudStatusPollTimer = null;

  function _setValueColor(el, variant) {
    if (!el) return;
    el.classList.remove('hud-value-green', 'hud-value-amber', 'hud-value-red', 'hud-muted');
    if (variant) el.classList.add(variant);
  }

  function _formatUptime(totalSeconds) {
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
    const s = Math.floor(totalSeconds);
    if (s < 60) return `${s}s`;
    if (s < 3600) {
      const m = Math.floor(s / 60);
      const r = s % 60;
      return r ? `${m}m ${r}s` : `${m}m`;
    }
    if (s < 86400) {
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      return m ? `${h}h ${m}m` : `${h}h`;
    }
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    return h ? `${d}d ${h}h` : `${d}d`;
  }

  function _formatUtcClock(ms) {
    const d = new Date(Number.isFinite(ms) ? ms : Date.now());
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`;
  }

  function _formatOffsetLabel(minutesEastOfUtc) {
    const totalMinutes = Number.isFinite(minutesEastOfUtc) ? minutesEastOfUtc : 0;
    if (totalMinutes === 0) return 'UTC';
    const sign = totalMinutes >= 0 ? '+' : '-';
    const absMinutes = Math.abs(totalMinutes);
    const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
    const minutes = String(absMinutes % 60).padStart(2, '0');
    return `GMT${sign}${hours}:${minutes}`;
  }

  function _getLocalClockLabel(d) {
    try {
      const tzName = new Intl.DateTimeFormat([], { timeZoneName: 'short' })
        .formatToParts(d)
        .find(part => part.type === 'timeZoneName')
        ?.value
        ?.trim();
      if (tzName && !/^GMT(?:[+-]\d{1,2}(?::\d{2})?)?$/i.test(tzName) && !/^UTC(?:[+-]\d{1,2}(?::\d{2})?)?$/i.test(tzName)) {
        return tzName;
      }
    } catch (_) {
      // Fall through to the numeric offset label below.
    }
    return _formatOffsetLabel(-d.getTimezoneOffset());
  }

  function _formatLocalClock(ms) {
    const d = new Date(Number.isFinite(ms) ? ms : Date.now());
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${_getLocalClockLabel(d)}`;
  }

  function _renderLastExit() {
    if (!hudLastExitEl) return;
    const v = hudState.lastExit;
    const list = _shellTabs();
    const activeTabId = _shellGetActiveTabId();
    const activeRunning = list.some(t => t && t.id === activeTabId && t.st === 'running');
    if (v === null || v === undefined) {
      hudLastExitEl.textContent = '—';
      _setValueColor(hudLastExitEl, 'hud-muted');
    } else if (v === 'killed') {
      hudLastExitEl.textContent = 'KILLED';
      _setValueColor(hudLastExitEl, activeRunning ? 'hud-muted' : 'hud-value-red');
    } else if (v === 0) {
      hudLastExitEl.textContent = '0';
      _setValueColor(hudLastExitEl, activeRunning ? 'hud-muted' : 'hud-value-green');
    } else {
      hudLastExitEl.textContent = String(v);
      _setValueColor(hudLastExitEl, activeRunning ? 'hud-muted' : 'hud-value-red');
    }
  }

  function _renderLatency() {
    if (!hudLatencyEl) return;
    const ms = hudState.latencyMs;
    if (ms === null || ms === undefined) {
      hudLatencyEl.textContent = '— ms';
      _setValueColor(hudLatencyEl, 'hud-muted');
      return;
    }
    hudLatencyEl.textContent = `${Math.round(ms)} ms`;
    if (ms >= LAT_BAD_MS) _setValueColor(hudLatencyEl, 'hud-value-red');
    else if (ms >= LAT_WARN_MS) _setValueColor(hudLatencyEl, 'hud-value-amber');
    else _setValueColor(hudLatencyEl, 'hud-value-green');
  }

  function _renderTabs() {
    if (!hudTabsEl) return;
    const list = _shellTabs();
    const running = list.reduce((n, t) => n + (t && t.st === 'running' ? 1 : 0), 0);
    const total = list.length;
    if (!total) hudTabsEl.textContent = '0';
    else if (running > 0) hudTabsEl.textContent = `${total} · ${running} active`;
    else hudTabsEl.textContent = String(total);
    _setValueColor(hudTabsEl, running > 0 ? 'hud-value-amber' : 'hud-muted');
  }

  let browserSessionCredentialId = '';
  let browserSessionAuthType = '';
  let browserSessionCredentialSequence = 0;

  function _renderSession() {
    const identity = typeof importedGetBrowserIdentitySnapshot === 'function'
      ? importedGetBrowserIdentitySnapshot()
      : { kind: 'anonymous', credentialId: '' };
    const canLogOut = identity.kind === 'browser_session'
      || (identity.kind === 'credential' && identity.validFormat);
    document.querySelectorAll('[data-action="logout"], [data-menu-action="logout"]').forEach(button => {
      button.classList.toggle('u-hidden', !canLogOut);
    });
    if (identity.kind === 'credential') {
      const label = identity.credentialId ? `${identity.credentialId.slice(0, 12)}••••` : 'INVALID';
      if (hudSessionEl) {
        hudSessionEl.textContent = label;
        hudSessionEl.title = identity.credentialId ? 'Access credential active' : 'Saved access credential needs attention';
        _setValueColor(hudSessionEl, identity.credentialId ? 'hud-value-green' : 'hud-value-red');
      }
      if (mobileAccessStateEl) mobileAccessStateEl.textContent = identity.credentialId ? 'Kept' : 'Check';
    } else if (identity.kind === 'browser_session') {
      if (hudSessionEl) {
        hudSessionEl.textContent = browserSessionCredentialId
          ? `${browserSessionCredentialId.slice(0, 12)}••••`
          : (browserSessionAuthType === 'oidc' ? 'OIDC' : 'SESSION');
        hudSessionEl.title = browserSessionCredentialId
          ? 'Access credential active through a browser session'
          : (browserSessionAuthType === 'oidc' ? 'Identity provider session' : 'Signed-in browser session');
        _setValueColor(hudSessionEl, 'hud-value-green');
      }
      if (mobileAccessStateEl) mobileAccessStateEl.textContent = 'Kept';
    } else {
      if (hudSessionEl) {
        hudSessionEl.textContent = 'ANON';
        hudSessionEl.title = 'Anonymous workspace — open Access to keep it across devices';
        _setValueColor(hudSessionEl, 'hud-muted');
      }
      if (mobileAccessStateEl) mobileAccessStateEl.textContent = 'Anonymous';
    }
  }

  async function _refreshBrowserSessionCredentialHint() {
    const sequence = ++browserSessionCredentialSequence;
    browserSessionCredentialId = '';
    browserSessionAuthType = '';
    _renderSession();
    const identity = typeof importedGetBrowserIdentitySnapshot === 'function'
      ? importedGetBrowserIdentitySnapshot()
      : null;
    if (identity?.kind !== 'browser_session') return;
    try {
      const response = await _shellApiFetch('/auth/principal', { cache: 'no-store' });
      if (!response?.ok) return;
      const payload = await response.json();
      if (sequence !== browserSessionCredentialSequence) return;
      browserSessionAuthType = payload?.authentication?.credential_type === 'oidc' ? 'oidc' : '';
      const credentialId = payload?.authentication?.credential_id;
      if (typeof credentialId === 'string' && /^crd_[0-9a-f]{32}$/.test(credentialId)) {
        browserSessionCredentialId = credentialId;
      }
    } catch (_) {
      // The browser session remains the safe fallback when metadata is unavailable.
    } finally {
      if (sequence === browserSessionCredentialSequence) _renderSession();
    }
  }

  let projectSharedUiController = null;

  function _projectSharedUiController() {
    if (projectSharedUiController) return projectSharedUiController;
    const factory = importedProjectSharedUi && importedProjectSharedUi.createProjectSharedUiController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectSharedUi is unavailable');
    projectSharedUiController = factory({
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      downloadBlobAsAttachment: _shellDownloadBlobAsAttachment,
      downloadUrlAsAttachment: _shellDownloadUrlAsAttachment,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
    });
    return projectSharedUiController;
  }

  function _projectDisplayName(project) {
    return _projectSharedUiController().displayName(project);
  }

  let projectActiveContextController = null;

  function _projectActiveContextController() {
    if (projectActiveContextController) return projectActiveContextController;
    const factory = importedProjectActiveContext
      && importedProjectActiveContext.createProjectActiveContextController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectActiveContext is unavailable');
    projectActiveContextController = factory({
      apiFetch: _shellApiFetch,
      emitUiEvent: (eventName, detail) => {
        _shellEmitUiEvent(eventName, detail);
      },
      hudProjectCell,
      hudProjectEl,
      isProjectWorkspaceOpen,
      logClientError: (message, err, details) => {
        _shellLogClientError(message, err, details);
      },
      projectDisplayName: _projectDisplayName,
      railNav,
      refreshProjectWorkspace,
      setValueColor: _setValueColor,
      showToast: _shellFn('showToast', importedShowToast),
      syncProjectNotesForm: _syncProjectNotesForm,
    });
    return projectActiveContextController;
  }

  function _activeProject() {
    return _projectActiveContextController().project();
  }

  function _setActiveProject(project) {
    return _projectActiveContextController().setProject(project);
  }

  function _renderActiveProject() {
    _projectActiveContextController().render();
  }

  async function loadActiveProjectContext() {
    return _projectActiveContextController().load();
  }

  let projectWorkspaceShellController = null;

  function _projectWorkspaceShellController() {
    if (projectWorkspaceShellController) return projectWorkspaceShellController;
    const projectWorkspaceShell = _projectModule('DarklabProjectWorkspaceShell', importedProjectWorkspaceShell);
    const factory = projectWorkspaceShell && projectWorkspaceShell.createProjectWorkspaceShellController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectWorkspaceShell is unavailable');
    projectWorkspaceShellController = factory({
      EntityMetadataClient,
      blurVisibleComposerInputIfMobile: () => {
        _shellFn('blurVisibleComposerInputIfMobile', importedBlurVisibleComposerInputIfMobile)?.();
      },
      closeMajorOverlays: (options = {}) => {
        if (typeof importedCloseMajorOverlays === 'function') importedCloseMajorOverlays(options);
      },
      closeProjectEntityEditor: _closeProjectEntityEditor,
      closeProjectMobileActionSheet: _closeProjectMobileActionSheet,
      closeProjectMobileCompareSheet: _closeProjectMobileCompareSheet,
      closeProjectPackageManifest: _closeProjectPackageManifest,
      closeProjectPackageWizard: _closeProjectPackageWizard,
      closeProjectTargetEditor: _closeProjectTargetEditor,
      emitUiEvent: (eventName, detail) => {
        _shellEmitUiEvent(eventName, detail);
      },
      flushProjectNotesAutosave: _flushProjectNotesAutosave,
      markInteractionSurfaceReady: (surfaceName, overlay, modal) => {
        _shellFn('markInteractionSurfaceReady', importedMarkInteractionSurfaceReady)?.(surfaceName, overlay, modal);
      },
      projectEntitiesController: _projectEntitiesControllerIfReady,
      projectWorkspaceBody,
      projectWorkspaceBroadcastKey: PROJECT_WORKSPACE_CONSTANTS.workspaceBroadcastKey,
      projectMobileCreateForm,
      projectMobileNameInput,
      projectWorkspaceCreateForm,
      projectWorkspaceMessage,
      projectWorkspaceModal,
      projectWorkspaceNameInput,
      projectWorkspaceOverlay,
      projectWorkspaceTab: projectWorkspaceState.tab,
      refocusComposerAfterAction: (options) => {
        _shellRefocusComposer(options);
      },
      refreshProjectWorkspace,
      selectedProjectId: () => projectWorkspaceState.selectedId(),
      setProjectMobileCreateOpen: _setProjectMobileCreateOpen,
      setProjectPaginationOffset: _setProjectPaginationOffset,
      setProjectWorkspaceTab: projectWorkspaceState.setTab,
      setSelectedProjectId: projectWorkspaceState.setSelectedId,
      showToast: _shellFn('showToast', importedShowToast),
    });
    return projectWorkspaceShellController;
  }

  let projectWorkspaceActionsController = null;
  let projectWorkspaceActionsControllerPromise = null;

  function _projectWorkspaceActionsController() {
    if (projectWorkspaceActionsController) return projectWorkspaceActionsController;
    const projectWorkspaceActions = _projectModule('DarklabProjectWorkspaceActions', importedProjectWorkspaceActions);
    const factory = projectWorkspaceActions && projectWorkspaceActions.createProjectWorkspaceActionsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectWorkspaceActions is unavailable');
    projectWorkspaceActionsController = factory({
      EntityMetadataClient,
      apiFetch: _shellApiFetch,
      projectRunItems: _projectRunItems,
      projectWorkspaceRequest: _projectWorkspaceRequest,
      refreshProjectWorkspace,
      selectedProjectId: () => projectWorkspaceState.selectedId(),
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      showConfirm: _shellFn('showConfirm', importedShowConfirm),
    });
    return projectWorkspaceActionsController;
  }

  function _projectWorkspaceActionsControllerIfReady() {
    return projectWorkspaceActionsController || (
      _projectFactoryReady(
        'DarklabProjectWorkspaceActions',
        importedProjectWorkspaceActions,
        'createProjectWorkspaceActionsController',
      )
        ? _projectWorkspaceActionsController()
        : null
    );
  }

  function _loadProjectWorkspaceActionsController() {
    const readyController = _projectWorkspaceActionsControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectWorkspaceActionsControllerPromise) return projectWorkspaceActionsControllerPromise;
    projectWorkspaceActionsControllerPromise = _loadProjectWorkspaceDeferredModules(['project_workspace_actions'])
      .then(() => _projectWorkspaceActionsController())
      .finally(() => {
        projectWorkspaceActionsControllerPromise = null;
      });
    return projectWorkspaceActionsControllerPromise;
  }

  function isProjectWorkspaceOpen() {
    if (!_projectWorkspaceModulesReady()) return _projectWorkspaceOverlayOpenFallback();
    return _projectWorkspaceShellController().isOpen();
  }

  function _setProjectWorkspaceMessage(text = '', { error = false, toast = true } = {}) {
    if (!_projectWorkspaceModulesReady()) {
      if (toast && text) _shellShowToast(text, error ? 'error' : 'info');
      return;
    }
    _projectWorkspaceShellController().setMessage(text, { error, toast });
  }

  async function _projectResponseError(resp, fallback) {
    return _projectWorkspaceShellController().responseError(resp, fallback);
  }

  function _selectedProject() {
    return _projectWorkspaceLifecycleController().selectedProject();
  }

  function _projectSummary(projectId = projectWorkspaceState.selectedId()) {
    return _projectWorkspaceLifecycleController().projectSummary(projectId);
  }

  function _ensureSelectedProject() {
    _projectWorkspaceLifecycleController().ensureSelectedProject();
  }

  function _projectCounts(summary) {
    return _projectSharedUiController().counts(summary);
  }

  function _projectCountEntries(summary) {
    return _projectSharedUiController().countEntries(summary);
  }

  function _projectTargetItems(summary) {
    return _projectSharedUiController().targetItems(summary);
  }

  let projectEntitiesController = null;
  let projectEntitiesControllerPromise = null;

  function _projectEntitiesController() {
    if (projectEntitiesController) return projectEntitiesController;
    const projectEntities = _projectModule('DarklabProjectEntities', importedProjectEntities);
    const factory = projectEntities && projectEntities.createProjectEntitiesController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectEntities is unavailable');
    projectEntitiesController = factory({
      apiFetch: _shellApiFetch,
      getSummary: projectId => projectWorkspaceState.summary(projectId),
      getActiveTab: projectWorkspaceState.entityTab,
      setActiveTab: projectWorkspaceState.setEntityTab,
      getSelectMode: projectWorkspaceState.entitySelectMode,
      setSelectMode: projectWorkspaceState.setEntitySelectMode,
      getSelectedIds: projectWorkspaceState.selectedEntityIds,
      getPicker: projectWorkspaceState.entityPicker,
      setPicker: projectWorkspaceState.setEntityPicker,
      getSelectedProjectId: projectWorkspaceState.selectedId,
      projectRows: projectWorkspaceState.rows,
      projectIsArchived: _projectIsArchived,
      formatDate: _formatProjectDate,
      shortProjectRunId: _shortProjectRunId,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      projectMobileEmptyPanel: _projectMobileEmptyPanel,
      projectItemRow: _projectItemRow,
      projectMobileContentRow: _projectMobileContentRow,
      projectMobileActionMenu: _projectMobileActionMenu,
      entityMetadataChips: _entityMetadataChips,
      projectTargetItems: _projectTargetItems,
      projectTargetFilterSet: _projectTargetFilterSet,
      projectRunFilterSet: _projectRunFilterSet,
      projectHostFilterSet: _projectHostFilterSet,
      projectResponseError: _projectResponseError,
      projectWorkspaceRequest: _projectWorkspaceRequest,
      refreshProjectWorkspace,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      mobileView: _projectMobileView,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      showConfirm: _shellFn('showConfirm', importedShowConfirm),
      logClientError: (message, err) => {
        _shellLogClientError(message, err);
      },
      downloadBlobAsAttachment: _downloadBlobAsAttachment,
      downloadUrlAsAttachment: _downloadUrlAsAttachment,
      closeProjectWorkspace,
      openAtlas: _shellOpenAtlas,
      projectDisplayName: _projectDisplayName,
      setWorkspaceTab: projectWorkspaceState.setTab,
    });
    return projectEntitiesController;
  }

  function _projectEntitiesControllerIfReady() {
    return projectEntitiesController || (
      _projectFactoryReady('DarklabProjectEntities', importedProjectEntities, 'createProjectEntitiesController')
        ? _projectEntitiesController()
        : null
    );
  }

  function _loadProjectEntitiesController() {
    const readyController = _projectEntitiesControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectEntitiesControllerPromise) return projectEntitiesControllerPromise;
    projectEntitiesControllerPromise = _loadProjectWorkspaceDeferredModules(['project_entities'])
      .then(() => _projectEntitiesController())
      .finally(() => {
        projectEntitiesControllerPromise = null;
      });
    return projectEntitiesControllerPromise;
  }

  let projectPackagesController = null;
  let projectPackagesControllerPromise = null;

  function _projectPackagesController() {
    if (projectPackagesController) return projectPackagesController;
    const projectPackages = _projectModule('DarklabProjectPackages', importedProjectPackages);
    const factory = projectPackages && projectPackages.createProjectPackagesController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectPackages is unavailable');
    projectPackagesController = factory({
      apiFetch: _shellApiFetch,
      EntityMetadataClient,
      manifestOverlay: projectPackageManifestOverlay,
      manifestTitle: projectPackageManifestTitle,
      manifestSummary: projectPackageManifestSummary,
      manifestJson: projectPackageManifestJson,
      wizardOverlay: projectPackageWizardOverlay,
      wizardBody: projectPackageWizardBody,
      getSelectedProjectId: projectWorkspaceState.selectedId,
      selectedProject: _selectedProject,
      projectSummary: _projectSummary,
      projectRunItems: _projectRunItems,
      projectArtifactItems: _projectArtifactItems,
      loadAllProjectArtifacts: _loadAllProjectArtifacts,
      projectTargetItems: _projectTargetItems,
      projectFindingItems: _projectFindingItems,
      projectFindingsLoaded: _projectFindingsLoaded,
      loadProjectFindings: _loadProjectFindings,
      projectFilesEnabled: _projectFilesEnabled,
      projectArtifactStatus: _projectArtifactStatus,
      projectArtifactDetail: _projectArtifactDetail,
      projectTargetFilterLabel: _projectTargetFilterLabel,
      entityLabelValues: _entityLabelValues,
      entityNoteBody: _entityNoteBody,
      entityMetadataChips: _entityMetadataChips,
      projectProvenanceSummary: _projectProvenanceSummary,
      projectProvenanceSummaryElement: _projectProvenanceSummaryElement,
      formatDate: _formatProjectDate,
      formatBytes: _formatProjectBytes,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      projectMobileEmptyPanel: _projectMobileEmptyPanel,
      projectMobileContentRow: _projectMobileContentRow,
      projectMobileActionMenu: _projectMobileActionMenu,
      projectItemRow: _projectItemRow,
      projectWorkspaceRequest: _projectWorkspaceRequest,
      refreshProjectWorkspace,
      renderProjectExplorer: _renderProjectExplorer,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      downloadBlobAsAttachment: _downloadBlobAsAttachment,
      downloadUrlAsAttachment: _downloadUrlAsAttachment,
      setWorkspaceTab: projectWorkspaceState.setTab,
      syncProjectWorkspaceNestedSuppression: _syncProjectWorkspaceNestedSuppression,
      focusProjectNestedSheet: _focusProjectNestedSheet,
      installProjectMobileKeyboardGuards: _installProjectMobileKeyboardGuards,
    });
    return projectPackagesController;
  }

  function _projectPackagesControllerIfReady() {
    return projectPackagesController || null;
  }

  function _loadProjectPackagesController() {
    if (projectPackagesController) return Promise.resolve(projectPackagesController);
    if (projectPackagesControllerPromise) return projectPackagesControllerPromise;
    const loader = global.loadProjectPackages;
    projectPackagesControllerPromise = (typeof loader === 'function' ? loader() : Promise.resolve())
      .then(() => _projectPackagesController())
      .finally(() => {
        projectPackagesControllerPromise = null;
      });
    return projectPackagesControllerPromise;
  }

  let projectReportController = null;
  let projectReportControllerPromise = null;

  let projectActivityController = null;
  let projectActivityControllerPromise = null;
  let projectAssessmentController = null;
  let projectAssessmentControllerPromise = null;
  let projectOverviewController = null;
  let projectOverviewControllerPromise = null;
  let projectMonitoringController = null;
  let projectMonitoringControllerPromise = null;

  function _projectActivityController() {
    if (projectActivityController) return projectActivityController;
    const projectActivity = _projectModule('DarklabProjectActivity', importedProjectActivity);
    const factory = projectActivity && projectActivity.createProjectActivityController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectActivity is unavailable');
    projectActivityController = factory({
      projectWorkspaceRequest: _projectWorkspaceRequest,
      projectResponseError: _projectResponseError,
      formatDate: _formatProjectDate,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      renderProjectExplorer: _renderProjectExplorer,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      openProjectObject: _openProjectObject,
    });
    return projectActivityController;
  }

  function _projectActivityControllerIfReady() {
    return projectActivityController || null;
  }

  function _loadProjectActivityController() {
    if (projectActivityController) return Promise.resolve(projectActivityController);
    if (projectActivityControllerPromise) return projectActivityControllerPromise;
    const loader = global.loadProjectActivity;
    projectActivityControllerPromise = (typeof loader === 'function' ? loader() : Promise.resolve())
      .then((namespace) => {
        if (namespace) importedProjectActivity = namespace;
        return _projectActivityController();
      })
      .finally(() => {
        projectActivityControllerPromise = null;
      });
    return projectActivityControllerPromise;
  }

  function _projectAssessmentController() {
    if (projectAssessmentController) return projectAssessmentController;
    const projectAssessment = _projectModule('DarklabProjectAssessment', importedProjectAssessment);
    const factory = projectAssessment && projectAssessment.createProjectAssessmentController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectAssessment is unavailable');
    projectAssessmentController = factory({
      projectWorkspaceRequest: _projectWorkspaceRequest,
      apiFetch: _shellApiFetch,
      projectResponseError: _projectResponseError,
      formatDate: _formatProjectDate,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      enhanceAppSelects: _shellEnhanceAppSelects(),
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      invalidateProjectOverview: (projectId = '') => _projectOverviewControllerIfReady()?.invalidate?.(projectId),
      invalidateProjectFindings: _invalidateProjectFindings,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      showConfirm: _shellFn('showConfirm', importedShowConfirm),
      closeProjectWorkspace: (options = {}) => closeProjectWorkspace(options),
      openHistoryRunDetails: _shellFn('openHistoryRunDetails', importedOpenHistoryRunDetails),
      openWorkspace: _shellFn('openWorkspace', importedOpenWorkspace),
      openAtlas: _shellOpenAtlas,
      openSecretsOptions: () => {
        importedOpenOptions?.();
        importedActivateOptionsTab?.('secrets', { persist: true, focus: true });
      },
      actionSheetContainer: () => projectWorkspaceModal,
      logClientError: _shellLogClientError,
      mobileView: _projectMobileView,
      assessmentBatchLimits: () => (
        _shellValue('APP_CONFIG')?.assessment_batch_limits || {}
      ),
      canMutateProjects: () => _shellActiveTeamScopeCan('mutate_projects'),
      canRunCommands: () => _shellActiveTeamScopeCan('run_commands'),
      canManageSecrets: () => _shellActiveTeamScopeCan('manage_secrets'),
      canTriageFindings: () => _shellActiveTeamScopeCan('triage_findings'),
    });
    return projectAssessmentController;
  }

  function _projectAssessmentControllerIfReady() {
    return projectAssessmentController || null;
  }

  function _loadProjectAssessmentController() {
    if (projectAssessmentController) return Promise.resolve(projectAssessmentController);
    if (projectAssessmentControllerPromise) return projectAssessmentControllerPromise;
    const loader = global.loadProjectAssessment;
    projectAssessmentControllerPromise = (typeof loader === 'function' ? loader() : Promise.resolve())
      .then((namespace) => {
        if (namespace) importedProjectAssessment = namespace;
        return _projectAssessmentController();
      })
      .finally(() => {
        projectAssessmentControllerPromise = null;
      });
    return projectAssessmentControllerPromise;
  }

  function _projectOverviewController() {
    if (projectOverviewController) return projectOverviewController;
    const projectOverview = _projectModule('DarklabProjectOverview', importedProjectOverview);
    const factory = projectOverview && projectOverview.createProjectOverviewController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectOverview is unavailable');
    projectOverviewController = factory({
      projectWorkspaceRequest: _projectWorkspaceRequest,
      projectResponseError: _projectResponseError,
      formatDate: _formatProjectDate,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      setProjectWorkspaceTab: projectWorkspaceState.setTab,
      openProjectAssessment: _openProjectAssessment,
      setProjectEntityTab: projectWorkspaceState.setEntityTab,
      projectTargetFilterSet: _projectTargetFilterSet,
      projectRunFilterSet: _projectRunFilterSet,
      projectHostFilterSet: _projectHostFilterSet,
      projectFindingSeverityFilterSet: _projectFindingSeverityFilterSet,
      projectFindingStatusFilterSet: _projectFindingStatusFilterSet,
      setProjectFindingOrphanFilter: (projectId, value) => _projectFiltersController().setFindingOrphanFilter(projectId, value),
      invalidateProjectFilteredFindings: _invalidateProjectFilteredFindings,
      openProjectEntityInAtlas: _openProjectEntityInAtlas,
      logClientError: _shellLogClientError,
      mobileView: _projectMobileView,
    });
    return projectOverviewController;
  }

  function _projectOverviewControllerIfReady() {
    return projectOverviewController || null;
  }

  function _loadProjectOverviewController() {
    if (projectOverviewController) return Promise.resolve(projectOverviewController);
    if (projectOverviewControllerPromise) return projectOverviewControllerPromise;
    const loader = global.loadProjectOverview;
    projectOverviewControllerPromise = (typeof loader === 'function' ? loader() : Promise.resolve())
      .then((namespace) => {
        if (namespace) importedProjectOverview = namespace;
        return _projectOverviewController();
      })
      .finally(() => {
        projectOverviewControllerPromise = null;
      });
    return projectOverviewControllerPromise;
  }

  function _projectMonitoringController() {
    if (projectMonitoringController) return projectMonitoringController;
    const projectMonitoring = _projectModule('DarklabProjectMonitoring', importedProjectMonitoring);
    const factory = projectMonitoring && projectMonitoring.createProjectMonitoringController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectMonitoring is unavailable');
    projectMonitoringController = factory({
      projectWorkspaceRequest: _projectWorkspaceRequest,
      projectResponseError: _projectResponseError,
      formatDate: _formatProjectDate,
      makeProjectButton: _makeProjectButton,
      emptyProjectPanel: _emptyProjectPanel,
      showConfirm: _shellFn('showConfirm', importedShowConfirm),
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      logClientError: _shellLogClientError,
      mobileView: _projectMobileView,
    });
    return projectMonitoringController;
  }

  function _projectMonitoringControllerIfReady() {
    return projectMonitoringController || null;
  }

  function _loadProjectMonitoringController() {
    if (projectMonitoringController) return Promise.resolve(projectMonitoringController);
    if (projectMonitoringControllerPromise) return projectMonitoringControllerPromise;
    const loader = global.loadProjectMonitoring;
    projectMonitoringControllerPromise = (typeof loader === 'function' ? loader() : Promise.resolve())
      .then((namespace) => {
        if (namespace) importedProjectMonitoring = namespace;
        return _projectMonitoringController();
      })
      .finally(() => {
        projectMonitoringControllerPromise = null;
      });
    return projectMonitoringControllerPromise;
  }

  function _projectReportController() {
    if (projectReportController) return projectReportController;
    const projectReport = _projectModule('DarklabProjectReport', importedProjectReport);
    const factory = projectReport && projectReport.createProjectReportController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectReport is unavailable');
    projectReportController = factory({
      apiFetch: _shellApiFetch,
      getSelectedProjectId: projectWorkspaceState.selectedId,
      selectedProject: _selectedProject,
      projectSummary: _projectSummary,
      projectRunItems: _projectRunItems,
      projectArtifactItems: _projectArtifactItems,
      loadAllProjectArtifacts: _loadAllProjectArtifacts,
      projectTargetItems: _projectTargetItems,
      projectFindingItems: _projectFindingItems,
      loadProjectFindings: _loadProjectFindings,
      projectArtifactDetail: _projectArtifactDetail,
      formatDate: _formatProjectDate,
      projectProvenanceSummaryElement: _projectProvenanceSummaryElement,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      mobileView: _projectMobileView,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      downloadUrlAsAttachment: _downloadUrlAsAttachment,
      showConfirm: _shellFn('showConfirm', importedShowConfirm),
      logClientError: (message, err) => {
        _shellLogClientError(message, err);
      },
    });
    return projectReportController;
  }

  function _projectReportControllerIfReady() {
    return projectReportController || null;
  }

  function _loadProjectReportController() {
    if (projectReportController) return Promise.resolve(projectReportController);
    if (projectReportControllerPromise) return projectReportControllerPromise;
    const loader = global.loadProjectReport;
    projectReportControllerPromise = (typeof loader === 'function' ? loader() : Promise.resolve())
      .then((namespace) => {
        if (namespace) importedProjectReport = namespace;
        return _projectReportController();
      })
      .finally(() => {
        projectReportControllerPromise = null;
      });
    return projectReportControllerPromise;
  }

  let projectFiltersController = null;

  function _projectFiltersController() {
    if (projectFiltersController) return projectFiltersController;
    const projectFilters = _projectModule('DarklabProjectFilters', importedProjectFilters);
    const factory = projectFilters && projectFilters.createProjectFiltersController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectFilters is unavailable');
    projectFiltersController = factory({
      getSelectedProjectId: projectWorkspaceState.selectedId,
      projectWorkspaceModal: () => projectWorkspaceModal,
      projectExplorerBody: () => projectExplorerBody,
      projectWorkspaceTab: projectWorkspaceState.tab,
      projectSummary: _projectSummary,
      findingReviewStates: PROJECT_WORKSPACE_CONSTANTS.findingReviewStates,
      findingSeverityRank: PROJECT_WORKSPACE_CONSTANTS.findingSeverityRank,
      findingReviewRank: PROJECT_WORKSPACE_CONSTANTS.findingReviewRank,
      projectFindingSortOptions: PROJECT_WORKSPACE_CONSTANTS.findingSortOptions,
      projectFindingNoteStateOptions: PROJECT_WORKSPACE_CONSTANTS.findingNoteStateOptions,
      projectFindingOrphanOptions: PROJECT_WORKSPACE_CONSTANTS.findingOrphanOptions,
      projectFindingScopeOptions: PROJECT_WORKSPACE_CONSTANTS.findingScopeOptions,
      projectFindingSeverityOptions: PROJECT_WORKSPACE_CONSTANTS.findingSeverityOptions,
      projectTargetItems: _projectTargetItems,
      projectTargetLabel: _projectTargetLabel,
      projectRunItems: _projectRunItems,
      projectRunById: _projectRunById,
      shortProjectRunId: _shortProjectRunId,
      projectFindingItems: _projectFindingItems,
      projectFilteredFindingItems: key => _projectFindingsDataControllerIfReady()?.filteredItems(key) || [],
      hasProjectFilteredFindingsKey: key => !!_projectFindingsDataControllerIfReady()?.hasFilteredKey(key),
      projectArtifactItems: _projectArtifactItems,
      entityLabelValues: _entityLabelValues,
      entityNoteBody: _entityNoteBody,
      findingReviewStateLabel: _findingReviewStateLabel,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
    });
    return projectFiltersController;
  }

  let projectFindingsDataController = null;
  let projectFindingsDataControllerPromise = null;

  function _projectFindingsDataController() {
    if (projectFindingsDataController) return projectFindingsDataController;
    const projectFindingsData = _projectModule('DarklabProjectFindingsData', importedProjectFindingsData);
    const factory = projectFindingsData && projectFindingsData.createProjectFindingsDataController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectFindingsData is unavailable');
    projectFindingsDataController = factory({
      apiFetch: _shellApiFetch,
      selectedProjectId: projectWorkspaceState.selectedId,
      mobileView: _projectMobileView,
      projectSummary: _projectSummary,
      findingFilteredKey: _projectFindingFilteredKey,
      findingServerFilterParams: _projectFindingServerFilterParams,
      collapsedFindingGroupLabels: _projectCollapsedFindingGroupLabels,
      filteredProjectFindings: _filteredProjectFindings,
      pageLimit: 50,
      projectResponseError: _projectResponseError,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      renderProjectPackageWizardModal: _renderProjectPackageWizardModal,
      projectPackageWizardActive: _projectPackageWizardActive,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      logClientError: (message, err) => {
        _shellLogClientError(message, err);
      },
    });
    return projectFindingsDataController;
  }

  function _projectFindingsDataControllerIfReady() {
    return projectFindingsDataController || (
      _projectFactoryReady(
        'DarklabProjectFindingsData',
        importedProjectFindingsData,
        'createProjectFindingsDataController',
      )
        ? _projectFindingsDataController()
        : null
    );
  }

  function _loadProjectFindingsDataController() {
    const readyController = _projectFindingsDataControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectFindingsDataControllerPromise) return projectFindingsDataControllerPromise;
    projectFindingsDataControllerPromise = _loadProjectWorkspaceDeferredModules(['project_findings_data'])
      .then(() => _projectFindingsDataController())
      .finally(() => {
        projectFindingsDataControllerPromise = null;
      });
    return projectFindingsDataControllerPromise;
  }

  let projectFindingsController = null;
  let projectFindingsControllerPromise = null;

  let projectFindingsBoardController = null;

  function _projectFindingsBoardController() {
    if (projectFindingsBoardController) return projectFindingsBoardController;
    const projectFindingsBoard = _projectModule('DarklabProjectFindingsBoard', importedProjectFindingsBoard);
    const factory = projectFindingsBoard && projectFindingsBoard.createProjectFindingsBoardController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectFindingsBoard is unavailable');
    projectFindingsBoardController = factory({
      entityMetadataChipClass: _entityMetadataChipClass,
      entityMetadataChips: _entityMetadataChips,
      projectFindingTargetText: _projectFindingTargetText,
      projectTargetLabel: _projectTargetLabel,
      makeProjectButton: _makeProjectButton,
      reviewControl: (finding, projectId) => _projectFindingsControllerIfReady()?.reviewControl(finding, projectId) || null,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      metaSeparator: ' · ',
    });
    return projectFindingsBoardController;
  }

  function _projectFindingsController() {
    if (projectFindingsController) return projectFindingsController;
    const projectFindings = _projectModule('DarklabProjectFindings', importedProjectFindings);
    const factory = projectFindings && projectFindings.createProjectFindingsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectFindings is unavailable');
    projectFindingsController = factory({
      findingReviewStates: PROJECT_WORKSPACE_CONSTANTS.findingReviewStates,
      collapsedFindingGroups: projectWorkspaceState.collapsedFindingGroups,
      collapsedFindingGroupLabels: _projectCollapsedFindingGroupLabels,
      findingsLoadingId: () => _projectFindingsDataControllerIfReady()?.loadingId() || '',
      hasFindings: projectId => !!_projectFindingsDataControllerIfReady()?.loaded(projectId),
      findingViewMode: projectWorkspaceState.findingViewMode,
      findingSelectMode: projectWorkspaceState.findingSelectMode,
      selectedFindingIds: projectWorkspaceState.selectedFindingIds,
      projectFindingPagination: (projectId, summary) => _projectFindingPagination(projectId, summary),
      projectFindingItems: _projectFindingItems,
      projectFindingChanges: projectId => (
        _projectFindingsDataControllerIfReady()?.findingChanges(projectId) || null
      ),
      projectFindingBoard: (projectId, summary, options) => (
        _projectFindingsDataControllerIfReady()?.board(projectId, summary, options) || []
      ),
      filteredProjectFindings: _filteredProjectFindings,
      projectFindingServerFiltersActive: _projectFindingServerFiltersActive,
      findingsBoardAvailable: () => !(document.body && document.body.classList.contains('mobile-terminal-mode')),
      projectFindingTargetText: _projectFindingTargetText,
      projectTargetLabel: _projectTargetLabel,
      entityMetadataChips: _entityMetadataChips,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      openProjectAssessment: _openProjectAssessment,
      emptyProjectPanel: _emptyProjectPanel,
      renderProjectFindingBoard: (container, projectId, summary, board) => (
        _projectFindingsBoardController().renderBoard(container, projectId, summary, board)
      ),
      setFindingSelectMode: projectWorkspaceState.setFindingSelectMode,
      projectItemRow: _projectItemRow,
      groupBy: _groupBy,
      metaSeparator: ' · ',
      groupCaret: '▾',
    });
    return projectFindingsController;
  }

  function _projectFindingsControllerIfReady() {
    return projectFindingsController || (
      _projectFactoryReady('DarklabProjectFindings', importedProjectFindings, 'createProjectFindingsController')
        ? _projectFindingsController()
        : null
    );
  }

  function _loadProjectFindingsController() {
    const readyController = _projectFindingsControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectFindingsControllerPromise) return projectFindingsControllerPromise;
    projectFindingsControllerPromise = _loadProjectWorkspaceDeferredModules([
      'project_findings_data',
      'project_findings',
      'project_findings_board',
    ])
      .then(() => _projectFindingsController())
      .finally(() => {
        projectFindingsControllerPromise = null;
      });
    return projectFindingsControllerPromise;
  }

  let projectArtifactsController = null;
  let projectArtifactsControllerPromise = null;

  function _projectArtifactsControllerIfReady() {
    return projectArtifactsController;
  }

  function _projectArtifactsFactoryReady() {
    return !!(
      _projectModule('DarklabProjectArtifacts', importedProjectArtifacts)
      && typeof _projectModule('DarklabProjectArtifacts', importedProjectArtifacts).createProjectArtifactsController === 'function'
    );
  }

  function _projectArtifactsController() {
    if (projectArtifactsController) return projectArtifactsController;
    const projectArtifacts = _projectModule('DarklabProjectArtifacts', importedProjectArtifacts);
    const factory = projectArtifacts && projectArtifacts.createProjectArtifactsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectArtifacts is unavailable');
    projectArtifactsController = factory({
      apiFetch: _shellApiFetch,
      projectResponseError: _projectResponseError,
      collapsedArtifactGroups: projectWorkspaceState.collapsedArtifactGroups,
      filesEnabled: () => !!(_shellValue('APP_CONFIG') && _shellValue('APP_CONFIG').workspace_enabled === true),
      selectedProjectId: projectWorkspaceState.selectedId,
      projectTargetFilterActive: _projectTargetFilterActive,
      projectFindingsLoaded: _projectFindingsLoaded,
      filteredProjectArtifacts: _filteredProjectArtifacts,
      projectRunFilterSet: _projectRunFilterSet,
      projectTargetFilterSet: _projectTargetFilterSet,
      projectTargetItems: _projectTargetItems,
      projectRunById: _projectRunById,
      shortProjectRunId: _shortProjectRunId,
      entityMetadataChips: _entityMetadataChips,
      formatDate: _formatProjectDate,
      formatBytes: _formatProjectBytes,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      projectItemRow: _projectItemRow,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      mobileView: _projectMobileView,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      logClientError: (message, err) => {
        _shellLogClientError(message, err);
      },
      groupBy: _groupBy,
      downloadBlobAsAttachment: _downloadBlobAsAttachment,
      downloadUrlAsAttachment: _downloadUrlAsAttachment,
      metaSeparator: ' · ',
      groupCaret: '▾',
    });
    return projectArtifactsController;
  }

  function _loadProjectArtifactsController() {
    if (projectArtifactsController) return Promise.resolve(projectArtifactsController);
    if (projectArtifactsControllerPromise) return projectArtifactsControllerPromise;
    if (_projectArtifactsFactoryReady()) {
      return Promise.resolve(_projectArtifactsController());
    }
    const loader = global.loadProjectArtifacts;
    if (typeof loader !== 'function') {
      return Promise.reject(new Error('Project artifacts loader is unavailable'));
    }
    projectArtifactsControllerPromise = loader()
      .then((namespace) => {
        if (namespace) importedProjectArtifacts = namespace;
        return _projectArtifactsController();
      })
      .finally(() => {
        projectArtifactsControllerPromise = null;
      });
    return projectArtifactsControllerPromise;
  }

  let projectWebSurfaceController = null;
  let projectWebSurfaceControllerPromise = null;

  function _projectWebSurfaceControllerIfReady() {
    return projectWebSurfaceController;
  }

  function _projectWebSurfaceFactoryReady() {
    const namespace = _projectModule('DarklabProjectWebSurface', importedProjectWebSurface);
    return !!(namespace && typeof namespace.createProjectWebSurfaceController === 'function');
  }

  function _projectWebSurfaceController() {
    if (projectWebSurfaceController) return projectWebSurfaceController;
    const namespace = _projectModule('DarklabProjectWebSurface', importedProjectWebSurface);
    const factory = namespace && namespace.createProjectWebSurfaceController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectWebSurface is unavailable');
    projectWebSurfaceController = factory({
      apiFetch: _shellApiFetch,
      projectResponseError: _projectResponseError,
      emptyProjectPanel: _emptyProjectPanel,
      formatDate: _formatProjectDate,
      shortProjectRunId: _shortProjectRunId,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      bindDismissible: importedBindDismissible,
      bindFocusTrap: importedBindFocusTrap,
      focusElement: importedFocusElement,
      showModalOverlay: importedShowModalOverlay,
      hideModalOverlay: importedHideModalOverlay,
      markInteractionSurfaceReady: importedMarkInteractionSurfaceReady,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      mobileView: _projectMobileView,
      closeProjectWorkspace,
      canMutateProjects: () => _shellActiveTeamScopeCan('mutate_projects'),
      openPackageWithArtifact: (projectId, artifactId) => {
        _loadProjectPackagesController()
          .then(controller => controller.openWizardForArtifacts(projectId, [artifactId]))
          .catch((err) => {
            _shellLogClientError('failed to hand Web Surface screenshot to package builder', err);
            _setProjectWorkspaceMessage('Could not open the screenshot in the package builder.', { error: true });
          });
      },
      openReportWithArtifact: (projectId, artifact) => {
        _loadProjectReportController()
          .then(controller => controller.includeArtifact(projectId, artifact))
          .then(() => {
            projectWorkspaceState.setTab('report');
            _renderProjectExplorer();
            if (_projectMobileView() === 'detail') _renderProjectMobileDetail();
          })
          .catch((err) => {
            _shellLogClientError('failed to hand Web Surface screenshot to report builder', err);
            _setProjectWorkspaceMessage('Could not add the screenshot to the report.', { error: true });
          });
      },
      openEntityInAtlas: (projectId, entity) => (
        _openProjectEntityInAtlas(projectId, _projectSummary(projectId), entity)
      ),
      openHistoryRunDetails: _shellFn('openHistoryRunDetails', importedOpenHistoryRunDetails),
      logClientError: (message, err, details) => _shellLogClientError(message, err, details),
      metaSeparator: ' · ',
    });
    return projectWebSurfaceController;
  }

  function _loadProjectWebSurfaceController() {
    if (projectWebSurfaceController) return Promise.resolve(projectWebSurfaceController);
    if (projectWebSurfaceControllerPromise) return projectWebSurfaceControllerPromise;
    if (_projectWebSurfaceFactoryReady()) return Promise.resolve(_projectWebSurfaceController());
    projectWebSurfaceControllerPromise = _loadProjectWorkspaceDeferredModules(['project_web_surface'])
      .then((modules) => {
        if (modules?.DarklabProjectWebSurface) importedProjectWebSurface = modules.DarklabProjectWebSurface;
        return _projectWebSurfaceController();
      })
      .finally(() => {
        projectWebSurfaceControllerPromise = null;
      });
    return projectWebSurfaceControllerPromise;
  }

  let projectDetailsController = null;

  function _projectDetailsController() {
    if (projectDetailsController) return projectDetailsController;
    const projectDetails = _projectModule('DarklabProjectDetails', importedProjectDetails);
    const factory = projectDetails && projectDetails.createProjectDetailsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectDetails is unavailable');
    projectDetailsController = factory({
      apiFetch: _shellApiFetch,
      entityMetadataClient: EntityMetadataClient,
      projectNotesForm,
      projectNotesInput,
      projectLabelsForm,
      projectLabelsInput,
      projectLabelsSaveButton,
      projectWorkspaceTab: projectWorkspaceState.tab,
      selectedProject: _selectedProject,
      selectedProjectId: projectWorkspaceState.selectedId,
      projectRows: projectWorkspaceState.rows,
      setProjectRows: projectWorkspaceState.setRows,
      projectSummary: _projectSummary,
      setProjectSummary: projectWorkspaceState.setSummary,
      activeProject: _activeProject,
      setActiveProject: _setActiveProject,
      projectDisplayName: _projectDisplayName,
      projectTargetItems: _projectTargetItems,
      entityLabelValues: _entityLabelValues,
      entityNoteBody: _entityNoteBody,
      entityMetadataChipClass: _entityMetadataChipClass,
      projectMetaRow: _projectMetaRow,
      formatDate: _formatProjectDate,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      renderProjectTargets: _renderProjectTargets,
      focusElement: importedFocusElement,
      projectResponseError: _projectResponseError,
      syncEntityLabels: _syncEntityLabels,
      projectWorkspaceRequest: _projectWorkspaceRequest,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      renderProjectList: _renderProjectList,
      renderProjectExplorer: _renderProjectExplorer,
      renderActiveProject: _renderActiveProject,
      projectNotesAutosaveDelayMs: PROJECT_WORKSPACE_CONSTANTS.projectNotesAutosaveDelayMs,
    });
    return projectDetailsController;
  }

  let projectListController = null;

  function _projectListController() {
    if (projectListController) return projectListController;
    const projectList = _projectModule('DarklabProjectList', importedProjectList);
    const factory = projectList && projectList.createProjectListController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectList is unavailable');
    projectListController = factory({
      projectWorkspaceBody,
      projectWorkspaceLoading: projectWorkspaceState.loading,
      projectRows: projectWorkspaceState.rows,
      selectedProjectId: projectWorkspaceState.selectedId,
      activeProject: _activeProject,
      projectSummary: _projectSummary,
      projectCountEntries: _projectCountEntries,
      projectArtifactsVisible: _projectArtifactsVisible,
      projectDisplayName: _projectDisplayName,
      appendProjectLabelChips: _appendProjectLabelChips,
      appendProjectMobileLabelChips: _appendProjectMobileLabelChips,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      mobileMenuText: '☰',
      mobileChevronText: '›',
      projectPagination: projectWorkspaceState.pagination,
      projectWorkspacePagination,
    });
    return projectListController;
  }

  let projectNavigationController = null;

  function _projectNavigationController() {
    if (projectNavigationController) return projectNavigationController;
    const projectNavigation = _projectModule('DarklabProjectNavigation', importedProjectNavigation);
    const factory = projectNavigation && projectNavigation.createProjectNavigationController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectNavigation is unavailable');
    projectNavigationController = factory({
      projectWorkspaceModal,
      projectMobileDetailTopbar,
      projectMobileTabs,
      activeProject: _activeProject,
      projectWorkspaceTab: projectWorkspaceState.tab,
      setProjectWorkspaceTab: projectWorkspaceState.setTab,
      projectCounts: _projectCounts,
      projectDisplayName: _projectDisplayName,
      projectIsArchived: _projectIsArchived,
      projectArtifactsVisible: _projectArtifactsVisible,
      projectTargetFilterActive: _projectTargetFilterActive,
      projectRunFilterActive: _projectRunFilterActive,
      projectFindingsLoaded: _projectFindingsLoaded,
      projectEntityTabCountText: (projectId, summary, total) => (
        _projectEntitiesControllerIfReady()?.tabCountText(projectId, summary, total) || String(total)
      ),
      projectFindingPagination: _projectFindingPagination,
      projectFindingServerFiltersActive: _projectFindingServerFiltersActive,
      filteredProjectFindings: _filteredProjectFindings,
      filteredProjectRuns: _filteredProjectRuns,
      filteredProjectArtifacts: _filteredProjectArtifacts,
      appendProjectLabelChips: _appendProjectLabelChips,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      metaSeparator: ' · ',
      mobileBackText: '‹ Back',
    });
    return projectNavigationController;
  }

  let projectNestedSheetsController = null;
  let projectNestedSheetsControllerPromise = null;

  function _projectNestedSheetsController() {
    if (projectNestedSheetsController) return projectNestedSheetsController;
    const projectNestedSheets = _projectModule('DarklabProjectNestedSheets', importedProjectNestedSheets);
    const factory = projectNestedSheets && projectNestedSheets.createProjectNestedSheetsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectNestedSheets is unavailable');
    projectNestedSheetsController = factory({
      projectWorkspaceModal,
      projectTargetEditorOverlay,
      projectEntityEditorOverlay,
      projectPackageManifestOverlay,
      projectPackageWizardOverlay,
      isProjectTargetEditorOpen: () => !!(projectTargetEditorOverlay && projectTargetEditorOverlay.classList.contains('open')),
      isProjectEntityEditorOpen: () => !!(projectEntityEditorOverlay && projectEntityEditorOverlay.classList.contains('open')),
      isProjectPackageManifestOpen: () => !!(projectPackageManifestOverlay && projectPackageManifestOverlay.classList.contains('open')),
      isProjectPackageWizardOpen: () => !!(projectPackageWizardOverlay && projectPackageWizardOverlay.classList.contains('open')),
    });
    return projectNestedSheetsController;
  }

  function _projectNestedSheetsControllerIfReady() {
    return projectNestedSheetsController || (
      _projectFactoryReady('DarklabProjectNestedSheets', importedProjectNestedSheets, 'createProjectNestedSheetsController')
        ? _projectNestedSheetsController()
        : null
    );
  }

  function _loadProjectNestedSheetsController() {
    const readyController = _projectNestedSheetsControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectNestedSheetsControllerPromise) return projectNestedSheetsControllerPromise;
    projectNestedSheetsControllerPromise = _loadProjectWorkspaceDeferredModules(['project_nested_sheets'])
      .then(() => _projectNestedSheetsController())
      .finally(() => {
        projectNestedSheetsControllerPromise = null;
      });
    return projectNestedSheetsControllerPromise;
  }

  let projectWorkspaceRendererController = null;

  function _projectWorkspaceRendererController() {
    if (projectWorkspaceRendererController) return projectWorkspaceRendererController;
    const projectWorkspaceRenderer = _projectModule('DarklabProjectWorkspaceRenderer', importedProjectWorkspaceRenderer);
    const factory = projectWorkspaceRenderer && projectWorkspaceRenderer.createProjectWorkspaceRendererController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectWorkspaceRenderer is unavailable');
    projectWorkspaceRendererController = factory({
      closeProjectEntityEditor: _closeProjectEntityEditor,
      closeProjectTargetEditor: _closeProjectTargetEditor,
      emptyProjectPanel: _emptyProjectPanel,
      enhanceAppSelects: _shellEnhanceAppSelects(),
      ensureSelectedProject: _ensureSelectedProject,
      flushProjectNotesAutosave: _flushProjectNotesAutosave,
      focusProjectWorkspaceTab: _focusProjectWorkspaceTab,
      isProjectWorkspaceOpen,
      loadProjectFilteredFindings: _loadProjectFilteredFindings,
      loadProjectFindings: _loadProjectFindings,
      mobileView: _projectMobileView,
      projectArtifactsVisible: _projectArtifactsVisible,
      projectExplorerBody,
      projectFindingServerFiltersActive: _projectFindingServerFiltersActive,
      projectFindingsLoaded: _projectFindingsLoaded,
      projectMobileDetailBody,
      projectMobileTabItems: _projectMobileTabItems,
      projectPackageWizardActive: _projectPackageWizardActive,
      projectPagination: projectWorkspaceState.pagination,
      projectRows: projectWorkspaceState.rows,
      projectSummary: _projectSummary,
      projectTargetFilterActive: _projectTargetFilterActive,
      projectWorkspaceLoading: projectWorkspaceState.loading,
      projectWorkspaceSubtitle,
      renderProjectArtifacts: _renderProjectArtifacts,
      renderProjectWebSurface: _renderProjectWebSurface,
      renderProjectDetails: _renderProjectDetails,
      renderProjectEntities: _renderProjectEntities,
      renderProjectFilterBar: _renderProjectFilterBar,
      renderProjectFindings: _renderProjectFindings,
      renderProjectHeader: _renderProjectHeader,
      renderProjectList: _renderProjectList,
      renderProjectMobile: _renderProjectMobile,
      renderProjectActivity: _renderProjectActivity,
      renderProjectAssessment: _renderProjectAssessment,
      renderProjectOverview: _renderProjectOverview,
      renderProjectMonitoring: _renderProjectMonitoring,
      renderProjectPackages: _renderProjectPackages,
      renderProjectPackageWizardModal: _renderProjectPackageWizardModal,
      renderProjectReport: _renderProjectReport,
      renderProjectRuns: _renderProjectRuns,
      scheduleProjectFilterSortDividerSync: _scheduleProjectFilterSortDividerSync,
      selectedProject: _selectedProject,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      setWorkspaceTab: projectWorkspaceState.setTab,
      syncProjectFilterSortDivider: _syncProjectFilterSortDivider,
      syncProjectForms: _syncProjectForms,
      workspaceTab: projectWorkspaceState.tab,
    });
    return projectWorkspaceRendererController;
  }

  let projectWorkspaceBootstrapController = null;

  function _projectWorkspaceBootstrapController() {
    if (projectWorkspaceBootstrapController) return projectWorkspaceBootstrapController;
    const projectWorkspaceBootstrap = _projectModule('DarklabProjectWorkspaceBootstrap', importedProjectWorkspaceBootstrap);
    const factory = projectWorkspaceBootstrap && projectWorkspaceBootstrap.createProjectWorkspaceBootstrapController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectWorkspaceBootstrap is unavailable');
    const bindDismissibleFn = _shellFn('bindDismissible', importedBindDismissible);
    const bindMobileSheetFn = _shellFn('bindMobileSheet', importedBindMobileSheet);
    projectWorkspaceBootstrapController = factory({
      bindDismissible: bindDismissibleFn,
      bindFocusTrap: importedBindFocusTrap,
      bindMobileSheet: bindMobileSheetFn,
      closeProjectEntityEditor: _closeProjectEntityEditor,
      closeProjectPackageManifest: _closeProjectPackageManifest,
      closeProjectPackageWizard: _closeProjectPackageWizard,
      closeProjectTargetEditor: _closeProjectTargetEditor,
      closeProjectWorkspace,
      isProjectEntityEditorOpen,
      isProjectPackageManifestOpen,
      isProjectPackageWizardOpen,
      isProjectWorkspaceOpen,
      isProjectTargetEditorOpen,
      projectDetailsController: _projectDetailsController,
      projectEntityEditorController: _projectEntityEditorControllerIfReady,
      projectEntityEditorOverlay,
      projectMobileTabs,
      projectPackageManifestOverlay,
      projectPackageWizardOverlay,
      projectActivityController: _projectActivityControllerIfReady,
      projectPackagesController: _projectPackagesControllerIfReady,
      projectTargetEditorOverlay,
      projectTargetsController: _projectTargetsController,
      projectWorkspaceEventsController: _projectWorkspaceEventsController,
      projectWorkspaceModal,
      projectWorkspaceOverlay,
      projectWorkspaceShellController: _projectWorkspaceShellController,
      syncProjectMobileTabEdges: _syncProjectMobileTabEdges,
    });
    return projectWorkspaceBootstrapController;
  }

  let projectTargetsController = null;

  function _projectTargetsController() {
    if (projectTargetsController) return projectTargetsController;
    const projectTargets = _projectModule('DarklabProjectTargets', importedProjectTargets);
    const factory = projectTargets && projectTargets.createProjectTargetsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectTargets is unavailable');
    projectTargetsController = factory({
      EntityMetadataClient,
      targetHelpers: PROJECT_TARGET_HELPERS,
      overlay: projectTargetEditorOverlay,
      form: projectTargetCreateForm,
      typeSelect: projectTargetTypeSelect,
      valueInput: projectTargetValueInput,
      valueHelp: projectTargetValueHelp,
      valueError: projectTargetValueError,
      labelInput: projectTargetLabelInput,
      notesInput: projectTargetNotesInput,
      title: projectTargetEditorTitle,
      submitButton: projectTargetSubmitButton,
      getLastTargetType: projectWorkspaceState.lastTargetType,
      setLastTargetType: projectWorkspaceState.setLastTargetType,
      setEditingTargetId: projectWorkspaceState.setEditingTargetId,
      selectedProjectId: projectWorkspaceState.selectedId,
      activeProject: _activeProject,
      entityLabelValues: _entityLabelValues,
      entityNoteBody: _entityNoteBody,
      entityMetadataChips: _entityMetadataChips,
      entityMetadataChipClass: _entityMetadataChipClass,
      makeProjectButton: _makeProjectButton,
      emptyProjectPanel: _emptyProjectPanel,
      projectWorkspaceRequest: _projectWorkspaceRequest,
      syncEntityLabels: _syncEntityLabels,
      syncEntityNote: _syncEntityNote,
      refreshProjectWorkspace,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectWorkspace: _renderProjectWorkspace,
      invalidateProjectTargetPage: projectId => _projectDetailsController().invalidateTargetPage(projectId),
      invalidateProjectOverview: (projectId = '') => _projectOverviewControllerIfReady()?.invalidate?.(projectId),
      loadProjectTargetPage: (projectId, options) => _projectDetailsController().loadTargetPage(projectId, options),
      renderProjectMobileDetail: _renderProjectMobileDetail,
      loadProjectAutocompleteTargets: () => {
        importedLoadProjectAutocompleteTargets?.().catch(() => {});
      },
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      syncProjectWorkspaceNestedSuppression: _syncProjectWorkspaceNestedSuppression,
      installProjectMobileKeyboardGuards: _installProjectMobileKeyboardGuards,
      focusProjectNestedSheet: _focusProjectNestedSheet,
    });
    return projectTargetsController;
  }

  let projectRunsController = null;
  let projectRunsControllerPromise = null;

  function _projectRunsController() {
    if (projectRunsController) return projectRunsController;
    const projectRuns = _projectModule('DarklabProjectRuns', importedProjectRuns);
    const factory = projectRuns && projectRuns.createProjectRunsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectRuns is unavailable');
    projectRunsController = factory({
      apiFetch: _shellApiFetch,
      projectResponseError: _projectResponseError,
      projectExplorerBody: () => projectExplorerBody,
      projectRunItems: _projectRunItems,
      projectComparableRuns: _projectComparableRuns,
      projectFindingItems: _projectFindingItems,
      projectFindingsLoaded: _projectFindingsLoaded,
      projectArtifactItems: _projectArtifactItems,
      projectArtifactsVisible: _projectArtifactsVisible,
      projectTargetFilterActive: _projectTargetFilterActive,
      projectRunFilterActive: _projectRunFilterActive,
      filteredProjectRuns: _filteredProjectRuns,
      entityLabelValues: _entityLabelValues,
      entityMetadataChips: _entityMetadataChips,
      formatDate: _formatProjectDate,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      emptyProjectPanel: _emptyProjectPanel,
      projectItemRow: _projectItemRow,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      mobileView: _projectMobileView,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      logClientError: (message, err) => {
        _shellLogClientError(message, err);
      },
    });
    return projectRunsController;
  }

  function _projectRunsControllerIfReady() {
    return projectRunsController || (
      _projectFactoryReady('DarklabProjectRuns', importedProjectRuns, 'createProjectRunsController')
        ? _projectRunsController()
        : null
    );
  }

  function _loadProjectRunsController() {
    const readyController = _projectRunsControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectRunsControllerPromise) return projectRunsControllerPromise;
    projectRunsControllerPromise = _loadProjectWorkspaceDeferredModules(['project_runs'])
      .then(() => _projectRunsController())
      .finally(() => {
        projectRunsControllerPromise = null;
      });
    return projectRunsControllerPromise;
  }

  let projectMobileCompareController = null;
  let projectMobileCompareControllerPromise = null;

  function _projectMobileCompareController() {
    if (projectMobileCompareController) return projectMobileCompareController;
    const projectMobileCompare = _projectModule('DarklabProjectMobileCompare', importedProjectMobileCompare);
    const factory = projectMobileCompare && projectMobileCompare.createProjectMobileCompareController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectMobileCompare is unavailable');
    projectMobileCompareController = factory({
      projectWorkspaceModal,
      projectSummary: projectWorkspaceState.summary,
      projectComparableRuns: _projectComparableRuns,
      projectRunBaselineLabelOptions: _projectRunBaselineLabelOptions,
      projectRunCompareOptionText: _projectRunCompareOptionText,
      entityLabelValues: _entityLabelValues,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      compareProjectRuns: _compareProjectRuns,
      closeProjectWorkspace,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
    });
    return projectMobileCompareController;
  }

  function _projectMobileCompareControllerIfReady() {
    return projectMobileCompareController || (
      _projectFactoryReady(
        'DarklabProjectMobileCompare',
        importedProjectMobileCompare,
        'createProjectMobileCompareController',
      )
        ? _projectMobileCompareController()
        : null
    );
  }

  function _loadProjectMobileCompareController() {
    const readyController = _projectMobileCompareControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectMobileCompareControllerPromise) return projectMobileCompareControllerPromise;
    projectMobileCompareControllerPromise = _loadProjectWorkspaceDeferredModules(['project_mobile_compare'])
      .then(() => _projectMobileCompareController())
      .finally(() => {
        projectMobileCompareControllerPromise = null;
      });
    return projectMobileCompareControllerPromise;
  }

  let projectMobileShellController = null;
  let projectMobileControllersPromise = null;

  function _projectMobileShellController() {
    if (projectMobileShellController) return projectMobileShellController;
    const projectMobileShell = _projectModule('DarklabProjectMobileShell', importedProjectMobileShell);
    const factory = projectMobileShell && projectMobileShell.createProjectMobileShellController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectMobileShell is unavailable');
    projectMobileShellController = factory({
      activeProject: _activeProject,
      closeProjectEntityEditor: _closeProjectEntityEditor,
      closeProjectTargetEditor: _closeProjectTargetEditor,
      emptyProjectPanel: _emptyProjectPanel,
      mobileSection: _projectMobileSection,
      orderedProjectRows: _orderedProjectRows,
      projectIsArchived: _projectIsArchived,
      projectMobileBody,
      projectMobileCreateForm,
      projectMobileDetailView,
      projectMobileListView,
      projectMobileNameInput,
      projectMobilePagination,
      projectMobileRoot,
      projectMobileSummary,
      projectPagination: projectWorkspaceState.pagination,
      projectRows: projectWorkspaceState.rows,
      projectPagination: projectWorkspaceState.pagination,
      projectWorkspaceLoading: projectWorkspaceState.loading,
      renderMobileListRow: _renderProjectMobileListRow,
      renderProjectPagination: _renderProjectPagination,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      renderProjectWorkspace: _renderProjectWorkspace,
      selectedProjectId: projectWorkspaceState.selectedId,
      ensureProjectSummary: _ensureProjectSummary,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      setSelectedProjectId: projectWorkspaceState.setSelectedId,
      setWorkspaceTab: projectWorkspaceState.setTab,
    });
    return projectMobileShellController;
  }

  function _projectMobileShellControllerIfReady() {
    return projectMobileShellController || (
      _projectFactoryReady('DarklabProjectMobileShell', importedProjectMobileShell, 'createProjectMobileShellController')
        ? _projectMobileShellController()
        : null
    );
  }

  let projectMobileDetailController = null;

  function _projectMobileDetailController() {
    if (projectMobileDetailController) return projectMobileDetailController;
    const projectMobileDetail = _projectModule('DarklabProjectMobileDetail', importedProjectMobileDetail);
    const factory = projectMobileDetail && projectMobileDetail.createProjectMobileDetailController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectMobileDetail is unavailable');
    projectMobileDetailController = factory({
      projectWorkspaceModal,
      projectMobileDetailView,
      projectMobileDetailBody,
      projectMobileTabs,
      notePreviewLimit: PROJECT_WORKSPACE_CONSTANTS.mobileNotePreviewLimit,
      selectedProjectId: projectWorkspaceState.selectedId,
      setSelectedProjectId: projectWorkspaceState.setSelectedId,
      projectWorkspaceTab: projectWorkspaceState.tab,
      projectWorkspaceLoading: projectWorkspaceState.loading,
      activeProject: _activeProject,
      projectRows: projectWorkspaceState.rows,
      projectSummary: _projectSummary,
      projectCounts: _projectCounts,
      projectDisplayName: _projectDisplayName,
      projectTargetItems: _projectTargetItems,
      projectRunItems: _projectRunItems,
      projectRunById: _projectRunById,
      projectComparableRuns: _projectComparableRuns,
      projectArtifactItems: _projectArtifactItems,
      pagedProjectArtifactItems: _pagedProjectArtifactItems,
      projectArtifactPagination: _projectArtifactPagination,
      projectArtifactServerFilterKey: _projectArtifactServerFilterKey,
      loadProjectArtifacts: _loadProjectArtifacts,
      projectFindingItems: _projectFindingItems,
      projectFindingChanges: projectId => (
        _projectFindingsDataControllerIfReady()?.findingChanges(projectId) || null
      ),
      projectFindingsLoaded: _projectFindingsLoaded,
      projectFindingsLoadingId: () => _projectFindingsDataControllerIfReady()?.loadingId() || '',
      hasProjectFindings: _projectFindingsLoaded,
      projectFindingPagination: _projectFindingPagination,
      projectFindingServerFiltersActive: _projectFindingServerFiltersActive,
      projectFindingGroupCollapsed: _projectFindingGroupCollapsed,
      collapsedFindingGroupLabels: _projectCollapsedFindingGroupLabels,
      projectArtifactGroupCollapsed: _projectArtifactGroupCollapsed,
      projectTargetFilterActive: _projectTargetFilterActive,
      projectRunFilterActive: _projectRunFilterActive,
      projectRunPagination: _projectRunPagination,
      loadProjectRuns: _loadProjectRuns,
      projectArtifactsVisible: _projectArtifactsVisible,
      projectArtifactStatus: _projectArtifactStatus,
      projectArtifactStatusLabel: _projectArtifactStatusLabel,
      projectArtifactDetailLines: _projectArtifactDetailLines,
      projectFindingTargetText: _projectFindingTargetText,
      projectTargetLabel: _projectTargetLabel,
      projectRunFindingCount: _projectRunFindingCount,
      projectRunArtifactCount: _projectRunArtifactCount,
      filteredProjectRuns: _filteredProjectRuns,
      filteredProjectFindings: _filteredProjectFindings,
      filteredProjectArtifacts: _filteredProjectArtifacts,
      entityLabelValues: _entityLabelValues,
      entityNoteBody: _entityNoteBody,
      entityMetadataChips: _entityMetadataChips,
      entityMetadataChipClass: _entityMetadataChipClass,
      formatDate: _formatProjectDate,
      shortProjectRunId: _shortProjectRunId,
      makeProjectButton: _makeProjectButton,
      bindProjectRuntimePressable: _bindProjectRuntimePressable,
      openProjectAssessment: _openProjectAssessment,
      emptyProjectPanel: _emptyProjectPanel,
      findingReviewControl: _findingReviewControl,
      renderProjectMobileDetailTopbar: _renderProjectMobileDetailTopbar,
      renderProjectMobileTabs: _renderProjectMobileTabs,
      renderProjectMobileEntitiesTab: _renderProjectMobileEntitiesTab,
      renderProjectMobileAssessmentTab: _renderProjectMobileAssessmentTab,
      renderProjectWebSurface: _renderProjectWebSurface,
      renderProjectMobileOverviewTab: _renderProjectMobileOverviewTab,
      renderProjectMobilePackagesTab: _renderProjectMobilePackagesTab,
      renderProjectMobileReportTab: _renderProjectMobileReportTab,
      renderProjectMobileActivityTab: _renderProjectMobileActivityTab,
      renderProjectMobileMonitoringTab: _renderProjectMobileMonitoringTab,
      setProjectMobileView: _setProjectMobileView,
      loadProjectFindings: _loadProjectFindings,
      loadProjectFilteredFindings: _loadProjectFilteredFindings,
      groupBy: _groupBy,
      mobileMenuText: '☰',
      caretText: '▾',
      metaSeparator: ' · ',
    });
    return projectMobileDetailController;
  }

  function _projectMobileDetailControllerIfReady() {
    return projectMobileDetailController || (
      _projectFactoryReady(
        'DarklabProjectMobileDetail',
        importedProjectMobileDetail,
        'createProjectMobileDetailController',
      )
        ? _projectMobileDetailController()
        : null
    );
  }

  function _loadProjectMobileControllers() {
    const shell = _projectMobileShellControllerIfReady();
    const detail = _projectMobileDetailControllerIfReady();
    if (shell && detail) {
      return Promise.resolve({ shell, detail });
    }
    if (projectMobileControllersPromise) return projectMobileControllersPromise;
    projectMobileControllersPromise = _loadProjectWorkspaceDeferredModules(['project_mobile_shell', 'project_mobile_detail'])
      .then(() => ({
        shell: _projectMobileShellController(),
        detail: _projectMobileDetailController(),
      }))
      .finally(() => {
        projectMobileControllersPromise = null;
      });
    return projectMobileControllersPromise;
  }

  function _projectMobileModeActive() {
    return _shellUseMobileTerminalViewportMode()
      || !!(document.body && document.body.classList.contains('mobile-terminal-mode'));
  }

  function _projectMobileView() {
    return _projectMobileShellControllerIfReady()?.currentView?.() || 'list';
  }

  let projectEntityEditorController = null;
  let projectEntityEditorControllerPromise = null;

  function _projectEntityEditorController() {
    if (projectEntityEditorController) return projectEntityEditorController;
    const projectEntityEditor = _projectModule('DarklabProjectEntityEditor', importedProjectEntityEditor);
    const factory = projectEntityEditor && projectEntityEditor.createProjectEntityEditorController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectEntityEditor is unavailable');
    projectEntityEditorController = factory({
      overlay: projectEntityEditorOverlay,
      title: projectEntityEditorTitle,
      subtitle: projectEntityEditorSubtitle,
      form: projectEntityEditorForm,
      labelsInput: projectEntityLabelsInput,
      noteInput: projectEntityNoteInput,
      activityRoot: projectEntityActivityRoot,
      submitButton: projectEntitySubmitButton,
      parseLabelInput: EntityMetadataClient.parseLabelInput,
      entityTitleForEditor: _entityTitleForEditor,
      entityEditorLabelForType: _entityEditorLabelForType,
      entityLabelValues: _entityLabelValues,
      entityNoteBody: _entityNoteBody,
      syncEntityLabels: _syncEntityLabels,
      syncEntityNote: _syncEntityNote,
      projectResponseError: _projectResponseError,
      projectWorkspaceRequest: _projectWorkspaceRequest,
      openProjectActivity: _openProjectActivity,
      refreshProjectWorkspace,
      invalidateProjectFindings: _invalidateProjectFindings,
      invalidateProjectTargetPage: projectId => _projectDetailsController().invalidateTargetPage(projectId),
      loadProjectFindings: _loadProjectFindings,
      renderProjectExplorer: _renderProjectExplorer,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      syncProjectWorkspaceNestedSuppression: _syncProjectWorkspaceNestedSuppression,
      installProjectMobileKeyboardGuards: _installProjectMobileKeyboardGuards,
      focusProjectNestedSheet: _focusProjectNestedSheet,
    });
    projectEntityEditorController.bindFormEvents?.();
    return projectEntityEditorController;
  }

  function _projectEntityEditorControllerIfReady() {
    return projectEntityEditorController || (
      _projectFactoryReady(
        'DarklabProjectEntityEditor',
        importedProjectEntityEditor,
        'createProjectEntityEditorController',
      )
        ? _projectEntityEditorController()
        : null
    );
  }

  function _loadProjectEntityEditorController() {
    const readyController = _projectEntityEditorControllerIfReady();
    if (readyController) return Promise.resolve(readyController);
    if (projectEntityEditorControllerPromise) return projectEntityEditorControllerPromise;
    projectEntityEditorControllerPromise = _loadProjectWorkspaceDeferredModules(['project_entity_editor'])
      .then(() => _projectEntityEditorController())
      .finally(() => {
        projectEntityEditorControllerPromise = null;
      });
    return projectEntityEditorControllerPromise;
  }

  let projectWorkspaceLifecycleController = null;

  function _projectWorkspaceLifecycleController() {
    if (projectWorkspaceLifecycleController) return projectWorkspaceLifecycleController;
    const projectWorkspaceLifecycle = _projectModule('DarklabProjectWorkspaceLifecycle', importedProjectWorkspaceLifecycle);
    const factory = projectWorkspaceLifecycle && projectWorkspaceLifecycle.createProjectWorkspaceLifecycleController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectWorkspaceLifecycle is unavailable');
    projectWorkspaceLifecycleController = factory({
      apiFetch: _shellApiFetch,
      projectWorkspaceBody,
      selectedProjectId: projectWorkspaceState.selectedId,
      setSelectedProjectId: projectWorkspaceState.setSelectedId,
      projectRows: projectWorkspaceState.rows,
      setProjectRows: projectWorkspaceState.setRows,
      projectPagination: projectWorkspaceState.pagination,
      setProjectPagination: projectWorkspaceState.setPagination,
      projectSummaries: projectWorkspaceState.summaries,
      setProjectSummary: projectWorkspaceState.setSummary,
      setProjectSummaries: projectWorkspaceState.setSummaries,
      projectWorkspaceLoading: projectWorkspaceState.loading,
      setProjectWorkspaceLoading: projectWorkspaceState.setLoading,
      workspaceTab: projectWorkspaceState.tab,
      activeProject: _activeProject,
      loadActiveProjectContext,
      invalidateProjectFindings: _invalidateProjectFindings,
      invalidateProjectRuns: _invalidateProjectRuns,
      invalidateProjectTargetPage: projectId => _projectDetailsController().invalidateTargetPage(projectId),
      invalidateProjectEntities: (projectId = '') => _projectEntitiesControllerIfReady()?.invalidate?.(projectId),
      invalidateProjectArtifacts: (projectId = '') => _projectArtifactsControllerIfReady()?.invalidate?.(projectId),
      invalidateProjectWebSurface: (projectId = '') => _projectWebSurfaceControllerIfReady()?.invalidate?.(projectId),
      invalidateProjectAssessment: (projectId = '', options = {}) => (
        _projectAssessmentControllerIfReady()?.invalidate?.(projectId, options)
      ),
      invalidateProjectOverview: (projectId = '') => _projectOverviewControllerIfReady()?.invalidate?.(projectId),
      invalidateProjectMonitoring: (projectId = '') => _projectMonitoringControllerIfReady()?.invalidate?.(projectId),
      renderProjectWorkspace: _renderProjectWorkspace,
      syncProjectNotesForm: _syncProjectNotesForm,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      logClientError: (message, err, details) => {
        _shellLogClientError(message, err, details);
      },
    });
    return projectWorkspaceLifecycleController;
  }

  let projectWorkspaceEventsController = null;

  function _projectWorkspaceEventsController() {
    if (projectWorkspaceEventsController) return projectWorkspaceEventsController;
    const projectWorkspaceEvents = _projectModule('DarklabProjectWorkspaceEvents', importedProjectWorkspaceEvents);
    const factory = projectWorkspaceEvents && projectWorkspaceEvents.createProjectWorkspaceEventsController;
    if (typeof factory !== 'function') throw new Error('DarklabProjectWorkspaceEvents is unavailable');
    projectWorkspaceEventsController = factory({
      activeProject: _activeProject,
      artifactGroupKey: _projectArtifactGroupKey,
      avoidProjectRunCompareLabelSelfTarget: _avoidProjectRunCompareLabelSelfTarget,
      clearEditingTargetIf: projectWorkspaceState.clearEditingTargetIf,
      closeProjectEntityEditor: _closeProjectEntityEditor,
      closeProjectFilterMenus: _closeProjectFilterMenus,
      closeProjectPackageManifest: _closeProjectPackageManifest,
      closeProjectTargetEditor: _closeProjectTargetEditor,
      closeProjectWorkspace,
      compareProjectRuns: _compareProjectRuns,
      confirmProjectDelete: _confirmProjectDelete,
      confirmProjectDestructive: _confirmProjectDestructive,
      confirmProjectPackageDelete: _confirmProjectPackageDelete,
      confirmProjectRunUnlink: _confirmProjectRunUnlink,
      confirmProjectTargetDelete: _confirmProjectTargetDelete,
      downloadProjectArtifact: _downloadProjectArtifact,
      downloadProjectPackage: _downloadProjectPackage,
      entitiesController: _projectEntitiesControllerIfReady,
      entitySelectMode: projectWorkspaceState.entitySelectMode,
      filteredProjectFindings: _filteredProjectFindings,
      filtersController: _projectFiltersController,
      findingGroupKey: _projectFindingGroupKey,
      findingSelectMode: projectWorkspaceState.findingSelectMode,
      flushProjectNotesAutosave: _flushProjectNotesAutosave,
      invalidateProjectFindings: _invalidateProjectFindings,
      invalidateProjectOverview: (projectId = '') => _projectOverviewControllerIfReady()?.invalidate?.(projectId),
      isProjectWorkspaceOpen,
      linkLastRunToProject: _linkLastRunToProject,
      ensureProjectSummary: _ensureProjectSummary,
      loadProjectRuns: _loadProjectRuns,
      loadProjectAutocompleteTargets: () => {
        importedLoadProjectAutocompleteTargets?.().catch(() => {});
      },
      loadProjectFilteredFindings: _loadProjectFilteredFindings,
      loadProjectFindings: _loadProjectFindings,
      loadProjectTargetPage: (projectId, options) => _projectDetailsController().loadTargetPage(projectId, options),
      mobileView: _projectMobileView,
      openProjectEntityEditor: _openProjectEntityEditor,
      openProjectEntityInAtlas: _openProjectEntityInAtlas,
      openAtlas: _shellOpenAtlas,
      openFindingsBoard: _shellOpenFindingsBoard,
      openProjectEntityPicker: _openProjectEntityPicker,
      openProjectMobileActionSheet: _openProjectMobileActionSheet,
      openProjectMobileCompareSheet: _openProjectMobileCompareSheet,
      openProjectPackageManifest: _openProjectPackageManifest,
      openProjectPackageWizardFromPackage: _openProjectPackageWizardFromPackage,
      openProjectTargetEditor: _openProjectTargetEditor,
      packagesController: _projectPackagesControllerIfReady,
      previewProjectArtifact: _previewProjectArtifact,
      reportController: _projectReportControllerIfReady,
      activityController: _projectActivityControllerIfReady,
      monitoringController: _projectMonitoringControllerIfReady,
      projectArtifactItems: _projectArtifactItems,
      projectArtifactPagination: _projectArtifactPagination,
      projectDisplayName: _projectDisplayName,
      projectExplorerBody,
      projectFindingPagination: _projectFindingPagination,
      projectFindingItems: _projectFindingItems,
      projectFindingCommandFilterSet: _projectFindingCommandFilterSet,
      projectFindingLabelFilterSet: _projectFindingLabelFilterSet,
      projectFindingServerFiltersActive: _projectFindingServerFiltersActive,
      projectFindingSeverityFilterSet: _projectFindingSeverityFilterSet,
      projectFindingScopeFilterSet: _projectFindingScopeFilterSet,
      projectFindingStatusFilterSet: _projectFindingStatusFilterSet,
      projectMobileDetailBody,
      projectMobileListView,
      projectMobileProjectActions: _projectMobileProjectActions,
      projectPackageById: _projectPackageById,
      projectPagination: projectWorkspaceState.pagination,
      projectRows: projectWorkspaceState.rows,
      projectRunPagination: _projectRunPagination,
      projectRunFilterSet: _projectRunFilterSet,
      projectRunItems: _projectRunItems,
      projectSummary: _projectSummary,
      projectHostFilterSet: _projectHostFilterSet,
      projectTargetFilterSet: _projectTargetFilterSet,
      projectTargetPage: projectId => _projectDetailsController().targetPage(projectId),
      projectTargetById: (projectId, targetId) => _projectDetailsController().targetById(projectId, targetId),
      removeCachedProjectTarget: (projectId, targetId) => _projectDetailsController().removeCachedTarget(projectId, targetId),
      updateCachedProjectTarget: (projectId, targetId, updates) => _projectDetailsController().updateCachedTarget(projectId, targetId, updates),
      projectTargetItems: _projectTargetItems,
      projectWorkspaceModal,
      projectWorkspaceRequest: _projectWorkspaceRequest,
      refreshProjectWorkspace,
      renderProjectExplorer: _renderProjectExplorer,
      renderProjectMobile: _renderProjectMobile,
      renderProjectMobileDetail: _renderProjectMobileDetail,
      renderProjectWorkspace: _renderProjectWorkspace,
      selectedEntityIds: projectWorkspaceState.selectedEntityIds,
      selectedFindingIds: projectWorkspaceState.selectedFindingIds,
      selectedProjectId: projectWorkspaceState.selectedId,
      selectProjectFromMobile: _selectProjectFromMobile,
      setFindingViewMode: projectWorkspaceState.setFindingViewMode,
      setProjectFindingPageOffset: _setProjectFindingPageOffset,
      setProjectArtifactPageOffset: _setProjectArtifactPageOffset,
      setProjectRunPageOffset: _setProjectRunPageOffset,
      setProjectPaginationOffset: _setProjectPaginationOffset,
      setCachedFindingReviewState: _setCachedFindingReviewState,
      updateCachedProjectFinding: _updateCachedProjectFinding,
      setFindingSelectMode: projectWorkspaceState.setFindingSelectMode,
      setProjectMobileCreateOpen: _setProjectMobileCreateOpen,
      setProjectMobileView: _setProjectMobileView,
      setProjectPackageDownloadBusy: _setProjectPackageDownloadBusy,
      setProjectWorkspaceMessage: _setProjectWorkspaceMessage,
      setProjectRunCompareMode: _setProjectRunCompareMode,
      setSelectedProjectId: projectWorkspaceState.setSelectedId,
      setWorkspaceTab: projectWorkspaceState.setTab,
      openHistoryRunDetails: _shellFn('openHistoryRunDetails', importedOpenHistoryRunDetails),
      restoreHistoryRunIntoTab: _shellFn('restoreHistoryRunIntoTab', importedRestoreHistoryRunIntoTab),
      syncProjectRunCompareMode: _syncProjectRunCompareMode,
      toggleArtifactGroup: projectWorkspaceState.toggleArtifactGroup,
      toggleFindingGroup: projectWorkspaceState.toggleFindingGroup,
      toggleMobileArchivedOpen: _toggleProjectMobileArchivedOpen,
      workspaceTab: projectWorkspaceState.tab,
      logClientError: _shellLogClientError,
    });
    return projectWorkspaceEventsController;
  }

  function _projectTargetById(summary, targetId) {
    return _projectFiltersController().targetById(summary, targetId);
  }

  function _projectTargetFilterLabel(target) {
    return _projectFiltersController().targetFilterLabel(target);
  }

  function _targetFilterableProjectTab() {
    return _projectFiltersController().targetFilterableProjectTab(projectWorkspaceState.tab());
  }

  function _projectTargetFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().targetFilterSet(projectId);
  }

  function _projectTargetFilterIds(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFiltersController().targetFilterIds(projectId, summary);
  }

  function _projectTargetFilterActive(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFiltersController().targetFilterActive(projectId, summary);
  }

  function _projectRunFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().runFilterSet(projectId);
  }

  function _projectHostFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().hostFilterSet(projectId);
  }

  function _projectRunFilterIds(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFiltersController().runFilterIds(projectId, summary);
  }

  function _projectRunFilterActive(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFiltersController().runFilterActive(projectId, summary);
  }

  function _projectRunFilterLabel(run) {
    return _projectFiltersController().runFilterLabel(run);
  }

  function _projectRunFilterChipLabel(run) {
    return _projectFiltersController().runFilterChipLabel(run);
  }

  function _projectFindingStatusFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingStatusFilterSet(projectId);
  }

  function _projectFindingCommandFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingCommandFilterSet(projectId);
  }

  function _projectFindingSeverityFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingSeverityFilterSet(projectId);
  }

  function _projectFindingScopeFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingScopeFilterSet(projectId);
  }

  function _projectFindingStatusFilterValues(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingStatusFilterValues(projectId);
  }

  function _projectFindingStatusFilterActive(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingStatusFilterActive(projectId);
  }

  function _projectFindingLabelFilterSet(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingLabelFilterSet(projectId);
  }

  function _projectFindingLabelOptions(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingLabelOptions(projectId);
  }

  function _projectFindingLabelFilterValues(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingLabelFilterValues(projectId);
  }

  function _projectFindingLabelFilterActive(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingLabelFilterActive(projectId);
  }

  function _projectFindingNoteStateValue(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingNoteStateValue(projectId);
  }

  function _projectFindingNoteStateFilterActive(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingNoteStateFilterActive(projectId);
  }

  function _projectFindingOrphanFilterValue(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingOrphanFilterValue(projectId);
  }

  function _projectFindingOrphanFilterActive(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingOrphanFilterActive(projectId);
  }

  function _projectFindingSortValue(projectId = projectWorkspaceState.selectedId()) {
    return _projectFiltersController().findingSortValue(projectId);
  }

  function _projectFindingTargetText(summary, finding) {
    return _projectFiltersController().findingTargetText(summary, finding);
  }

  function _sortProjectFindings(findings, projectId, summary) {
    return _projectFiltersController().sortProjectFindings(findings, projectId, summary);
  }

  function _findingReviewStateLabel(value) {
    const normalized = String(value || '').trim();
    const state = PROJECT_WORKSPACE_CONSTANTS.findingReviewStates.find(item => item.value === normalized);
    return state ? state.label : normalized;
  }

  function _projectFindingGroupKey(projectId, runLabel) {
    return `${String(projectId || '')}\x1f${String(runLabel || '')}`;
  }

  function _projectFindingGroupCollapsed(projectId, runLabel) {
    return projectWorkspaceState.collapsedFindingGroups().has(_projectFindingGroupKey(projectId, runLabel));
  }

  function _projectCollapsedFindingGroupLabels(projectId = projectWorkspaceState.selectedId()) {
    const normalized = String(projectId || '');
    if (!normalized) return [];
    const prefix = `${normalized}\x1f`;
    return [...projectWorkspaceState.collapsedFindingGroups()]
      .filter(key => String(key || '').startsWith(prefix))
      .map(key => String(key).slice(prefix.length))
      .filter(Boolean);
  }

  function _projectArtifactGroupKey(projectId, runId) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.groupKey(projectId, runId);
    return `${String(projectId || '')}\x1f${String(runId || '')}`;
  }

  function _projectArtifactGroupCollapsed(projectId, runId) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.groupCollapsed(projectId, runId);
    return projectWorkspaceState.collapsedArtifactGroups().has(_projectArtifactGroupKey(projectId, runId));
  }

  function _projectRunItems(summary) {
    return _projectSharedUiController().runItems(summary);
  }

  function _projectRunPagination(projectId = projectWorkspaceState.selectedId()) {
    return _projectRunsControllerIfReady()?.page(projectId) || {
      runs: [],
      total: 0,
      limit: 50,
      offset: 0,
      loading: false,
      loaded: false,
      error: '',
    };
  }

  function _setProjectRunPageOffset(projectId = projectWorkspaceState.selectedId(), offset = 0) {
    _projectRunsControllerIfReady()?.setPageOffset(projectId, offset);
  }

  async function _loadProjectRuns(projectId = projectWorkspaceState.selectedId(), options = {}) {
    await _loadProjectRunsController().then(controller => controller.load(projectId, options));
  }

  function _invalidateProjectRuns(projectId = '') {
    _projectRunsControllerIfReady()?.invalidate(projectId);
  }

  function _projectRunById(summary, runId) {
    return _projectSharedUiController().runById(summary, runId);
  }

  function _projectComparableRuns(summary) {
    return _projectSharedUiController().comparableRuns(summary);
  }

  function _shortProjectRunId(runId) {
    return _projectSharedUiController().shortRunId(runId);
  }

  function _projectArtifactItems(summary) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.items(summary);
    return summary && Array.isArray(summary.artifacts) ? summary.artifacts : [];
  }

  function _projectArtifactPagination(projectId = projectWorkspaceState.selectedId()) {
    const controller = _projectArtifactsControllerIfReady() || (_projectArtifactsFactoryReady() ? _projectArtifactsController() : null);
    if (controller) return controller.page(projectId);
    return { artifacts: [], total: 0, runCounts: {}, limit: 50, offset: 0, loading: true, loaded: false, error: '' };
  }

  function _setProjectArtifactPageOffset(projectId = projectWorkspaceState.selectedId(), offset = 0) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) controller.setPageOffset(projectId, offset);
  }

  function _pagedProjectArtifactItems(projectId = projectWorkspaceState.selectedId(), artifacts = []) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.pagedItems(projectId, artifacts);
    const page = _projectArtifactPagination(projectId);
    const offset = Math.max(0, Number(page.offset || 0));
    const limit = Math.max(1, Number(page.limit || 50));
    return (Array.isArray(artifacts) ? artifacts : []).slice(offset, offset + limit);
  }

  async function _loadProjectArtifacts(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId), options = {}) {
    const controller = await _loadProjectArtifactsController();
    return controller.load(projectId, summary, options);
  }

  async function _loadAllProjectArtifacts(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    const controller = await _loadProjectArtifactsController();
    return controller.loadAll(projectId, summary);
  }

  function _projectFilesEnabled() {
    return !!(_shellValue('APP_CONFIG') && _shellValue('APP_CONFIG').workspace_enabled === true);
  }

  function _projectArtifactsVisible() {
    return _projectFilesEnabled();
  }

  function _projectArtifactStatus(artifact) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.status(artifact);
    const artifactStatus = String(artifact && artifact.file_status || '').trim();
    if (['available', 'missing', 'changed', 'disabled'].includes(artifactStatus)) return artifactStatus;
    return artifact && artifact.file_available === false ? 'missing' : 'available';
  }

  function _projectArtifactStatusLabel(artifact) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.statusLabel(artifact);
    const artifactStatus = _projectArtifactStatus(artifact);
    if (artifactStatus === 'disabled') return 'disabled';
    if (artifactStatus === 'changed') return 'changed';
    if (artifactStatus === 'missing') return 'missing';
    return 'available';
  }

  function _projectArtifactAccessory(projectId, artifact) {
    const controller = _projectArtifactsControllerIfReady();
    return controller ? controller.accessory(projectId, artifact) : null;
  }

  function _entityLabelValues(entity) {
    return _projectSharedUiController().entityLabelValues(entity);
  }

  function _entityNoteBody(entity) {
    return _projectSharedUiController().entityNoteBody(entity);
  }

  function _entityMetadataChips(entity) {
    return _projectSharedUiController().entityMetadataChips(entity);
  }

  function _entityMetadataChipClass(kind = 'label') {
    return _projectSharedUiController().entityMetadataChipClass(kind);
  }

  function _projectProvenanceSummary(manifest, options) {
    return _projectSharedUiController().projectProvenanceSummary(manifest, options);
  }

  function _projectProvenanceSummaryElement(manifest, options) {
    return _projectSharedUiController().projectProvenanceSummaryElement(manifest, options);
  }

  function _appendProjectLabelChips(parent, project, { className = 'project-label-chips' } = {}) {
    _projectDetailsController().appendLabelChips(parent, project, { className });
  }

  function _appendProjectMobileLabelChips(parent, project) {
    _projectDetailsController().appendMobileLabelChips(parent, project);
  }

  function _entityTitleForEditor(entityType, entity) {
    return _projectSharedUiController().entityTitleForEditor(entityType, entity);
  }

  function _entityEditorLabelForType(entityType) {
    return _projectSharedUiController().entityEditorLabelForType(entityType);
  }

  function _projectArtifactDetail(artifact) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.detail(artifact);
    const parts = [
      artifact && artifact.kind || 'file',
      artifact && artifact.content_type || 'unknown type',
      _formatProjectDate(artifact && artifact.created),
    ];
    const artifactStatus = _projectArtifactStatus(artifact);
    const statusDetail = String(artifact && artifact.file_status_detail || '').trim();
    if (artifactStatus === 'changed') {
      parts.push(`current ${_formatProjectBytes(artifact && artifact.current_byte_size)}`);
    } else if (artifactStatus === 'missing') {
      parts.push(statusDetail || 'workspace file is missing');
    } else if (artifactStatus === 'disabled') {
      parts.push(statusDetail || 'Files are disabled on this instance');
    }
    return parts.filter(Boolean).join(' · ');
  }

  function _projectArtifactDetailLines(artifact) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.detailLines(artifact);
    const artifactStatus = _projectArtifactStatus(artifact);
    const statusDetail = String(artifact && artifact.file_status_detail || '').trim();
    const lines = [
      [artifact && artifact.kind || 'file', artifact && artifact.content_type || 'unknown type'].filter(Boolean).join(' · '),
      _formatProjectDate(artifact && artifact.created),
    ].filter(Boolean);
    if (artifactStatus === 'changed') {
      lines.push(`current ${_formatProjectBytes(artifact && artifact.current_byte_size)}`);
    } else if (artifactStatus === 'missing') {
      lines.push(statusDetail || 'workspace file is missing');
    } else if (artifactStatus === 'disabled') {
      lines.push(statusDetail || 'Files are disabled on this instance');
    }
    return lines;
  }

  function _projectArtifactDownloadName(artifactPath = '', fallback = 'artifact') {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.downloadName(artifactPath, fallback);
    const name = String(artifactPath || '').split('/').filter(Boolean).pop();
    return name || fallback;
  }

  function _downloadBlobAsAttachment(blob, filename, successMessage = '') {
    _projectSharedUiController().downloadBlobAsAttachment(blob, filename, successMessage);
  }

  function _downloadUrlAsAttachment(url, filename = '', successMessage = '') {
    _projectSharedUiController().downloadUrlAsAttachment(url, filename, successMessage);
  }

  function _syncProjectWorkspaceNestedSuppression() {
    _projectNestedSheetsControllerIfReady()?.syncWorkspaceSuppression();
  }

  function _focusProjectNestedSheet(overlay, preferred = null) {
    _loadProjectNestedSheetsController()
      .then(controller => controller.focusNestedSheet(overlay, preferred))
      .catch((err) => {
        _shellLogClientError('failed to focus project nested sheet', err);
      });
  }

  function _syncProjectMobileFocusedField() {
    _projectNestedSheetsControllerIfReady()?.syncMobileFocusedField();
  }

  function _installProjectMobileKeyboardGuards() {
    _loadProjectNestedSheetsController()
      .then(controller => controller.installKeyboardGuards())
      .catch((err) => {
        _shellLogClientError('failed to install project mobile keyboard guards', err);
      });
  }

  function _closeProjectPackageManifest() {
    const controller = _projectPackagesControllerIfReady();
    if (controller) {
      controller.closeManifest();
      return;
    }
    if (!projectPackageManifestOverlay) return;
    projectPackageManifestOverlay.classList.add('u-hidden');
    projectPackageManifestOverlay.classList.remove('open');
    projectPackageManifestOverlay.setAttribute('aria-hidden', 'true');
  }

  function isProjectPackageManifestOpen() {
    const controller = _projectPackagesControllerIfReady();
    if (controller) return controller.isManifestOpen();
    return !!(projectPackageManifestOverlay && projectPackageManifestOverlay.classList.contains('open'));
  }

  function _openProjectPackageManifest(pkg) {
    _loadProjectPackagesController()
      .then(controller => controller.openManifest(pkg))
      .catch((err) => {
        _shellLogClientError('failed to load project package manifest', err);
        _setProjectWorkspaceMessage('Could not load package manifest.', { error: true });
      });
  }

  async function _previewProjectArtifact(projectId, artifactId) {
    const controller = await _loadProjectArtifactsController();
    await controller.preview(projectId, artifactId);
  }

  async function _downloadProjectArtifact(projectId, artifactId, artifactPath = '') {
    const controller = await _loadProjectArtifactsController();
    await controller.download(projectId, artifactId, artifactPath);
  }

  function _projectPackageItems(summary) {
    const controller = _projectPackagesControllerIfReady();
    if (controller) return controller.items(summary);
    return summary && Array.isArray(summary.packages) ? summary.packages : [];
  }

  function _projectPackageWizardActive(projectId = projectWorkspaceState.selectedId()) {
    const controller = _projectPackagesControllerIfReady();
    return controller ? controller.isWizardActive(projectId) : false;
  }

  function isProjectPackageWizardOpen() {
    const controller = _projectPackagesControllerIfReady();
    if (controller) return controller.isWizardOpen();
    return !!(projectPackageWizardOverlay && projectPackageWizardOverlay.classList.contains('open'));
  }

  function isProjectEntityEditorOpen() {
    const controller = _projectEntityEditorControllerIfReady();
    if (!controller) {
      return !!(projectEntityEditorOverlay && projectEntityEditorOverlay.classList.contains('open'));
    }
    return controller.isOpen();
  }

  function _closeProjectEntityEditor() {
    const controller = _projectEntityEditorControllerIfReady();
    if (!controller) {
      if (!projectEntityEditorOverlay) return;
      projectEntityEditorOverlay.classList.add('u-hidden');
      projectEntityEditorOverlay.classList.remove('open');
      projectEntityEditorOverlay.setAttribute('aria-hidden', 'true');
      return;
    }
    controller.close();
  }

  function _openProjectEntityEditor(projectId, entityType, entity, options = {}) {
    return _loadProjectEntityEditorController()
      .then(controller => controller.open(projectId, entityType, entity, options));
  }

  function openEntityMetadataEditor(entityType, entity, options = {}) {
    const projectId = options && Object.prototype.hasOwnProperty.call(options, 'projectId')
      ? options.projectId
      : '';
    _ensureProjectWorkspaceModules()
      .then(() => _openProjectEntityEditor(projectId, entityType, entity, options))
      .catch((err) => {
        _shellLogClientError('failed to load project entity editor', err);
        _shellShowToast('Could not open the metadata editor.', 'error');
      });
  }

  function _renderProjectPackageWizardModal(options = {}) {
    const controller = _projectPackagesControllerIfReady();
    if (controller) controller.renderWizardModal(options);
  }

  function _openProjectPackageWizard(projectId, preset = 'evidence') {
    _loadProjectPackagesController()
      .then(controller => controller.openWizard(projectId, preset))
      .catch((err) => {
        _shellLogClientError('failed to load project package wizard', err);
        _setProjectWorkspaceMessage('Could not load package builder.', { error: true });
      });
  }

  function _openProjectPackageWizardFromPackage(projectId, pkg) {
    _loadProjectPackagesController()
      .then(controller => controller.openWizardFromPackage(projectId, pkg))
      .catch((err) => {
        _shellLogClientError('failed to load project package wizard', err);
        _setProjectWorkspaceMessage('Could not load package builder.', { error: true });
      });
  }

  function _closeProjectPackageWizard(options = {}) {
    const controller = _projectPackagesControllerIfReady();
    if (controller) controller.closeWizard(options);
    else if (projectPackageWizardOverlay) {
      projectPackageWizardOverlay.classList.add('u-hidden');
      projectPackageWizardOverlay.classList.remove('open');
      projectPackageWizardOverlay.setAttribute('aria-hidden', 'true');
    }
  }

  function _projectPackageById(summary, packageId) {
    const controller = _projectPackagesControllerIfReady();
    if (controller) return controller.byId(summary, packageId);
    const normalized = String(packageId || '').trim();
    if (!normalized || !summary || !Array.isArray(summary.packages)) return null;
    return summary.packages.find(item => String(item && item.id || '') === normalized) || null;
  }

  function _setProjectPackageDownloadBusy(button, busy) {
    const controller = _projectPackagesControllerIfReady();
    if (controller) {
      controller.setDownloadBusy(button, busy);
      return;
    }
    if (button) button.disabled = !!busy;
  }

  async function _downloadProjectPackage(projectId, pkg) {
    const controller = await _loadProjectPackagesController();
    await controller.downloadPackage(projectId, pkg);
  }

  function _projectFindingItems(projectId = projectWorkspaceState.selectedId()) {
    return _projectFindingsDataControllerIfReady()?.items(projectId) || [];
  }

  function _projectFindingsLoaded(projectId = projectWorkspaceState.selectedId()) {
    return !!_projectFindingsDataControllerIfReady()?.loaded(projectId);
  }

  function _projectFindingPagination(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFindingsDataControllerIfReady()?.page(projectId, summary) || {
      findings: [],
      total: Number(summary && summary.counts && summary.counts.findings || 0),
      limit: 50,
      offset: 0,
      loading: false,
      loaded: false,
      error: '',
    };
  }

  function _setProjectFindingPageOffset(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId), offset = 0) {
    _projectFindingsDataControllerIfReady()?.setPageOffset(projectId, summary, offset);
  }

  function _projectFindingServerFilterParams(projectId, summary = _projectSummary(projectId)) {
    return _projectFiltersController().findingServerFilterParams(projectId, summary);
  }

  function _projectArtifactServerFilterParams(projectId, summary = _projectSummary(projectId)) {
    const controller = _projectArtifactsControllerIfReady();
    return controller ? controller.serverFilterParams(projectId, summary) : new URLSearchParams();
  }

  function _projectArtifactServerFilterKey(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    const controller = _projectArtifactsControllerIfReady();
    if (controller) return controller.serverFilterKey(projectId, summary);
    const params = _projectArtifactServerFilterParams(projectId, summary);
    params.sort?.();
    return params.toString();
  }

  function _projectFindingServerFiltersActive(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFiltersController().findingServerFiltersActive(projectId, summary);
  }

  function _projectFindingFilteredKey(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFiltersController().findingFilteredKey(projectId, summary);
  }

  function _projectFilteredFindingItems(projectId = projectWorkspaceState.selectedId(), summary = _projectSummary(projectId)) {
    return _projectFiltersController().filteredFindingItems(projectId, summary);
  }

  function _invalidateProjectFilteredFindings(projectId = '') {
    _projectFindingsDataControllerIfReady()?.invalidateFiltered(projectId);
  }

  function _invalidateProjectFindings(projectId = '') {
    _projectFindingsDataControllerIfReady()?.invalidate(projectId);
  }

  function _projectTargetLabel(summary, targetId) {
    return _projectSharedUiController().targetLabel(summary, targetId);
  }

  function _formatProjectDate(value) {
    return _projectSharedUiController().formatDate(value);
  }

  function _formatProjectBytes(value) {
    return _projectSharedUiController().formatBytes(value);
  }

  function _emptyProjectPanel(text) {
    return _projectSharedUiController().emptyPanel(text);
  }

  function _projectMetaRow(label, value) {
    return _projectSharedUiController().metaRow(label, value);
  }

  function _projectItemRow({ title, meta = '', detail = '', badge = '', chips = [], action = null, accessory = null, forceArticle = false }) {
    return _projectSharedUiController().itemRow({ title, meta, detail, badge, chips, action, accessory, forceArticle });
  }

  function _findingReviewControl(finding, projectId) {
    return _projectFindingsControllerIfReady()?.reviewControl(finding, projectId) || null;
  }

  function _findingRowAccessory(finding, projectId) {
    return _projectFindingsControllerIfReady()?.rowAccessory(finding, projectId) || null;
  }

  function _openProjectTargetEditor(projectId, target = null) {
    _projectTargetsController().openEditor(projectId, target);
  }

  function _closeProjectTargetEditor(options = {}) {
    if (!_projectWorkspaceModulesReady()) {
      if (!projectTargetEditorOverlay) return;
      projectTargetEditorOverlay.classList.add('u-hidden');
      projectTargetEditorOverlay.classList.remove('open');
      projectTargetEditorOverlay.setAttribute('aria-hidden', 'true');
      return;
    }
    _projectTargetsController().closeEditor(options);
  }

  function isProjectTargetEditorOpen() {
    if (!_projectWorkspaceModulesReady()) {
      return !!(projectTargetEditorOverlay && projectTargetEditorOverlay.classList.contains('open'));
    }
    return _projectTargetsController().isOpen();
  }

  function _projectTargetDisplayRow(projectId, target) {
    return _projectTargetsController().targetDisplayRow(projectId, target);
  }

  function _projectRunRemoveControl(projectId, run) {
    return _projectRunsControllerIfReady()?.runRemoveControl(projectId, run) || null;
  }

  function _projectRunFindingCount(projectId, runId, run = null) {
    return _projectRunsControllerIfReady()?.runFindingCount(projectId, runId, run) || 0;
  }

  function _projectRunArtifactCount(summary, runId, run = null) {
    return _projectRunsControllerIfReady()?.runArtifactCount(summary, runId, run) || 0;
  }

  function _projectRunControls(projectId, run, summary) {
    return _projectRunsControllerIfReady()?.runControls(projectId, run, summary) || null;
  }

  function _projectRunBaselineLabelOptions(runs) {
    return _projectRunsControllerIfReady()?.baselineLabelOptions(runs) || [];
  }

  function _projectRunCompareOptionText(run) {
    return _projectRunsControllerIfReady()?.compareOptionText(run) || String(run && (run.command || run.id) || '');
  }

  function _syncProjectRunCompareMode(wrap) {
    _projectRunsControllerIfReady()?.syncCompareMode(wrap);
  }

  function _setProjectRunCompareMode(modeButton, event = null) {
    _projectRunsControllerIfReady()?.setCompareMode(modeButton, event);
  }

  function _avoidProjectRunCompareLabelSelfTarget(container, label) {
    _projectRunsControllerIfReady()?.avoidCompareLabelSelfTarget(container, label);
  }

  function _compareProjectRuns(projectId, leftId, mode, targetValue, controls = null) {
    _loadProjectRunsController()
      .then(controller => controller.compareRuns(projectId, leftId, mode, targetValue, controls))
      .catch((err) => {
        _shellLogClientError('failed to load project run comparison', err);
        _setProjectWorkspaceMessage('Could not load project run comparison.', { error: true });
      });
  }

  function _renderProjectRunCompareControls(runs) {
    return _projectRunsControllerIfReady()?.renderCompareControls(runs) || document.createTextNode('');
  }

  function _renderProjectTargets(projectId, targets) {
    return _projectTargetsController().renderTargets(projectId, targets);
  }

  function _setProjectFilterMenuOpen(menu, open) {
    _projectFiltersController().setFilterMenuOpen(menu, open);
  }

  function _closeProjectFilterMenus(exceptMenu = null) {
    _projectFiltersController().closeFilterMenus(exceptMenu);
  }

  function _renderProjectFilterBar(projectId, summary) {
    return _projectFiltersController().renderFilterBar(projectId, summary);
  }

  function _syncProjectFilterSortDivider(root) {
    _projectFiltersController().syncFilterSortDivider(root);
  }

  function _scheduleProjectFilterSortDividerSync(root) {
    if (!_projectWorkspaceModulesReady()) return;
    _projectFiltersController().scheduleFilterSortDividerSync(root);
  }

  function _projectRunDirectTargetIds(run) {
    return _projectFiltersController().runDirectTargetIds(run);
  }

  function _projectFindingTargetIds(finding) {
    return _projectFiltersController().findingTargetIds(finding);
  }

  function _projectRunIdsMatchingTargets(projectId, filterIds) {
    return _projectFiltersController().runIdsMatchingTargets(projectId, filterIds);
  }

  function _projectRunMatchesTargetFilters(run, projectId, filterIds, matchingRunIds) {
    return _projectFiltersController().runMatchesTargetFilters(run, projectId, filterIds, matchingRunIds);
  }

  function _filteredProjectRuns(projectId, summary) {
    return _projectFiltersController().filteredRuns(projectId, summary);
  }

  function _filteredProjectFindings(projectId, summary) {
    return _projectFiltersController().filteredFindings(projectId, summary);
  }

  function _filteredProjectArtifacts(projectId, summary) {
    return _projectFiltersController().filteredArtifacts(projectId, summary);
  }

  function _groupBy(items, keyFn) {
    return _projectSharedUiController().groupBy(items, keyFn);
  }

  async function _loadProjectFindings(projectId, options = {}) {
    return _loadProjectFindingsDataController().then(controller => controller.load(projectId, options));
  }

  async function _loadProjectFilteredFindings(projectId, summary = _projectSummary(projectId), options = {}) {
    const controller = await _loadProjectFindingsDataController();
    await controller.loadFiltered(projectId, summary, options);
  }

  function _syncProjectForms(project = _selectedProject()) {
    _projectDetailsController().syncForms(project);
  }

  function _syncProjectNotesForm() {
    if (!_projectWorkspaceModulesReady()) return;
    _projectDetailsController().syncNotesForm();
  }

  function _flushProjectNotesAutosave() {
    if (!_projectWorkspaceModulesReady()) return Promise.resolve();
    return _projectDetailsController().flushNotesAutosave();
  }

  function _makeProjectButton(label, action, projectId, role = 'secondary', tone = '') {
    return _projectSharedUiController().makeButton(label, action, projectId, role, tone);
  }

  function _projectIsArchived(project) {
    return _projectListController().isArchived(project);
  }

  function _orderedProjectRows(activeId, rows = projectWorkspaceState.rows()) {
    return _projectListController().orderedRows(activeId, rows);
  }

  function _renderProjectList() {
    _projectListController().renderList();
  }

  function _projectMobileTabItems(projectId, summary) {
    return _projectNavigationController().mobileTabItems(projectId, summary);
  }

  function _syncProjectMobileActiveTabScroll() {
    _projectNavigationController().syncMobileActiveTabScroll();
  }

  function _syncProjectMobileTabEdges() {
    _projectNavigationController().syncMobileTabEdges();
  }

  function _renderProjectMobileListRow(project, activeId) {
    return _projectListController().renderMobileListRow(project, activeId);
  }

  function _renderProjectPagination(host, options = {}) {
    return _projectListController().renderPagination(host, options);
  }

  function _projectMobileSection(label, count, { open = true } = {}) {
    return _projectListController().mobileSection(label, count, { open });
  }

  async function _setProjectMobileCreateOpen(open, { focus = false } = {}) {
    const { shell } = await _loadProjectMobileControllers();
    shell.setCreateOpen(open, { focus });
  }

  async function _setProjectMobileView(view) {
    const { shell } = await _loadProjectMobileControllers();
    shell.setView(view);
  }

  async function _selectProjectFromMobile(projectId, tab = '') {
    const { shell } = await _loadProjectMobileControllers();
    shell.selectProject(projectId, tab);
  }

  function _projectMobileProjectActions(project) {
    return _projectMobileShellControllerIfReady()?.projectActions(project) || [];
  }

  function _toggleProjectMobileArchivedOpen() {
    const controller = _projectMobileShellControllerIfReady();
    if (!controller) return;
    controller.setArchivedOpen(!controller.isArchivedOpen());
  }

  function _renderProjectMobileDetailTopbar(project, activeId) {
    _projectNavigationController().renderMobileDetailTopbar(project, activeId);
  }

  function _renderProjectMobileTabs(projectId, summary) {
    _projectNavigationController().renderMobileTabs(projectId, summary);
  }

  function _projectMobileActionMenu(projectId, label, actions = []) {
    return _projectMobileDetailControllerIfReady()?.actionMenu(projectId, label, actions) || null;
  }

  function _closeProjectMobileActionSheet({ restoreFocus = true } = {}) {
    _projectMobileDetailControllerIfReady()?.closeActionSheet({ restoreFocus });
  }

  function _openProjectMobileActionSheet(projectId, label, actions = [], returnFocus = null) {
    _loadProjectMobileControllers()
      .then(({ detail }) => detail.openActionSheet(projectId, label, actions, returnFocus))
      .catch((err) => {
        _shellLogClientError('failed to open project mobile action sheet', err);
      });
  }

  function _closeProjectMobileCompareSheet({ restoreFocus = true } = {}) {
    _projectMobileCompareControllerIfReady()?.close({ restoreFocus });
  }

  function _openProjectMobileCompareSheet(projectId, returnFocus = null) {
    _loadProjectMobileCompareController()
      .then(controller => controller.open(projectId, returnFocus))
      .catch((err) => {
        _shellLogClientError('failed to open project mobile compare sheet', err);
      });
  }

  function _projectMobileContentRow({
    title,
    meta = '',
    detail = '',
    badge = '',
    chips = [],
    action = null,
    accessory = null,
    className = '',
  }) {
    const controller = _projectMobileDetailControllerIfReady();
    if (!controller) return _projectItemRow({ title, meta, detail, badge, chips, action, accessory });
    return controller.contentRow({
      title,
      meta,
      detail,
      badge,
      chips,
      action,
      accessory,
      className,
    });
  }

  function _projectMobileEmptyPanel(text, actions = []) {
    return _projectMobileDetailControllerIfReady()?.emptyPanel(text, actions) || _emptyProjectPanel(text);
  }

  function _renderProjectMobileDetail() {
    _projectMobileDetailControllerIfReady()?.renderDetail();
  }

  function _renderProjectMobile() {
    if (!_projectMobileModeActive()) return;
    if (projectMobileShellController && projectMobileDetailController) {
      projectMobileShellController.renderMobile();
      return;
    }
    if (projectMobileBody && !String(projectMobileBody.textContent || '').trim()) {
      projectMobileBody.replaceChildren(_emptyProjectPanel('Loading projects...'));
    }
    _loadProjectMobileControllers()
      .then(({ shell }) => {
        shell.renderMobile();
      })
      .catch((err) => {
        _shellLogClientError('failed to load project mobile workspace', err);
        if (projectMobileBody) projectMobileBody.replaceChildren(_emptyProjectPanel('Could not load Projects.'));
      });
  }

  function _renderProjectHeader(project, summary, options = {}) {
    return _projectNavigationController().renderProjectHeader(project, summary, options);
  }

  function _focusProjectWorkspaceTab(tabId) {
    _projectNavigationController().focusWorkspaceTab(tabId);
  }

  function cycleProjectWorkspaceTab(offset = 1) {
    if (!_projectWorkspaceModulesReady()) return false;
    return _projectWorkspaceRendererController().cycleTab(offset);
  }

  function _renderProjectDetails(container, project, summary) {
    _projectDetailsController().renderDetails(container, project, summary);
  }

  function _renderProjectRuns(container, projectId, summary) {
    if (projectRunsController) {
      projectRunsController.renderRuns(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project runs...'));
    _loadProjectRunsController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'runs') return;
        container.replaceChildren();
        controller.renderRuns(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project runs', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project runs.'));
      });
  }

  function _openProjectEntityInAtlas(projectId, summary, entity) {
    _loadProjectEntitiesController()
      .then(controller => controller.openInAtlas(projectId, summary, entity))
      .catch((err) => {
        _shellLogClientError('failed to open project entity in Atlas', err);
        _setProjectWorkspaceMessage('Could not open the entity in Atlas.', { error: true });
      });
  }

  function _openProjectEntityPicker(projectId) {
    _loadProjectEntitiesController()
      .then(controller => controller.openPicker(projectId))
      .catch((err) => {
        _shellLogClientError('failed to open project entity picker', err);
        _setProjectWorkspaceMessage('Could not load entity picker.', { error: true });
      });
  }

  function _renderProjectEntities(container, projectId, summary) {
    if (projectEntitiesController) {
      projectEntitiesController.renderEntities(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project entities...'));
    _loadProjectEntitiesController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'entities') return;
        container.replaceChildren();
        controller.renderEntities(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project entities', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project entities.'));
      });
  }

  function _renderProjectFindings(container, projectId, summary) {
    if (projectFindingsController) {
      projectFindingsController.renderFindings(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project findings...'));
    _loadProjectFindingsController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'findings') return;
        container.replaceChildren();
        controller.renderFindings(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project findings', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project findings.'));
      });
  }

  function _renderProjectArtifacts(container, projectId, summary) {
    if (projectArtifactsController) {
      projectArtifactsController.renderArtifacts(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project artifacts...'));
    _loadProjectArtifactsController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'artifacts') return;
        container.replaceChildren();
        controller.renderArtifacts(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project artifacts', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project artifacts.'));
      });
  }

  function _renderProjectWebSurface(container, projectId) {
    if (projectWebSurfaceController) {
      projectWebSurfaceController.render(container, projectId);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading Web Surface captures...'));
    _loadProjectWebSurfaceController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'web-surface') return;
        container.replaceChildren();
        controller.render(container, projectId);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project Web Surface', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load the Web Surface.'));
      });
  }

  function _renderProjectPackages(container, projectId, summary) {
    if (projectPackagesController) {
      projectPackagesController.renderPackages(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading evidence packages...'));
    _loadProjectPackagesController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'packages') return;
        container.replaceChildren();
        controller.renderPackages(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project packages', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load evidence packages.'));
      });
  }

  function _renderProjectActivity(container, projectId, summary) {
    if (projectActivityController) {
      projectActivityController.renderActivity(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project activity...'));
    _loadProjectActivityController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'activity') return;
        controller.renderActivity(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project activity', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project activity.'));
      });
  }

  function _renderProjectOverview(container, projectId, summary) {
    if (projectOverviewController) {
      projectOverviewController.renderOverview(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project overview...'));
    _loadProjectOverviewController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'overview') return;
        controller.renderOverview(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project overview', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project overview.'));
      });
  }

  function _renderProjectAssessment(container, projectId) {
    if (projectAssessmentController) {
      projectAssessmentController.renderAssessment(container, projectId);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project assessment...'));
    _loadProjectAssessmentController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'assessment') return;
        controller.renderAssessment(container, projectId);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project assessment', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project assessment.'));
      });
  }

  function _renderProjectMonitoring(container, projectId, summary) {
    if (projectMonitoringController) {
      projectMonitoringController.renderMonitoring(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading project monitoring...'));
    _loadProjectMonitoringController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'monitoring') return;
        controller.renderMonitoring(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project monitoring', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load project monitoring.'));
      });
  }

  function _openProjectObject(projectId, { tab = '', targetType = '', targetId = '' } = {}) {
    const normalizedProjectId = String(projectId || projectWorkspaceState.selectedId() || '').trim();
    const normalizedTab = String(tab || '').trim();
    if (!normalizedProjectId || !normalizedTab) return;
    if (normalizedTab === 'activity') {
      _openProjectActivity(normalizedProjectId, { targetType, targetId });
      return;
    }
    projectWorkspaceState.setTab(normalizedTab);
    _renderProjectExplorer();
  }

  function _openProjectAssessment(projectId, {
    assessmentId = '', batchId = '', category = '', state = '', priority = '',
  } = {}) {
    const normalizedProjectId = String(projectId || projectWorkspaceState.selectedId() || '').trim();
    const normalizedAssessmentId = String(assessmentId || '').trim();
    if (!normalizedProjectId || !normalizedAssessmentId) return;
    projectWorkspaceState.setTab('assessment');
    if (_projectMobileView() === 'detail') _renderProjectMobileDetail();
    else _renderProjectExplorer();
    _loadProjectAssessmentController()
      .then(controller => controller.focusCycle(normalizedProjectId, normalizedAssessmentId, {
        batch_id: String(batchId || '').trim(),
        category: String(category || '').trim(),
        state: String(state || '').trim(),
        priority: String(priority || '').trim(),
      }))
      .catch((err) => {
        _shellLogClientError('failed to open project assessment cycle', err);
      });
  }

  function _openProjectActivity(projectId, { targetId = '', targetType = '' } = {}) {
    const normalizedProjectId = String(projectId || projectWorkspaceState.selectedId() || '').trim();
    if (!normalizedProjectId) return;
    _closeProjectEntityEditor();
    projectWorkspaceState.setTab('activity');
    _renderProjectExplorer();
    _loadProjectActivityController()
      .then((controller) => {
        const st = controller.stateFor(normalizedProjectId);
        st.filters.target_id = String(targetId || '').trim();
        st.filters.target_type = String(targetType || '').trim();
        st.offset = 0;
        st.loaded = false;
        if (projectWorkspaceState.tab() === 'activity') _renderProjectExplorer();
        return controller.load(normalizedProjectId);
      })
      .catch((err) => {
        _shellLogClientError('failed to open project activity', err);
      });
  }

  function _renderProjectReport(container, projectId, summary) {
    if (projectReportController) {
      projectReportController.renderReport(container, projectId, summary);
      return;
    }
    container.replaceChildren(_emptyProjectPanel('Loading report builder...'));
    _loadProjectReportController()
      .then((controller) => {
        if (!container.isConnected || projectWorkspaceState.tab() !== 'report') return;
        controller.renderReport(container, projectId, summary);
      })
      .catch((err) => {
        _shellLogClientError('failed to load project report builder', err);
        if (!container.isConnected) return;
        container.replaceChildren(_emptyProjectPanel('Could not load the report builder.'));
      });
  }

  function _renderProjectMobileEntitiesTab(projectId, summary) {
    if (projectEntitiesController) return projectEntitiesController.renderMobileEntitiesTab(projectId, summary);
    const panel = _emptyProjectPanel('Loading project entities...');
    _loadProjectEntitiesController()
      .then(() => {
        if (projectWorkspaceState.tab() === 'entities' && _projectMobileView() === 'detail') {
          _renderProjectMobileDetail();
        }
      })
      .catch((err) => {
        _shellLogClientError('failed to load mobile project entities', err);
        if (panel.isConnected) panel.replaceChildren('Could not load project entities.');
      });
    return panel;
  }

  function _renderProjectMobileReportTab(projectId, summary) {
    if (projectReportController) return projectReportController.renderMobileReportTab(projectId, summary);
    const panel = _emptyProjectPanel('Loading report builder...');
    _loadProjectReportController()
      .then(() => {
        if (projectWorkspaceState.tab() === 'report' && _projectMobileView() === 'detail') {
          _renderProjectMobileDetail();
        }
      })
      .catch((err) => {
        _shellLogClientError('failed to load mobile project report builder', err);
        if (panel.isConnected) panel.replaceChildren('Could not load the report builder.');
      });
    return panel;
  }

  function _renderProjectMobilePackagesTab(projectId, summary) {
    if (projectPackagesController) return projectPackagesController.renderMobilePackagesTab(projectId, summary);
    const panel = _emptyProjectPanel('Loading evidence packages...');
    _loadProjectPackagesController()
      .then(() => {
        if (projectWorkspaceState.tab() === 'packages' && _projectMobileView() === 'detail') {
          _renderProjectMobileDetail();
        }
      })
      .catch((err) => {
        _shellLogClientError('failed to load mobile project packages', err);
        if (panel.isConnected) panel.replaceChildren('Could not load evidence packages.');
      });
    return panel;
  }

  function _renderProjectMobileActivityTab(projectId, summary) {
    if (projectActivityController) return projectActivityController.renderMobileActivityTab(projectId, summary);
    const panel = _emptyProjectPanel('Loading project activity...');
    _loadProjectActivityController()
      .then(() => {
        if (projectWorkspaceState.tab() === 'activity' && _projectMobileView() === 'detail') {
          _renderProjectMobileDetail();
        }
      })
      .catch((err) => {
        _shellLogClientError('failed to load mobile project activity', err);
        if (panel.isConnected) panel.replaceChildren('Could not load project activity.');
      });
    return panel;
  }

  function _renderProjectMobileOverviewTab(projectId, summary) {
    if (projectOverviewController) return projectOverviewController.renderMobileOverviewTab(projectId, summary);
    const panel = _emptyProjectPanel('Loading project overview...');
    _loadProjectOverviewController()
      .then(() => {
        if (projectWorkspaceState.tab() === 'overview' && _projectMobileView() === 'detail') {
          _renderProjectMobileDetail();
        }
      })
      .catch((err) => {
        _shellLogClientError('failed to load mobile project overview', err);
        if (panel.isConnected) panel.replaceChildren('Could not load project overview.');
      });
    return panel;
  }

  function _renderProjectMobileAssessmentTab(projectId) {
    if (projectAssessmentController) return projectAssessmentController.renderMobileAssessmentTab(projectId);
    const panel = _emptyProjectPanel('Loading project assessment...');
    _loadProjectAssessmentController()
      .then(() => {
        if (projectWorkspaceState.tab() === 'assessment' && _projectMobileView() === 'detail') {
          _renderProjectMobileDetail();
        }
      })
      .catch((err) => {
        _shellLogClientError('failed to load mobile project assessment', err);
        if (panel.isConnected) panel.replaceChildren('Could not load project assessment.');
      });
    return panel;
  }

  function _renderProjectMobileMonitoringTab(projectId, summary) {
    if (projectMonitoringController) return projectMonitoringController.renderMobileMonitoringTab(projectId, summary);
    const panel = _emptyProjectPanel('Loading project monitoring...');
    _loadProjectMonitoringController()
      .then(() => {
        if (projectWorkspaceState.tab() === 'monitoring' && _projectMobileView() === 'detail') {
          _renderProjectMobileDetail();
        }
      })
      .catch((err) => {
        _shellLogClientError('failed to load mobile project monitoring', err);
        if (panel.isConnected) panel.replaceChildren('Could not load project monitoring.');
      });
    return panel;
  }

  function _renderProjectExplorer() {
    _projectWorkspaceRendererController().renderExplorer();
  }

  function _renderProjectWorkspace() {
    _projectWorkspaceRendererController().renderWorkspace();
  }

  async function _loadProjectSummaries(projects) {
    await _projectWorkspaceLifecycleController().loadProjectSummaries(projects);
  }

  async function _ensureProjectSummary(projectId = projectWorkspaceState.selectedId()) {
    return _projectWorkspaceLifecycleController().ensureProjectSummary(projectId);
  }

  function _setProjectPaginationOffset(offset) {
    projectWorkspaceState.setPaginationOffset(offset);
  }

  async function refreshProjectWorkspace() {
    if (!_projectWorkspaceModulesReady()) {
      if (!_projectWorkspaceOverlayOpenFallback()) return;
      await _ensureProjectWorkspaceModules();
    }
    await _projectWorkspaceLifecycleController().refreshProjectWorkspace();
  }

  function _createProjectWorkspaceInitialLoad() {
    const pagination = projectWorkspaceState.pagination?.() || {};
    const limit = Math.max(1, Number(pagination.limit || 50));
    const offset = Math.max(0, Number(pagination.offset || 0));
    const query = new URLSearchParams({
      include_archived: '1',
      include_counts: '1',
      limit: String(limit),
      offset: String(offset),
    });
    return {
      limit,
      offset,
      projectsResp: _shellApiFetch(`/projects?${query.toString()}`, { cache: 'no-store' }),
      activeContext: loadActiveProjectContext(),
    };
  }

  function _settleProjectWorkspaceInitialLoad(initialLoad) {
    if (!initialLoad || typeof initialLoad !== 'object') return;
    [
      initialLoad.projectsResp,
      initialLoad.activeContext,
    ].forEach((promise) => {
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    });
  }

  function _scheduleProjectWorkspaceExternalRefresh() {
    if (!_projectWorkspaceModulesReady()) {
      if (!_projectWorkspaceOverlayOpenFallback()) return;
      _ensureProjectWorkspaceModules()
        .then(() => _scheduleProjectWorkspaceExternalRefresh())
        .catch((err) => {
          _shellLogClientError('failed to load project workspace for external refresh', err);
        });
      return;
    }
    _projectWorkspaceShellController().scheduleExternalRefresh();
  }

  function _notifyProjectWorkspaceChanged(reason = 'updated', projectId = '', { local = true } = {}) {
    if (!_projectWorkspaceModulesReady()) return;
    _projectWorkspaceShellController().notifyChanged(reason, projectId, { local });
  }

  _projectActiveContextController().bindTargetDiscoveryEvent();

  async function openProjectWorkspace(options = {}) {
    const openToken = ++projectWorkspaceOpenToken;
    await _ensureProjectWorkspaceDom();
    if (openToken !== projectWorkspaceOpenToken) return false;
    const initialLoad = _createProjectWorkspaceInitialLoad();
    if (!_projectWorkspaceModulesReady() && projectWorkspaceOverlay) {
      projectWorkspaceOverlay.classList.remove('u-hidden');
      projectWorkspaceOverlay.classList.add('open');
      projectWorkspaceOverlay.setAttribute('aria-hidden', 'false');
      if (projectWorkspaceBody && !String(projectWorkspaceBody.textContent || '').trim()) {
        projectWorkspaceBody.textContent = 'Loading projects...';
      }
      if (projectMobileBody && !String(projectMobileBody.textContent || '').trim()) {
        projectMobileBody.textContent = 'Loading projects...';
      }
      _shellFn('markInteractionSurfaceReady', importedMarkInteractionSurfaceReady)?.('projects', projectWorkspaceOverlay, projectWorkspaceModal);
    }
    try {
      await _ensureProjectWorkspaceModules();
    } catch (err) {
      _settleProjectWorkspaceInitialLoad(initialLoad);
      throw err;
    }
    if (openToken !== projectWorkspaceOpenToken) {
      _settleProjectWorkspaceInitialLoad(initialLoad);
      return false;
    }
    try {
      await _projectWorkspaceShellController().open({
        ...options,
        refreshOptions: { initialLoad, ...(options.refreshOptions || {}) },
      });
    } catch (err) {
      _settleProjectWorkspaceInitialLoad(initialLoad);
      throw err;
    }
    if (openToken !== projectWorkspaceOpenToken) {
      _settleProjectWorkspaceInitialLoad(initialLoad);
      _projectWorkspaceShellController().close({ refocus: false });
      return false;
    }
    return true;
  }

  async function openProjectWorkspaceById(projectId, options = {}) {
    const normalizedProjectId = String(projectId || '').trim();
    if (!normalizedProjectId) return false;
    projectWorkspaceReturnToAtlas = options?.returnToAtlas && typeof options.returnToAtlas === 'object'
      ? { ...options.returnToAtlas }
      : null;
    const requestedTab = String(options?.tab || 'details').trim() || 'details';
    const opened = await openProjectWorkspace({ tab: requestedTab });
    if (!opened) {
      projectWorkspaceReturnToAtlas = null;
      return false;
    }
    projectWorkspaceState.setSelectedId(normalizedProjectId);
    projectWorkspaceState.setTab(requestedTab);
    await _ensureProjectSummary(normalizedProjectId);
    _renderProjectWorkspace();
    _renderProjectExplorer();
    return true;
  }

  async function openProjectAssessment(projectId, options = {}) {
    const opened = await openProjectWorkspaceById(projectId, { tab: 'assessment' });
    if (!opened) return false;
    _openProjectAssessment(projectId, options);
    return true;
  }

  function _autoPromoteProjectPickerContent(projects, preferredProjectId = '') {
    const wrap = document.createElement('div');
    wrap.className = 'history-project-picker';
    const select = document.createElement('select');
    select.className = 'form-select form-control-compact';
    select.setAttribute('aria-label', 'Project');
    projects.forEach((project) => {
      const option = document.createElement('option');
      option.value = String(project.id || '');
      option.textContent = _projectDisplayName(project) || String(project.id || '');
      select.appendChild(option);
    });
    if (preferredProjectId && projects.some(project => String(project.id || '') === preferredProjectId)) {
      select.value = preferredProjectId;
    }
    const help = document.createElement('div');
    help.className = 'history-project-picker-help';
    help.textContent = 'Choose a project for the new auto-promote rule.';
    wrap.append(select, help);
    return { wrap, select };
  }

  async function _promptAutoPromoteRuleProject(preferredProjectId = '') {
    if (!_shellFn('showConfirm', importedShowConfirm)) return '';
    const resp = await _shellApiFetch('/projects?include_archived=1&include_counts=0&limit=100&offset=0', { cache: 'no-store' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    const projects = (Array.isArray(data.projects) ? data.projects : [])
      .filter(project => String(project && project.status || 'active') !== 'archived');
    if (!projects.length) {
      _shellShowToast('Create an active project before creating an auto-promote rule.', 'error');
      return '';
    }
    const activeProject = _activeProject();
    const preferredId = preferredProjectId
      || (activeProject && activeProject.id ? String(activeProject.id) : '');
    const ordered = _orderedProjectRows(preferredId, projects);
    const { wrap, select } = _autoPromoteProjectPickerContent(ordered, preferredId);
    const choicePromise = _shellShowConfirm({
      body: 'Create auto-promote rule from Atlas view',
      content: wrap,
      defaultFocus: select,
      actions: [
        { id: 'cancel', label: 'Cancel', role: 'cancel' },
        { id: 'create', label: 'Create rule', role: 'primary' },
      ],
      refocusOnResolve: false,
    });
    const enhanceAppSelects = _shellEnhanceAppSelects();
    if (typeof enhanceAppSelects === 'function') {
      enhanceAppSelects(wrap);
      if (_shellUseMobileTerminalViewportMode()) {
        wrap.querySelector('.app-select-menu')?.classList.add('dropdown-up');
      }
    }
    const choice = await choicePromise;
    return choice === 'create' ? String(select.value || '') : '';
  }

  async function openProjectAutoPromoteRuleFromAtlas(draft = {}) {
    const activeProject = _activeProject();
    let projectId = String(draft.project_id || '').trim();
    if (!projectId && activeProject && activeProject.id) projectId = String(activeProject.id);
    if (!projectId) projectId = await _promptAutoPromoteRuleProject();
    if (!projectId) return false;
    await openProjectWorkspace();
    projectWorkspaceState.setSelectedId(projectId);
    projectWorkspaceState.setTab('entities');
    await _ensureProjectSummary(projectId);
    const entitiesController = await _loadProjectEntitiesController();
    entitiesController.openAutoPromoteRuleFromAtlas(projectId, draft);
    _renderProjectWorkspace();
    _renderProjectExplorer();
    return true;
  }

  function closeProjectWorkspace({ refocus = true } = {}) {
    const returnToAtlas = refocus ? projectWorkspaceReturnToAtlas : null;
    projectWorkspaceReturnToAtlas = null;
    projectWorkspaceOpenToken += 1;
    if (!_projectWorkspaceModulesReady()) {
      _refreshProjectWorkspaceElements();
      if (!projectWorkspaceOverlay) return;
      projectWorkspaceOverlay.classList.add('u-hidden');
      projectWorkspaceOverlay.classList.remove('open');
      projectWorkspaceOverlay.setAttribute('aria-hidden', 'true');
    } else {
      _projectWorkspaceShellController().close({ refocus: refocus && !returnToAtlas });
    }
    if (returnToAtlas) {
      void _shellOpenAtlas(returnToAtlas)
        .catch((err) => _reportSurfaceOpenFailure('atlas', err, { source: 'project-return' }));
    }
  }

  async function _projectWorkspaceRequest(url, options = {}) {
    return _projectWorkspaceShellController().request(url, options);
  }

  async function _syncEntityLabels(entityType, entityId, nextLabels) {
    const controller = await _loadProjectWorkspaceActionsController();
    await controller.syncEntityLabels(entityType, entityId, nextLabels);
  }

  async function _syncEntityNote(entityType, entityId, body) {
    const controller = await _loadProjectWorkspaceActionsController();
    await controller.syncEntityNote(entityType, entityId, body);
  }

  async function _linkLastRunToProject(projectId, summary) {
    const controller = await _loadProjectWorkspaceActionsController();
    await controller.linkLastRunToProject(projectId, summary);
  }

  async function _confirmProjectDestructive({ body, actionLabel, actionId, note }) {
    const controller = await _loadProjectWorkspaceActionsController();
    return controller.confirmDestructive({ body, actionLabel, actionId, note });
  }

  function _confirmProjectTargetDelete(targetValue) {
    return _loadProjectWorkspaceActionsController()
      .then(controller => controller.confirmTargetDelete(targetValue));
  }

  function _confirmProjectRunUnlink(projectId, runId, runCommand) {
    return _loadProjectWorkspaceActionsController()
      .then(controller => controller.confirmRunUnlink(projectId, runId, runCommand));
  }

  function _confirmProjectPackageDelete(packageName) {
    return _loadProjectWorkspaceActionsController()
      .then(controller => controller.confirmPackageDelete(packageName));
  }

  function _confirmProjectDelete(projectName) {
    return _loadProjectWorkspaceActionsController()
      .then(controller => controller.confirmProjectDelete(projectName));
  }

  function _setCachedFindingReviewState(projectId, findingId, reviewState) {
    _projectFindingsDataControllerIfReady()?.setCachedReviewState(projectId, findingId, reviewState);
  }

  function _updateCachedProjectFinding(projectId, findingId, updates) {
    _projectFindingsDataControllerIfReady()?.updateCachedFinding(projectId, findingId, updates);
  }

  function _renderUptime() {
    if (!hudUptimeEl) return;
    if (hudState.serverUptime === null) {
      hudUptimeEl.textContent = '—';
      _setValueColor(hudUptimeEl, 'hud-muted');
      return;
    }
    const deltaS = (performance.now() - hudState.serverUptimeAt) / 1000;
    hudUptimeEl.textContent = _formatUptime(hudState.serverUptime + deltaS);
    _setValueColor(hudUptimeEl, null);
  }

  function _renderClock() {
    if (!hudClockEl) return;
    const mode = typeof importedGetHudClockPreference === 'function'
      ? importedGetHudClockPreference()
      : 'utc';
    const now = Date.now();
    hudClockEl.textContent = mode === 'local' ? _formatLocalClock(now) : _formatUtcClock(now);
    if (mode === 'local') {
      try {
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'browser local time';
        hudClockEl.title = `Clock: local time (${zone}, ${_getLocalClockLabel(new Date(now))})`;
      } catch (_) {
        hudClockEl.title = 'Clock: local time';
      }
    } else {
      hudClockEl.title = 'Clock: UTC';
    }
    _setValueColor(hudClockEl, null);
  }

  function _renderDb() {
    if (!hudDbEl) return;
    if (hudState.db === 'ok') {
      hudDbEl.textContent = 'ONLINE';
      _setValueColor(hudDbEl, 'hud-value-green');
    } else if (hudState.db === 'down') {
      hudDbEl.textContent = 'OFFLINE';
      _setValueColor(hudDbEl, 'hud-value-red');
    } else {
      hudDbEl.textContent = '—';
      _setValueColor(hudDbEl, 'hud-muted');
    }
  }

  function _renderRedis() {
    if (!hudRedisEl) return;
    if (hudState.redis === 'ok') {
      hudRedisEl.textContent = 'ONLINE';
      _setValueColor(hudRedisEl, 'hud-value-green');
      hudRedisEl.title = 'Redis backend is reachable';
    } else if (hudState.redis === 'down') {
      hudRedisEl.textContent = 'OFFLINE';
      _setValueColor(hudRedisEl, 'hud-value-red');
      hudRedisEl.title = 'Redis configured but unreachable';
    } else if (hudState.redis === 'none') {
      hudRedisEl.textContent = 'N/A';
      _setValueColor(hudRedisEl, 'hud-muted');
      hudRedisEl.title = 'Redis not configured — rate limiting and process tracking run in-process';
    } else {
      hudRedisEl.textContent = '—';
      _setValueColor(hudRedisEl, 'hud-muted');
    }
  }

  async function pollHudStatus() {
    const t0 = performance.now();
    try {
      const resp = await fetch('/status', { cache: 'no-store', credentials: 'same-origin' });
      const t1 = performance.now();
      hudState.latencyMs = t1 - t0;
      if (resp.ok) {
        const data = await resp.json();
        if (typeof data.uptime === 'number') {
          hudState.serverUptime = data.uptime;
          hudState.serverUptimeAt = performance.now();
        }
        if (typeof data.db === 'string')    hudState.db = data.db;
        if (typeof data.redis === 'string') hudState.redis = data.redis;
      }
    } catch (_) {
      hudState.latencyMs = null;
      hudState.db = 'down';
      if (hudState.redis !== 'none') hudState.redis = 'down';
    }
    _renderLatency();
    _renderUptime();
    _renderDb();
    _renderRedis();
  }

  function _currentHudStatusPollMs() {
    return document.visibilityState === 'visible'
      ? STATUS_POLL_VISIBLE_MS
      : STATUS_POLL_HIDDEN_MS;
  }

  function _startHudStatusPoll({ pollNow = false } = {}) {
    if (hudStatusPollTimer) clearInterval(hudStatusPollTimer);
    hudStatusPollTimer = setInterval(pollHudStatus, _currentHudStatusPollMs());
    if (pollNow) pollHudStatus();
  }

  // Cross-tab access changes fire storage events; refresh immediately as well
  // as on each inexpensive status poll.
  window.addEventListener('storage', e => {
    if (e.key === 'access_credential' || e.key === 'anonymous_id') {
      _renderSession();
      loadActiveProjectContext().catch(() => {});
      return;
    }
    if (e.key === PROJECT_WORKSPACE_CONSTANTS.workspaceBroadcastKey && e.newValue) {
      _scheduleProjectWorkspaceExternalRefresh();
    }
  });
  window.addEventListener('app:identity-changed', () => {
    void _refreshBrowserSessionCredentialHint();
    loadActiveProjectContext().catch(() => {});
  });
  document.addEventListener('visibilitychange', () => {
    _startHudStatusPoll({ pollNow: document.visibilityState === 'visible' });
    if (document.visibilityState === 'visible') void _refreshBrowserSessionCredentialHint();
  });
  window.addEventListener('resize', () => {
    _scheduleProjectFilterSortDividerSync(projectExplorerBody);
  });

  if (_shellFn('onUiEvent', importedOnUiEvent)) {
    _shellOnUiEvent('app:history-rendered', () => {
      try { renderRailRecent(); } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:workflows-rendered', (e) => {
      try { renderRailWorkflows(e.detail && e.detail.items); } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:workflows-closed', () => {
      try {
        const renderWorkflowItemsFn = (typeof importedHasWorkflowHandler === 'function' && importedHasWorkflowHandler('renderWorkflowItems'))
          ? importedRenderWorkflowItems
          : _shellFn('renderWorkflowItems');
        renderWorkflowItemsFn?.(allWorkflows);
      } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:tab-status-changed', () => {
      try { _renderTabs(); } catch (_) { /* non-critical */ }
      try { _renderLastExit(); } catch (_) { /* non-critical */ }
      try { refreshHudActions(); } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:tab-activated', () => {
      try { _renderLastExit(); } catch (_) { /* non-critical */ }
      try { refreshHudActions(); } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:tab-created', () => {
      try { _renderTabs(); } catch (_) { /* non-critical */ }
      try { refreshHudActions(); } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:tab-closed', () => {
      try { _renderTabs(); } catch (_) { /* non-critical */ }
      try { refreshHudActions(); } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:last-exit-changed', (e) => {
      hudState.lastExit = e.detail ? e.detail.value : null;
      try { _renderLastExit(); } catch (_) { /* non-critical */ }
    });
    _shellOnUiEvent('app:tab-kill-visibility-changed', (e) => {
      const tabId = e.detail && e.detail.tabId;
      const activeId = _shellGetActiveTabId();
      if (tabId !== activeId) return;
      try { _setHudKillVisible(!!(e.detail && e.detail.visible)); } catch (_) { /* non-critical */ }
    });
  }

  // Initial render and pollers.
  _renderLastExit();
  _renderTabs();
  void _refreshBrowserSessionCredentialHint();
  _renderClock();
  _renderLatency();
  _renderUptime();
  _renderDb();
  _renderRedis();
  _renderActiveProject();

  _startHudStatusPoll({ pollNow: true });
  setInterval(() => { _renderClock(); _renderUptime(); _renderSession(); }, CLOCK_TICK_MS);

  // ── Init ─────────────────────────────────────────────────────────
  applyCollapsed();
  applyWidth();
  applySectionsState();
  renderRailRecent();
  bindHudStatusMonitorTriggers();
  const ensureWorkflowCatalogLoadedFn = (
    typeof importedHasWorkflowHandler === 'function' && importedHasWorkflowHandler('ensureWorkflowCatalogLoaded')
  ) ? importedEnsureWorkflowCatalogLoaded : _shellFn('ensureWorkflowCatalogLoaded');
  if (ensureWorkflowCatalogLoadedFn) {
    ensureWorkflowCatalogLoadedFn()
      .then(items => renderRailWorkflows(items))
      .catch(() => {});
  }
  refreshHudActions();
  loadActiveProjectContext().catch(() => {});

  document.addEventListener('click', (event) => {
    if (event.target?.closest?.('#project-mobile-new-btn')) {
      event.preventDefault();
      event.stopPropagation();
      _setProjectWorkspaceMessage('');
      _setProjectMobileCreateOpen(true, { focus: true }).catch((err) => {
        _shellLogClientError('failed to open mobile project create form', err);
      });
      return;
    }
    if (event.target?.closest?.('#project-mobile-create-form [data-project-mobile-action="cancel-create"]')) {
      event.preventDefault();
      event.stopPropagation();
      _setProjectWorkspaceMessage('');
      _setProjectMobileCreateOpen(false).catch((err) => {
        _shellLogClientError('failed to close mobile project create form', err);
      });
    }
  }, true);

  // Expose the workflows renderer for controller.js to call after /workflows loads.
  if (typeof importedSetProjectHudHandlers === 'function') {
    importedSetProjectHudHandlers({ renderHudClock: _renderClock });
  }
  if (typeof importedSetProjectContextHandlers === 'function') {
    importedSetProjectContextHandlers({
      closeProjectWorkspace,
      cycleProjectWorkspaceTab,
      getActiveProjectContext: _activeProject,
      isProjectWorkspaceOpen,
      notifyProjectWorkspaceChanged: _notifyProjectWorkspaceChanged,
      openEntityMetadataEditor,
      openProjectAutoPromoteRuleFromAtlas,
      openProjectAssessment,
      openProjectWorkspace,
      openProjectWorkspaceById,
      refreshActiveProjectContext: loadActiveProjectContext,
      refreshProjectWorkspace,
    });
  }
  if (typeof importedSetControllerActionHandlers === 'function') {
    importedSetControllerActionHandlers({ toggleRailCollapsed });
  }

})(globalThis);
